import { DETECTION_STEP_METRICS, aggregateStepMetrics, isStepMetric } from './detectionStepMetrics';
import { VM_VLM_MODEL } from "@/config/vlmModels";
/**
 * Shared components and constants for the Evaluation Suite.
 * Used across EvaluationPage, EvaluationCreatePage, and EvaluationRunPage.
 */

import { Fragment, useState, useEffect, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import { API_BASE_URL } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { authHeaders } from '@/utils/authHeaders';
import {
  Loader2,
  Check,
  AlertCircle,
  FileJson,
  X,
  Table2,
  BarChart3,
  Film,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Info,
  ExternalLink,
  Sparkles,
  Zap,
  Lightbulb,
  Maximize2,
  Database,
  TrendingUp,
} from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Tooltip as UITooltip, TooltipTrigger as UITooltipTrigger, TooltipContent as UITooltipContent, TooltipProvider as UITooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import type {
  EvaluationMode,
  EvaluationRowConfig,
  EvaluationRowResult,
  EvaluationTrialResult,
} from '@/types/evaluation';

export type EvaluationMetricDefinition = {
  title: string;
  key: string;
  color: string;
  isPercentage: boolean;
  correctKey?: string;
  totalKey?: string;
  group?: string;
  subline?: string;
  supportKey?: string;
  tooltip?: string;
  // Graph View composite chart types (MetricsChartsGrid): when set, this
  // metric renders via RecallBreakdownChart / GroupedTierChart instead of
  // SingleMetricChart. `key` still identifies it in the Metrics Filter
  // checkbox list; `tiers` (grouped_tiers only) lists the 3 underlying
  // metric keys/labels/colors to show as grouped bars.
  chartType?: 'recall_breakdown' | 'grouped_tiers';
  tiers?: { key: string; label: string; color: string }[];
};

/**
 * Shared content container for every Evaluation-tab page. Keeps the text/content
 * width + side spacing identical across pages. Pair with a full-width page root
 * and a full-bleed <EvalPageHeader> so the divider reaches the screen edges
 * while the content stays aligned.
 */
export const EVAL_CONTAINER = 'mx-auto w-full max-w-[1800px] px-6';

/** The Evaluate-tab views. The URL key (?view=) is stable; labels are display-only.
 * 'benchmarks' is Nomadic-admin-only — its nav entry and page section are gated by
 * isNomadicOrgUser; it stays in EVAL_VIEWS so the ?view=benchmarks route resolves. */
export type EvalView = 'history' | 'jobs' | 'benchmarks' | 'search';
export const EVAL_VIEWS: EvalView[] = ['history', 'jobs', 'benchmarks', 'search'];
export const EVAL_VIEW_LABELS: Record<EvalView, string> = {
  history: 'Recent Evaluations',
  jobs: 'Evaluation Jobs',
  benchmarks: 'Benchmarks',
  search: 'Search Evaluation',
};

/** URL for a given Evaluate view + scope — used for breadcrumb links and back targets. */
export const evalViewHref = (view: EvalView, scope: 'user' | 'org'): string =>
  `/use-cases/evaluation?view=${view}&scope=${scope}`;

/** Customer orgs enrolled in the eval-page beta, in addition to Nomadic admins. Keep in sync with backend/app/utils/constants.py:EVAL_BETA_ORG_IDS. */
export const EVAL_BETA_ORG_IDS: ReadonlySet<string> = new Set([
  'org_afd41c6f350a', // Bedrock Robotics Inc.
]);

/** Whether this profile may use the Evaluate feature at all (nav visibility + page access). */
export const canAccessEval = (profile: { admin_mode?: boolean; org_id?: string | null } | null | undefined): boolean => {
  if (!profile) return false;
  if (profile.admin_mode) return true;
  return Boolean(profile.org_id && EVAL_BETA_ORG_IDS.has(profile.org_id));
};

/** Nomadic's own internal org. Keep in sync with backend/app/utils/constants.py:NOMADICML_INTERNAL_ORG_IDS. */
export const NOMADIC_INTERNAL_ORG_ID = 'org_d65efc8bb860';

/** Nomadic-org gate for internal-only eval UI (e.g. per-row VLM overrides) that
 * must stay hidden from every beta customer org — deliberately org_id-only,
 * not admin_mode, since admin_mode can be set per-customer in enclave
 * deployments and isn't a reliable proxy for "is actually Nomadic staff". */
export const isNomadicOrgUser = (profile: { org_id?: string | null } | null | undefined): boolean =>
  Boolean(profile && profile.org_id === NOMADIC_INTERNAL_ORG_ID);

/**
 * Breadcrumb trail for an Evaluate *detail* page (run / compare / optimization):
 * "Evaluate › {origin view} › …". `fromView`/`fromScope` come from the detail
 * page's own query params so the trail (and back button) return to where the
 * user actually was (e.g. Organization → Recent Evaluations).
 */
export const evalDetailTrail = (
  fromView?: string | null,
  fromScope?: string | null,
): { label: string; to: string }[] => {
  const trail = [{ label: 'Evaluate', to: '/use-cases/evaluation' }];
  if (fromView && (EVAL_VIEWS as string[]).includes(fromView)) {
    const scope = fromScope === 'org' ? 'org' : 'user';
    trail.push({
      label: EVAL_VIEW_LABELS[fromView as EvalView],
      to: evalViewHref(fromView as EvalView, scope),
    });
  }
  return trail;
};

/**
 * Shared top bar for every Evaluation-tab page: a bordered back button + a
 * breadcrumb on one line with a divider underneath. Matches the batch-viewer
 * header. `trail` holds any parent crumbs (links); `current` is the page itself.
 */
export const EvalPageHeader = ({
  current,
  currentBadge,
  trail = [],
  right,
  innerClassName,
  backTo,
  sticky = false,
}: {
  current: string;
  /** Optional badge/chip rendered right after the current page title. */
  currentBadge?: ReactNode;
  trail?: { label: string; to: string }[];
  /** Optional right-aligned nav content (e.g. a scope toggle). */
  right?: ReactNode;
  /**
   * Applied to the inner row. Pass a centering container (e.g.
   * "container mx-auto px-6 max-w-7xl") to keep the divider full-bleed while
   * aligning the text/right-nav with the page's content column.
   */
  innerClassName?: string;
  /** Where the back button goes when there's no in-app history (deep link / refresh). */
  backTo?: string;
  /** Pin the header to the top of the viewport while the page scrolls. */
  sticky?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(backTo ?? '/use-cases/evaluation');
    }
  };
  return (
    <div className={cn('border-b border-zinc-200 mb-4', sticky && 'sticky top-0 z-30 bg-white')}>
      <div className={cn('flex h-12 items-center gap-2', innerClassName)}>
        <button
          onClick={goBack}
          className="flex items-center justify-center size-7 rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] hover:bg-zinc-50 transition-colors flex-shrink-0"
          title="Go back"
        >
          <ChevronLeft className="h-3.5 w-3.5 text-zinc-700" />
        </button>
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="gap-1.5 text-sm flex-nowrap min-w-0">
            {trail.map((crumb) => (
              <Fragment key={crumb.to}>
                <BreadcrumbItem className="flex-shrink-0">
                  <BreadcrumbLink asChild>
                    <Link
                      to={crumb.to}
                      className="text-zinc-500 hover:text-zinc-900 transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="flex-shrink-0" />
              </Fragment>
            ))}
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbPage className="block truncate text-zinc-900">{current}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {currentBadge ? <div className="flex-shrink-0">{currentBadge}</div> : null}
        {right ? <div className="ml-auto flex-shrink-0 pl-3">{right}</div> : null}
      </div>
    </div>
  );
};

/**
 * Rounded "Mine / Organization" scope toggle, styled like the Videos page tabs.
 */
export const EvalScopeTabs = ({
  scope,
  onChange,
}: {
  scope: 'user' | 'org';
  onChange: (scope: 'user' | 'org') => void;
}) => {
  const tabs: { id: 'user' | 'org'; label: string }[] = [
    { id: 'user', label: 'Mine' },
    { id: 'org', label: 'Organization' },
  ];
  return (
    <div className="flex h-9 min-w-max items-center rounded-[18px] bg-zinc-100 p-[3px]">
      {tabs.map((tab) => {
        const isActive = tab.id === scope;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex h-[30px] shrink-0 items-center rounded-[14px] px-3.5 text-sm font-medium transition-all',
              isActive
                ? 'border border-zinc-200 bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-600 hover:text-zinc-900',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export function normalizeGeminiModelAlias(value: string | null | undefined): string | undefined {
  if (!value) return value ?? undefined;
  return value === 'gemini-3-pro' ? 'gemini-3.1-pro' : value;
}

// VLM Model options for dropdown
export const VLM_MODEL_OPTIONS = [
  { value: 'nomadicvl', label: 'NomadicVL' },
  { value: 'nomadicvl_plus', label: 'NomadicVL+' },
  { value: 'nomadicvl_small', label: 'NomadicVL-Small' },
  { value: 'nomadicvl_gemini_3_5_flash', label: 'Nomadic VL Gemini 3.5 Flash' },
  { value: 'nomadicvl_gemini_3_6_flash', label: 'Nomadic VL Gemini 3.6 Flash' },
  VM_VLM_MODEL,
  { value: 'nomadicvl_experimental', label: 'Experimental' },
  { value: 'nomadicvl_gemma', label: 'Gemma' },
  { value: 'nomadicvl_cosmos', label: 'NomadicVL Cosmos' },
  { value: 'nomadicvl_cosmos_thinking', label: 'NomadicVL Cosmos (Thinking)' },
  { value: 'nomadicvl_gemini_robotics_er', label: 'NomadicVL Gemini Robotics ER 2' },
  { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
  { value: 'gpt-6.1-terra', label: 'GPT-6.1 Terra' },
  { value: 'gpt-6-astra', label: 'GPT-6 Astra' },
  { value: 'gpt-5.6', label: 'GPT-5.6 Sol' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  { value: 'gpt-5.5', label: 'GPT-5.5' },
  { value: 'gpt-5.4', label: 'GPT-5.4' },
  { value: 'claude-opus-5', label: 'Claude Opus 5' },
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { value: 'claude-fable-5', label: 'Claude Fable 5' },
  { value: 'kimi-k3', label: 'Kimi K3' },
  { value: 'muse-spark-1.1', label: 'Muse Spark 1.1' },
  { value: 'glm-4.6v', label: 'GLM-4.6V' },
  { value: 'glm-5.3-flash', label: 'GLM-5.3-Flash' },
  { value: 'qwen3.7-plus', label: 'Qwen3.7-Plus' },
  { value: 'qwen3-vl-235b-a22b-instruct', label: 'Qwen3-VL-235B-A22B-Instruct' },
  { value: 'qwen3.5-122b-a10b', label: 'Qwen3.5-122B-A10B' },
  { value: 'qwen3.5-397b-a17b', label: 'Qwen3.5-397B-A17B' },
  { value: 'qwen3.8-flash', label: 'Qwen3.8-Flash' },
  { value: 'qwen3.8-max', label: 'Qwen3.8-Max' },
  { value: 'qwen3.8-27b', label: 'Qwen3.8-27B' },
];

// LLM Agentic Validation Model options for dropdown
export const LLM_AGENTIC_MODEL_OPTIONS = [
  // 'off' must stay first — the benchmark dialog state defaults to 'off' and
  // the <select> needs a matching <option> or the browser silently shows the
  // first one while leaving state untouched (regression from PR #2396).
  { value: 'off',                label: 'Off (no agentic validation)' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { value: 'gemini-3-flash', label: 'Gemini 3 Flash' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
];

// VLM Model options for benchmark evaluations.
// MUST stay in sync with the admin-only VLM picker in
// frontend/src/components/ProfilePage.tsx and with `ALLOWED_VLM_MODELS` in
// backend/app/utils/vlm_inference_router.py (anything not in that backend list
// is rejected upstream and silently routed back to NomadicVL).
export const BENCHMARK_VLM_OPTIONS = [
  { value: 'nomadicvl', label: 'NomadicVL (Recommended)' },
  { value: 'nomadicvl_plus', label: 'NomadicVL+' },
  { value: 'nomadicvl_small', label: 'NomadicVL-Small' },
  { value: 'nomadicvl_gemini_3_5_flash', label: 'Nomadic VL Gemini 3.5 Flash' },
  { value: 'nomadicvl_gemini_3_6_flash', label: 'Nomadic VL Gemini 3.6 Flash' },
  VM_VLM_MODEL,
  { value: 'nomadicvl_experimental', label: 'NomadicVL (Experimental)' },
  { value: 'nomadicvl_gemma', label: 'NomadicVL Gemma' },
  { value: 'nomadicvl_cosmos', label: 'NomadicVL Cosmos' },
  { value: 'nomadicvl_cosmos_thinking', label: 'NomadicVL Cosmos (Thinking)' },
  { value: 'nomadicvl_gemini_robotics_er', label: 'NomadicVL Gemini Robotics ER 2' },
  { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
  { value: 'gpt-6.1-terra', label: 'GPT-6.1 Terra' },
  { value: 'gpt-6-astra', label: 'GPT-6 Astra' },
  { value: 'gpt-5.6', label: 'GPT-5.6 Sol' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  { value: 'gpt-5.5', label: 'GPT-5.5' },
  { value: 'gpt-5.4', label: 'GPT-5.4' },
  { value: 'claude-opus-5', label: 'Claude Opus 5' },
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { value: 'claude-fable-5', label: 'Claude Fable 5' },
  { value: 'kimi-k3', label: 'Kimi K3' },
  { value: 'muse-spark-1.1', label: 'Muse Spark 1.1' },
  { value: 'glm-4.6v', label: 'GLM-4.6V' },
  { value: 'glm-5.3-flash', label: 'GLM-5.3-Flash' },
  { value: 'qwen3.7-plus', label: 'Qwen3.7-Plus' },
  { value: 'qwen3-vl-235b-a22b-instruct', label: 'Qwen3-VL-235B-A22B-Instruct' },
  { value: 'qwen3.5-122b-a10b', label: 'Qwen3.5-122B-A10B' },
  { value: 'qwen3.5-397b-a17b', label: 'Qwen3.5-397B-A17B' },
  { value: 'qwen3.8-flash', label: 'Qwen3.8-Flash' },
  { value: 'qwen3.8-max', label: 'Qwen3.8-Max' },
  { value: 'qwen3.8-27b', label: 'Qwen3.8-27B' },
];

// Category options for tuning parameter grid
export const CATEGORY_OPTIONS = [
  { value: 'driving', label: 'Driving' },
  { value: 'robotics', label: 'Robotics' },
  { value: 'aerial', label: 'Aerial' },
  { value: 'security', label: 'Security' },
  { value: 'environment', label: 'Environment' },
];

// Color palette for evaluation rows
export const EVAL_ROW_COLORS = [
  '#1f2753', // navy (primary)
  '#06B6D4', // cyan
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#F97316', // orange
  '#84CC16', // lime
];

// All available metrics for the charts
export const ALL_METRICS = [
  // Composite charts — condense recall's two failure modes and the
  // no-validation/with-validation/oracle tiers into 3 charts instead of 9.
  {
    title: 'Recall Breakdown', key: 'recall', color: '#06B6D4', isPercentage: true,
    chartType: 'recall_breakdown' as const,
  },
  {
    title: 'Precision Tiers', key: 'precision', color: '#8B5CF6', isPercentage: true,
    chartType: 'grouped_tiers' as const,
    tiers: [
      { key: 'precision_no_validation', label: 'No Validation', color: '#A78BFA' },
      { key: 'precision', label: 'With Validation', color: '#8B5CF6' },
      { key: 'precision_oracle_validation', label: 'Oracle', color: '#5B21B6' },
    ],
  },
  {
    title: 'F1 Tiers', key: 'f1_score', color: '#10B981', isPercentage: true,
    chartType: 'grouped_tiers' as const,
    tiers: [
      { key: 'f1_no_validation', label: 'No Validation', color: '#6EE7B7' },
      { key: 'f1_score', label: 'With Validation', color: '#10B981' },
      { key: 'f1_oracle_validation', label: 'Oracle', color: '#047857' },
    ],
  },
  {
    title: 'Validator Quality', key: 'ai_rejection_precision', color: '#CA8A04', isPercentage: true,
    chartType: 'grouped_tiers' as const,
    tiers: [
      { key: 'ai_rejection_precision', label: 'Precision', color: '#F43F5E' },
      { key: 'ai_rejection_recall', label: 'Recall', color: '#CA8A04' },
      { key: 'ai_validation_f1', label: 'F1', color: '#7C2D12' },
      { key: 'validator_accuracy', label: 'Accuracy', color: '#0369A1' },
    ],
  },
];

// Benchmark-specific metrics (accuracy per category).
// MotionBench: per question_type from the dataset.
export const MOTIONBENCH_METRICS = [
  { title: 'Overall Accuracy', key: 'accuracy', color: '#8B5CF6', isPercentage: true,
    correctKey: 'total_correct', totalKey: 'total_answered' },
  { title: 'Action Order', key: 'accuracy/Action Order', color: '#06B6D4', isPercentage: true,
    correctKey: 'correct/Action Order', totalKey: 'total/Action Order' },
  { title: 'Motion-related Objects', key: 'accuracy/Motion-related Objects', color: '#10B981', isPercentage: true,
    correctKey: 'correct/Motion-related Objects', totalKey: 'total/Motion-related Objects' },
  { title: 'Motion Recognition', key: 'accuracy/Motion Recognition', color: '#F59E0B', isPercentage: true,
    correctKey: 'correct/Motion Recognition', totalKey: 'total/Motion Recognition' },
  { title: 'Repetition Count', key: 'accuracy/Repetition Count', color: '#EC4899', isPercentage: true,
    correctKey: 'correct/Repetition Count', totalKey: 'total/Repetition Count' },
  { title: 'Camera Motion', key: 'accuracy/Camera Motion', color: '#14B8A6', isPercentage: true,
    correctKey: 'correct/Camera Motion', totalKey: 'total/Camera Motion' },
  { title: 'Location-related Motion', key: 'accuracy/Location-related Motion', color: '#EF4444', isPercentage: true,
    correctKey: 'correct/Location-related Motion', totalKey: 'total/Location-related Motion' },
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// DrivingDojo: per-action + per-time + per-weather slices.
// Source of truth for DrivingDojo per-class prompts. The eval dialog fills
// every active class's prompt at submit time (override > default), so the
// backend bench always receives a non-empty `category_prompts` map and the
// previous `backend/app/services/drivingdojo_eval.py:CLASS_PROMPTS` constant
// has been removed. Also drives the "Modified" comparator on the Run
// Configuration card.
export const DRIVINGDOJO_DEFAULT_PROMPTS: Record<string, string> = {
  hardbrake:                "Identify all ego-vehicle hard brakes and sudden stops.",
  go_straight:              "Find all moments the ego-vehicle proceeds straight through a traffic-light controlled intersection. Extend the event window to at least 10 seconds.",
  lane_change:              "Identify all ego-vehicle lane changes.",
  light_to_green_start:     "Find all moments where the ego-vehicle starts moving as a traffic light turns green.",
  light_to_green_go:        "Find all moments where the ego vehicle continues moving through a traffic light as it turns green — the ego was already in motion and did not come to a stop.",
  light_to_red_stop:        "Find all moments where the ego-vehicle stops at a traffic light turning red.",
  light_to_red_go:          "Find all moments the ego-vehicle proceeds through a traffic light turning red without stopping.",
  turn_left:                "Identify all ego-vehicle left turns at an intersection.",
  turn_right:               "Identify all ego-vehicle right turns at an intersection.",
  pedestrian_cutin:         "Find all moments where a pedestrian cuts in front of the ego vehicle.",
  moving_obstacle:          "Find all moments where a moving object appears in front of the ego vehicle, excluding other traffic participants (e.g., vehicles, pedestrians, road workers, cyclists).",
  gate_opens:               "Find all moments where a parking or barrier gate opens in front of the ego vehicle.",
  potential_near_collision: "Identify all ego-vehicle potential near-collisions.",
};

// Retrieval phrasings of the same classes, used by the Embedding Retrieval
// preset. Deliberately NOT the detection prompts above: those are imperative
// instructions ("Identify all ego-vehicle...") whose non-semantic tokens end
// up in the query vector. A query here is embedded and compared by cosine
// similarity against caption/video vectors, so it reads as a description of
// the scene. Editable per row in the start dialog.
// `potential_near_collision` is absent on purpose — it has no annotated
// videos in the dataset, so it can only ever score an empty positive set.
export const DRIVINGDOJO_RETRIEVAL_QUERIES: Record<string, string> = {
  hardbrake:            'ego vehicle braking hard, sudden deceleration',
  go_straight:          'ego vehicle driving straight through a traffic light intersection',
  lane_change:          'ego vehicle changing lanes',
  light_to_green_start: 'traffic light turns green and the ego vehicle starts moving from a stop',
  light_to_green_go:    'traffic light turns green while the ego vehicle is already moving',
  light_to_red_stop:    'traffic light turns red and the ego vehicle comes to a stop',
  light_to_red_go:      'ego vehicle continues through a traffic light as it turns red',
  turn_left:            'ego vehicle turning left at an intersection',
  turn_right:           'ego vehicle turning right at an intersection',
  pedestrian_cutin:     'a pedestrian cutting in front of the ego vehicle',
  moving_obstacle:      'a moving obstacle appearing on the road ahead, not a vehicle or pedestrian',
  gate_opens:           'a parking or barrier gate opening in front of the ego vehicle',
};

// The embedding arms the preset compares, each at its best/chunk-level config.
// Ids are local to the dialog; each expands to one embedding config, and arms
// sharing a config share one corpus.
export const EMBEDDING_PRESET_ARMS: Array<{
  id: string;
  label: string;
  config: Record<string, unknown>;
}> = [
  { id: 'gemini_presummary', label: 'Gemini pre-summary text',
    config: { embedding_type: 'gemini_presummary' } },
  { id: 'gemini_video', label: 'Gemini video · 30s windows',
    config: { embedding_type: 'gemini_video', gemini_video_window_seconds: 30, gemini_video_fps: 1 } },
  { id: 'cosmos_video', label: 'Cosmos video',
    config: { embedding_type: 'cosmos_video' } },
  { id: 'grounded_caption', label: 'Grounded caption · per segment',
    config: { embedding_type: 'grounded_caption', grounded_caption_scope: 'segment' } },
];

// MUST stay in sync with backend/app/services/drivingdojo_eval.py:DRIVINGDOJO_ACTIONS
// (id + display label) and dev/drivingdojo/build_dataset_meta.py:LABEL_MAP.
export const DRIVINGDOJO_ACTIONS: Array<{ id: string; label: string }> = [
  { id: 'hardbrake',                label: 'Hard brake / sudden deceleration' },
  { id: 'go_straight',              label: 'Going straight through intersection' },
  { id: 'lane_change',              label: 'Lane change' },
  // Traffic-light transitions split by ego response; see
  // backend/app/services/drivingdojo_eval.py:DRIVINGDOJO_ACTIONS.
  { id: 'light_to_green_start',     label: 'Light turns green; ego starts from stop' },
  { id: 'light_to_green_go',        label: 'Light turns green; ego already moving' },
  { id: 'light_to_red_stop',        label: 'Light turns red; ego stops' },
  { id: 'light_to_red_go',          label: 'Light turns red; ego continues through' },
  { id: 'turn_left',                label: 'Left turn at intersection' },
  { id: 'turn_right',                label: 'Right turn at intersection' },
  { id: 'pedestrian_cutin',         label: 'Pedestrian cuts in front of ego vehicle' },
  { id: 'moving_obstacle',          label: 'Moving obstacle appears on road' },
  { id: 'gate_opens',               label: 'Parking gate opens' },
  { id: 'potential_near_collision', label: 'Potential collision with other road user' },
];

// Palette cycled for the 11 action cards. Mirrors the variety of MOTIONBENCH_METRICS.
const _ACTION_COLORS = [
  '#06B6D4', '#10B981', '#F59E0B', '#EC4899', '#14B8A6',
  '#EF4444', '#8B5CF6', '#0EA5E9', '#F97316', '#84CC16', '#A855F7',
];

// DROID action-classification taxonomy.
// MUST stay in sync with backend/app/services/droid_eval.py:DROID_ACTIONS
// (which itself mirrors dev/droid_taxonomy.py). Order = empirical distribution
// over the cleaned dataset (most-common first).
export const DROID_ACTIONS: Array<{ id: string; label: string; description: string }> = [
  { id: 'pick_and_place',                    label: 'Pick and place',
    description: 'Grasp, lift, transport, and place an object at a different container, surface, or location than it started (e.g. into a bowl, on top of another block, handed to a person). Placing it at a different spot on the SAME surface is `move`, not pick_and_place.' },
  { id: 'move',                              label: 'Move (relocate on same surface)',
    description: 'Relocate an object to a different spot on the same surface (drag or briefly lift). The object stays on the same workspace and is not transferred to a different container or location.' },
  { id: 'pour_or_tool_use',                  label: 'Pour or tool use',
    description: 'Pour from a container, or apply a tool via repeated contact (wipe, stir, scoop).' },
  { id: 'press_or_toggle',                   label: 'Press or toggle',
    description: 'Press a rigid button or flip a rigid switch with brief low-travel contact.' },
  { id: 'close_articulated',                 label: 'Close articulated',
    description: 'Close a drawer, door, lid, cabinet, fridge, microwave, or tap.' },
  { id: 'open_articulated',                  label: 'Open articulated',
    description: 'Open a drawer, door, lid, cabinet, fridge, microwave, or tap.' },
  { id: 'composite_multi_pick_and_place',    label: 'Composite: multi pick & place',
    description: 'Two or more sequential pick_and_place actions on different objects.' },
  { id: 'other',                             label: 'Other',
    description: 'Visually ambiguous robot motion, off-distribution behaviour, or actions that don’t fit a single class.' },
  { id: 'fold_unfold',                       label: 'Fold / unfold',
    description: 'Fold or unfold a deformable object such as cloth.' },
  { id: 'push',                              label: 'Push',
    description: 'Push without grasp (free object nudge or lever with travel).' },
  { id: 'rotate_object',                     label: 'Rotate object',
    description: 'Grasp and rotate a held object (flip, twist, turn dial/knob/cap).' },
  { id: 'composite_open_then_place',         label: 'Composite: open then place',
    description: 'Open an articulated object then place something inside (and optionally close it).' },
  { id: 'composite_move_and_pick_and_place', label: 'Composite: move + pick & place',
    description: 'A move step (same-surface relocation) and a pick_and_place step (object placed at a different container, surface, or location) chained in the same episode.' },
  { id: 'pull',                              label: 'Pull',
    description: 'Pull a rope, strap, cord, chain, cable, or lever toward the agent.' },
];

export const DROID_VIEWS: Array<{ id: string; label: string }> = [
  { id: 'exterior_image_1_left', label: 'Exterior camera 1 (left)' },
  { id: 'exterior_image_2_left', label: 'Exterior camera 2 (left)' },
  { id: 'wrist_image_left',      label: 'Wrist camera (left)' },
];

// Per-class accuracy cards for DROID. Each per-class card carries
// correctKey/totalKey so the legacy "<correct>/<total> correct" subline renders.
export const DROID_METRICS = [
  { title: 'Overall Accuracy', key: 'accuracy', color: '#8B5CF6', isPercentage: true,
    correctKey: 'total_correct', totalKey: 'total_answered' },
  ...DROID_ACTIONS.map((a, i) => ({
    title: a.label,
    key: `accuracy/${a.id}`,
    color: _ACTION_COLORS[i % _ACTION_COLORS.length],
    isPercentage: true,
    correctKey: `correct/${a.id}`,
    totalKey: `total/${a.id}`,
    supportKey: `total/${a.id}`,
    group: 'Per-class accuracy',
  })),
  // Target-object axis — only emitted by runs that opted into target-object
  // mode. Cards with missing keys hide gracefully on legacy runs.
  { title: 'Target Object Accuracy (overall)', key: 'target_object_accuracy', color: '#A855F7',
    isPercentage: true,
    correctKey: 'target_object_correct', totalKey: 'target_object_answered',
    supportKey: 'target_object_answered',
    group: 'Target object' },
  ...DROID_ACTIONS.map((a, i) => ({
    title: a.label,
    key: `target_object_accuracy/${a.id}`,
    color: _ACTION_COLORS[i % _ACTION_COLORS.length],
    isPercentage: true,
    correctKey: `target_object_correct/${a.id}`,
    totalKey: `target_object_total/${a.id}`,
    supportKey: `target_object_total/${a.id}`,
    group: 'Target object — per-class accuracy',
  })),
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// Event-detection metric cards. Single-class query scored as TP/FP/FN/TN
// against a target class with the other selected classes as distractors.
export const DROID_EVENT_DETECTION_METRICS = [
  { title: 'F1 Score', key: 'event_detection_f1', color: '#8B5CF6', isPercentage: true,
    supportKey: 'event_detection_total',
    group: 'Event-detection metrics' },
  { title: 'Precision', key: 'event_detection_precision', color: '#06B6D4', isPercentage: true,
    supportKey: 'event_detection_total',
    group: 'Event-detection metrics' },
  { title: 'Recall', key: 'event_detection_recall', color: '#10B981', isPercentage: true,
    supportKey: 'event_detection_total',
    group: 'Event-detection metrics' },
  { title: 'Overall Accuracy', key: 'accuracy', color: '#A855F7', isPercentage: true,
    correctKey: 'total_correct', totalKey: 'total_answered',
    group: 'Event-detection metrics' },
  { title: 'True positives',  key: 'event_detection_tp', color: '#22C55E', isPercentage: false,
    group: 'Event-detection metrics' },
  { title: 'False positives', key: 'event_detection_fp', color: '#EF4444', isPercentage: false,
    group: 'Event-detection metrics' },
  { title: 'False negatives', key: 'event_detection_fn', color: '#F59E0B', isPercentage: false,
    group: 'Event-detection metrics' },
  { title: 'True negatives',  key: 'event_detection_tn', color: '#94A3B8', isPercentage: false,
    group: 'Event-detection metrics' },
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// ViFailback failure detection. Positive class = failure; an episode with >=1
// detected event is a predicted failure. Keys mirror backend vifailback_eval.compute_metrics.
export const VIFAILBACK_METRICS = [
  { title: 'Video Accuracy', key: 'accuracy', color: '#A855F7', isPercentage: true,
    correctKey: 'total_correct', totalKey: 'total_answered',
    group: 'Overall' },
  { title: 'Failure Precision', key: 'failure_precision', color: '#06B6D4', isPercentage: true,
    supportKey: 'total_answered',
    tooltip: 'Of the episodes the model flagged as failures, the share that are labelled failures.',
    group: 'Failure detection' },
  { title: 'Failure Recall', key: 'failure_recall', color: '#10B981', isPercentage: true,
    supportKey: 'n_failures_gt',
    tooltip: 'Of the labelled failure episodes, the share the model flagged.',
    group: 'Failure detection' },
  { title: 'Failure F1', key: 'failure_f1', color: '#8B5CF6', isPercentage: true,
    supportKey: 'total_answered',
    group: 'Failure detection' },
  { title: 'Failures caught (TP)', key: 'tp', color: '#22C55E', isPercentage: false,
    group: 'Counts' },
  { title: 'Successes flagged (FP)', key: 'fp', color: '#EF4444', isPercentage: false,
    group: 'Counts' },
  { title: 'Failures missed (FN)', key: 'fn', color: '#F59E0B', isPercentage: false,
    group: 'Counts' },
  { title: 'Successes passed (TN)', key: 'tn', color: '#94A3B8', isPercentage: false,
    group: 'Counts' },
  { title: 'Cost (USD)', key: 'cost_usd', color: '#6B7280', isPercentage: false,
    group: 'Counts' },
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// `group` tags drive the section dividers in the run-results page. MotionBench
// metrics omit this field on purpose (renders flat, identical to before).
export const DRIVINGDOJO_METRICS = [
  { title: 'Overall Accuracy', key: 'accuracy', color: '#8B5CF6', isPercentage: true,
    correctKey: 'total_correct', totalKey: 'total_answered' },
  // Actions
  ...DRIVINGDOJO_ACTIONS.map((a, i) => ({
    title: a.label,
    key: `accuracy/action/${a.id}`,
    color: _ACTION_COLORS[i % _ACTION_COLORS.length],
    isPercentage: true,
    correctKey: `correct/action/${a.id}`,
    totalKey: `total/action/${a.id}`,
    group: 'Actions',
  })),
  { title: 'Action mean (macro)', key: 'accuracy/action/macro_mean', color: '#7C3AED',
    isPercentage: true, group: 'Actions' },
  // Environment — daytime
  { title: 'Day', key: 'accuracy/time/DAY', color: '#FBBF24', isPercentage: true,
    correctKey: 'correct/time/DAY', totalKey: 'total/time/DAY', group: 'Environment' },
  { title: 'Night', key: 'accuracy/time/NIGHT', color: '#1E3A8A', isPercentage: true,
    correctKey: 'correct/time/NIGHT', totalKey: 'total/time/NIGHT', group: 'Environment' },
  { title: 'Time mean (macro)', key: 'accuracy/time/macro_mean', color: '#7C3AED',
    isPercentage: true, group: 'Environment' },
  // Environment — weather
  { title: 'Weather: Normal', key: 'accuracy/weather/WEATHER_NORMAL', color: '#10B981', isPercentage: true,
    correctKey: 'correct/weather/WEATHER_NORMAL', totalKey: 'total/weather/WEATHER_NORMAL', group: 'Environment' },
  { title: 'Weather: Rainy', key: 'accuracy/weather/WEATHER_RAINY', color: '#0EA5E9', isPercentage: true,
    correctKey: 'correct/weather/WEATHER_RAINY', totalKey: 'total/weather/WEATHER_RAINY', group: 'Environment' },
  { title: 'Weather: Snow', key: 'accuracy/weather/WEATHER_SNOW', color: '#94A3B8', isPercentage: true,
    correctKey: 'correct/weather/WEATHER_SNOW', totalKey: 'total/weather/WEATHER_SNOW', group: 'Environment' },
  { title: 'Weather: Unknown', key: 'accuracy/weather/WEATHER_UNKNOWN', color: '#6B7280', isPercentage: true,
    correctKey: 'correct/weather/WEATHER_UNKNOWN', totalKey: 'total/weather/WEATHER_UNKNOWN', group: 'Environment' },
  { title: 'Weather mean (macro)', key: 'accuracy/weather/macro_mean', color: '#7C3AED',
    isPercentage: true, group: 'Environment' },
  // Location: top three by local volume; everything else still lands in the run doc but isn't carded.
  { title: 'Location: Beijing', key: 'accuracy/location/beijing', color: '#DC2626', isPercentage: true,
    correctKey: 'correct/location/beijing', totalKey: 'total/location/beijing', group: 'Location' },
  { title: 'Location: Shenzhen', key: 'accuracy/location/shenzhen', color: '#F97316', isPercentage: true,
    correctKey: 'correct/location/shenzhen', totalKey: 'total/location/shenzhen', group: 'Location' },
  { title: 'Location: Xuzhou', key: 'accuracy/location/xuzhou', color: '#84CC16', isPercentage: true,
    correctKey: 'correct/location/xuzhou', totalKey: 'total/location/xuzhou', group: 'Location' },
  { title: 'Location mean (macro)', key: 'accuracy/location/macro_mean', color: '#7C3AED',
    isPercentage: true, group: 'Location' },
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// DrivingDojo event-overlap protocol (v1) — per-class detection scoring.
// Headline: micro-F1. Cards cover overall + per-class + per-slice F1.
// MUST stay in sync with backend `build_metrics_blob` keys.
//
// `subline` is a template string with `{metric_key}` placeholders the renderer
// substitutes from `aggregated_metrics`. Use this instead of `correctKey` /
// `totalKey` when you want a richer subline (e.g. "TP: 4/5 — FP: 1") rather
// than the legacy "<correct>/<total>" pair.
//
// `supportKey` (optional): when present and `aggregated_metrics[supportKey]`
// is 0 or missing, the card renders "—" instead of "0%" — used for inactive
// canonical classes and slice values that had no GT in this run.
//
// `tooltip` (optional): one-line explanation surfaced via the hover info icon.
// Final Statistics: the headline P/R/F1/tIoU numbers, both micro and macro.
// `f1/macro` has no per-class equivalent (macro-averaging only makes sense
// across classes), so it's excluded when this list is reused per-class.
export const DRIVINGDOJO_FINAL_STATS_METRICS: EvaluationMetricDefinition[] = [
  { title: 'Overall F1 (micro)', key: 'f1', color: '#8B5CF6', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Micro-averaged F1 across all classes and videos. TP / FP / FN are summed globally, then F1 is computed from those totals — every prediction and GT contributes equally.' },
  { title: 'Macro F1',           key: 'f1/macro', color: '#7C3AED', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Mean of per-class F1 over classes that have GT in the dataset. Each class contributes equally regardless of support.' },
  { title: 'Precision (micro)',  key: 'precision', color: '#06B6D4', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {total_tp}/{total_pred} — FP: {total_fp}',
    tooltip: 'ΣTP / (ΣTP + ΣFP). Of all predictions, how many overlapped a GT.' },
  { title: 'Recall (micro)',     key: 'recall', color: '#10B981', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {total_tp}/{total_gt}',
    tooltip: 'ΣTP / (ΣTP + ΣFN). Of all GT instances, how many were covered by at least one prediction.' },
  { title: 'Mean tIoU',          key: 'mean_tiou', color: '#0EA5E9', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Average of the best overlapping prediction\'s temporal IoU per matched GT. Reported across classes that had any TP.' },
];

// VLM Event Detection Statistics: how good is the raw initial detector at detecting
// events, independent of whether AI rejection later throws a result out.
export const DRIVINGDOJO_VLM_STATS_METRICS: EvaluationMetricDefinition[] = [
  // No separate "VLM Noise Rate" card — it's exactly 1 - Precision (No
  // Validation), so the raw noise count is folded into that card's subtext
  // instead of showing a redundant derived rate.
  { title: 'Precision (No Validation)', key: 'precision_no_validation', color: '#A78BFA', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {raw_tp_count}/{total_raw_predictions} — Noise: {vlm_noise_count}',
    tooltip: 'Precision if every raw initial detector prediction (kept + AI-rejected) were accepted as-is, ignoring AI rejection entirely. Noise = predictions that matched no GT event; 1 - Precision.' },
  { title: 'Recall (No Validation)', key: 'recall_no_validation', color: '#67E8F9', isPercentage: true, group: 'Overall Event Detection',
    subline: '{gt_covered_count}/{total_gt}',
    tooltip: 'Recall if AI rejection never happened. Identical to Recall (Oracle Validation) — recall is bounded by what the raw initial detector proposed, not by validation quality.' },
  { title: 'F1 (No Validation)', key: 'f1_no_validation', color: '#6EE7B7', isPercentage: true, group: 'Overall Event Detection' },
  { title: 'FN Share: Initial Detection Missed', key: 'fn_share_vlm_missed', color: '#0E7490', isPercentage: true, group: 'Overall Event Detection',
    subline: '{vlm_missed_count}/{total_fn}',
    tooltip: 'Of all false negatives, % that were initial detection misses (never proposed at all).' },
];

// Rejection Statistics: grades the agentic-validation (AI rejection) model
// itself as a classifier over the VLM's raw output. The raw counts behind
// each rate (AI-Rejected/Incorrectly/Correctly Rejected) are condensed into
// grey subtext under the rate cards below rather than their own cards.
export const DRIVINGDOJO_REJECTION_STATS_METRICS: EvaluationMetricDefinition[] = [
  { title: 'Rejection Precision', key: 'ai_rejection_precision', color: '#F43F5E', isPercentage: true, group: 'Rejection Statistics',
    subline: '{ai_correctly_rejected_count}/{ai_rejected_count}',
    tooltip: 'Of all AI-rejected events, % that were actually incorrect (1 - Incorrectly Rejected / AI-Rejected Events) — grades the rejection model\'s own precision.' },
  { title: 'Rejection Recall', key: 'ai_rejection_recall', color: '#CA8A04', isPercentage: true, group: 'Rejection Statistics',
    subline: '{ai_correctly_rejected_count}/{vlm_noise_count}',
    tooltip: 'Of all actually-incorrect raw predictions, % the validator successfully caught and rejected.' },
  { title: 'Validator F1', key: 'ai_validation_f1', color: '#7C2D12', isPercentage: true, group: 'Rejection Statistics',
    tooltip: 'Harmonic mean of Rejection Precision and Rejection Recall — grades the validator itself as a classifier.' },
  { title: 'Recall Impact (Validation)', key: 'ai_recall_impact_rate', color: '#EA580C', isPercentage: true, group: 'Rejection Statistics',
    subline: '{ai_incorrectly_rejected_count}/{total_gt}',
    tooltip: 'Incorrectly Rejected / total GT events — how much recall is lost specifically to bad rejections.' },
  { title: 'FN Share: Incorrectly Rejected', key: 'fn_share_incorrectly_rejected', color: '#BE185D', isPercentage: true, group: 'Rejection Statistics',
    subline: '{ai_incorrectly_rejected_count}/{total_fn}',
    tooltip: 'Of all false negatives, % caused by AI rejection wrongly throwing out a correct event. Sums to 100% with FN Share: Initial Detection Missed (in the Overall Event Detection section).' },
  { title: 'Validator Accuracy', key: 'validator_accuracy', color: '#0369A1', isPercentage: true, group: 'Rejection Statistics',
    subline: '{validator_correct_decisions}/{total_raw_decisions}',
    tooltip: 'Of every raw prediction the validator looked at (kept + rejected), % it decided on correctly — kept ones that actually overlapped a GT, plus rejected ones that actually didn\'t. Unlike Rejection Precision/Recall (which only grade the rejected subset), this blends both outcomes into one number, symmetric regardless of the validator\'s own valid/invalid call.' },
];

// Oracle Validation Statistics: the ceiling achievable with a perfect
// validator (always keeps correct predictions, always rejects incorrect ones).
export const DRIVINGDOJO_ORACLE_STATS_METRICS: EvaluationMetricDefinition[] = [
  { title: 'Precision (Oracle Validation)', key: 'precision_oracle_validation', color: '#5B21B6', isPercentage: true, group: 'Oracle Validation Statistics',
    subline: 'TP: {raw_tp_count} (FP: 0 by definition)',
    tooltip: 'Precision assuming a perfect validator that always rejects incorrect predictions — always 1.0 whenever any true positive exists.' },
  { title: 'Recall (Oracle Validation)', key: 'recall_oracle_validation', color: '#0E7490', isPercentage: true, group: 'Oracle Validation Statistics',
    subline: '{gt_covered_count}/{total_gt}',
    tooltip: 'Recall assuming perfect validation. Same value as Recall (No Validation).' },
  { title: 'F1 (Oracle Validation)', key: 'f1_oracle_validation', color: '#047857', isPercentage: true, group: 'Oracle Validation Statistics',
    tooltip: 'The F1 ceiling achievable if the validator were perfect, given the VLM\'s raw detection recall.' },
];

// Final Statistics for a regular (non-DrivingDojo) event-detection eval row.
// Same "Final Statistics" group/format as DrivingDojo, but scoped to a single
// query (no macro-F1/mean-tIoU — those are multi-class-only concepts) and
// keyed to what `evaluate_predictions()` actually returns (`f1_score`, not
// `f1`).
export const EVENT_DETECTION_FINAL_STATS_METRICS: EvaluationMetricDefinition[] = [
  { title: 'F1', key: 'f1_score', color: '#8B5CF6', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Harmonic mean of precision and recall.' },
  { title: 'Precision', key: 'precision', color: '#06B6D4', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {tp}/{total_predictions} — FP: {fp}',
    tooltip: 'TP / (TP + FP). Of all predictions, how many overlapped a GT event.' },
  { title: 'Recall', key: 'recall', color: '#10B981', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {tp}/{total_ground_truth}',
    tooltip: 'TP / (TP + FN). Of all GT events, how many were covered by at least one prediction.' },
];

// Full three-tier metric set for a regular eval row — Final Statistics + VLM
// Statistics + Rejection Statistics + Oracle Validation Statistics — the same
// grouped/collapsible card format DrivingDojo uses, reused via
// GroupedMetricCards in RowResultCard (single row — micro/macro are the same
// thing when there's only one row, so no split needed there).
export const EVENT_DETECTION_VALIDATION_METRIC_DEFS: EvaluationMetricDefinition[] = [
  ...EVENT_DETECTION_FINAL_STATS_METRICS,
  ...DRIVINGDOJO_VLM_STATS_METRICS,
  ...DETECTION_STEP_METRICS,
  ...DRIVINGDOJO_REJECTION_STATS_METRICS,
  ...DRIVINGDOJO_ORACLE_STATS_METRICS,
];

// Final Statistics for the ACROSS-ROWS summary (SummaryStatsCards) — mirrors
// DrivingDojo's split of micro vs macro F1: Precision/Recall/F1 are
// micro-averaged (ΣTP/ΣFP/ΣFN across all rows, then the rate is computed from
// those totals — every prediction and GT event contributes equally
// regardless of which row it came from); F1 (macro) is the mean of each row's
// own F1 score instead (every row/query contributes equally regardless of
// its event count). `precision`/`recall`/`f1_score` are computed specially in
// SummaryStatsCards (not a simple per-row average or sum) — see there.
export const EVENT_DETECTION_OVERALL_STATS_METRICS: EvaluationMetricDefinition[] = [
  { title: 'F1 (micro)', key: 'f1_score', color: '#8B5CF6', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Micro-averaged F1: TP/FP/FN summed across all rows, then F1 is computed from those totals — every prediction and GT event contributes equally.' },
  { title: 'F1 (macro)', key: 'f1_score_macro', color: '#7C3AED', isPercentage: true, group: 'Overall Event Detection',
    tooltip: 'Mean of each row\'s own F1 score. Each query/row contributes equally, regardless of how many events or videos it has.' },
  { title: 'Precision (micro)', key: 'precision', color: '#06B6D4', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {tp}/{total_predictions} — FP: {fp}',
    tooltip: 'ΣTP / (ΣTP + ΣFP) across all rows.' },
  { title: 'Recall (micro)', key: 'recall', color: '#10B981', isPercentage: true, group: 'Overall Event Detection',
    subline: 'TP: {tp}/{total_ground_truth}',
    tooltip: 'ΣTP / (ΣTP + ΣFN) across all rows.' },
];

export const EVENT_DETECTION_OVERALL_METRIC_DEFS: EvaluationMetricDefinition[] = [
  ...EVENT_DETECTION_OVERALL_STATS_METRICS,
  ...DRIVINGDOJO_VLM_STATS_METRICS,
  ...DETECTION_STEP_METRICS,
  ...DRIVINGDOJO_REJECTION_STATS_METRICS,
  ...DRIVINGDOJO_ORACLE_STATS_METRICS,
];

// Bare-key card defs shared between the global "Overall" section and each
// per-class result card (Part E) — per-class cards feed these the same
// definitions against a metrics dict remapped to strip the `/class/<aid>`
// suffix, so `f1/macro` (no per-class equivalent) must be filtered out there.
export const DRIVINGDOJO_VALIDATION_METRIC_DEFS: EvaluationMetricDefinition[] = [
  ...DRIVINGDOJO_FINAL_STATS_METRICS,
  ...DRIVINGDOJO_VLM_STATS_METRICS,
  ...DETECTION_STEP_METRICS,
  ...DRIVINGDOJO_REJECTION_STATS_METRICS,
  ...DRIVINGDOJO_ORACLE_STATS_METRICS,
];

export const DRIVINGDOJO_EVENT_METRICS = [
  // Overall block, split into 4 labeled sections. Headline F1 is also
  // rendered in the page header via `metrics.f1`.
  ...DRIVINGDOJO_VALIDATION_METRIC_DEFS,

  // Note: no "Event/Action (F1)" per-class cards here — that breakdown now
  // lives in the per-class expandable rows below the Overall section
  // (DrivingDojoClassResultCard), which show the full 4-section breakdown
  // per class rather than just F1.

  // Note: no Environment (F1) / Location (F1) breakdown — dropped as unused
  // clutter. The underlying f1/time/*, f1/weather/*, f1/location/* metric
  // keys are still computed and stored, just not rendered here.
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// Metric set for Driving Dojo's Graph View (MetricsChartsGrid) — distinct
// from DRIVINGDOJO_EVENT_METRICS (used for the Overall stat-card section):
// the graph view condenses the three-tier/rejection stats into 4 composite
// charts, while the stat cards keep every individual number with sublines.
export const DRIVINGDOJO_GRAPH_METRICS: EvaluationMetricDefinition[] = [
  {
    title: 'Recall Breakdown', key: 'recall', color: '#10B981', isPercentage: true,
    chartType: 'recall_breakdown',
  },
  {
    title: 'Precision Tiers', key: 'precision', color: '#06B6D4', isPercentage: true,
    chartType: 'grouped_tiers',
    tiers: [
      { key: 'precision_no_validation', label: 'No Validation', color: '#A78BFA' },
      { key: 'precision', label: 'With Validation', color: '#06B6D4' },
      { key: 'precision_oracle_validation', label: 'Oracle', color: '#5B21B6' },
    ],
  },
  {
    title: 'F1 Tiers', key: 'f1', color: '#8B5CF6', isPercentage: true,
    chartType: 'grouped_tiers',
    tiers: [
      { key: 'f1_no_validation', label: 'No Validation', color: '#6EE7B7' },
      { key: 'f1', label: 'With Validation', color: '#8B5CF6' },
      { key: 'f1_oracle_validation', label: 'Oracle', color: '#047857' },
    ],
  },
  {
    title: 'Validator Quality', key: 'ai_rejection_precision', color: '#CA8A04', isPercentage: true,
    chartType: 'grouped_tiers',
    tiers: [
      { key: 'ai_rejection_precision', label: 'Precision', color: '#F43F5E' },
      { key: 'ai_rejection_recall', label: 'Recall', color: '#CA8A04' },
      { key: 'ai_validation_f1', label: 'F1', color: '#7C2D12' },
      { key: 'validator_accuracy', label: 'Accuracy', color: '#0369A1' },
    ],
  },
];

// Embedding Retrieval — corpus-preparation stats from the runner's row result.
// Per-query AUC / best-F1 metrics live in the interactive query panel, not here.
export const EMBEDDING_RETRIEVAL_METRICS: EvaluationMetricDefinition[] = [
  { title: 'Total Videos', key: 'total_videos', color: '#3B82F6', isPercentage: false, group: 'Corpus' },
  { title: 'Relevant Videos', key: 'relevant_videos', color: '#10B981', isPercentage: false, group: 'Corpus' },
  { title: 'Negative Videos', key: 'negative_videos', color: '#6B7280', isPercentage: false, group: 'Corpus' },
  { title: 'Embeddings Cached', key: 'embeddings_cached', color: '#8B5CF6', isPercentage: false, group: 'Embeddings' },
  { title: 'Embeddings Generated', key: 'embeddings_generated', color: '#06B6D4', isPercentage: false, group: 'Embeddings' },
  // 0 = the whole video was embedded as one vector; anything else is the
  // window length. Tells two otherwise-identical ablation runs apart.
  { title: 'Window (s)', key: 'window_seconds', color: '#6366F1', isPercentage: false, group: 'Embeddings' },
  { title: 'Sampling FPS', key: 'fps', color: '#6366F1', isPercentage: false, group: 'Embeddings' },
  { title: 'Spans Embedded', key: 'spans_embedded', color: '#6366F1', isPercentage: false, group: 'Embeddings' },
  { title: 'Spans / Video', key: 'spans_per_video', color: '#6366F1', isPercentage: false, group: 'Embeddings' },
  { title: 'Pre-summaries Generated', key: 'presummaries_generated', color: '#14B8A6', isPercentage: false, group: 'Embeddings' },
  { title: 'Embeddings Failed', key: 'embeddings_failed', color: '#EF4444', isPercentage: false, group: 'Embeddings' },
  { title: 'Missing Pre-summary', key: 'missing_presummary', color: '#F59E0B', isPercentage: false, group: 'Embeddings' },
];

// Resolve which metric list to render for a given benchmark + protocol.
// Default = MotionBench so existing runs without an explicit benchmark_name still render.
// `opts.protocol` (DrivingDojo only): "event_overlap_v1" → new per-class F1 cards;
// anything else → legacy top-K accuracy cards (kept watchable for old runs).
// `opts.topK` (DrivingDojo legacy only).
export function getBenchmarkMetrics(
  benchmarkName: string | undefined | null,
  opts?: { topK?: number; protocol?: string | null | undefined; activeClasses?: string[] | null; mode?: string | null | undefined; forGraphs?: boolean },
) {
  switch ((benchmarkName ?? '').toLowerCase()) {
    case 'droid': {
      // Event-detection mode has a completely different metric set: P/R/F1
      // against a single target class instead of multi-class accuracy.
      if (opts?.mode === 'event_detection') {
        return DROID_EVENT_DETECTION_METRICS;
      }
      // When the run only opted into a subset of the 14 DROID classes, hide
      // the per-class cards for the un-selected ones — empty bars + 0/0
      // sublines are pure noise for ablations that focus on a few classes.
      const active = opts?.activeClasses;
      if (!active || active.length === 0) return DROID_METRICS;
      const activeSet = new Set(active);
      const isPerClassKey = (key: string) =>
        key.startsWith('accuracy/') || key.startsWith('target_object_accuracy/');
      const classIdFromKey = (key: string) => key.split('/')[1];
      return DROID_METRICS.filter((m) => {
        const k = (m as { key?: string }).key ?? '';
        if (!isPerClassKey(k)) return true;
        return activeSet.has(classIdFromKey(k));
      });
    }
    case 'drivingdojo': {
      if (opts?.protocol === 'event_overlap_v1') {
        return opts?.forGraphs ? DRIVINGDOJO_GRAPH_METRICS : DRIVINGDOJO_EVENT_METRICS;
      }
      // Legacy top-K accuracy view. Old runs keep rendering exactly as before.
      const topK = Math.max(1, Math.min(5, opts?.topK ?? 1));
      if (topK <= 1) return DRIVINGDOJO_METRICS;
      const topkCards = Array.from({ length: topK }, (_, i) => ({
        title: `Top-${i + 1}`,
        key: `accuracy/topk/${i + 1}`,
        color: '#8B5CF6',
        isPercentage: true,
        correctKey: `correct/topk/${i + 1}`,
        totalKey: `total/topk/${i + 1}`,
        group: 'Top-k Ranks',
      }));
      // Keep "Overall Accuracy" first, then the rank curve, then everything else.
      return [DRIVINGDOJO_METRICS[0], ...topkCards, ...DRIVINGDOJO_METRICS.slice(1)];
    }
    case 'refav':
    case 'embedding_retrieval':
      return EMBEDDING_RETRIEVAL_METRICS;
    case 'vifailback':
      return VIFAILBACK_METRICS;
    case 'motionbench':
    default:
      return MOTIONBENCH_METRICS;
  }
}

// Backwards-compat alias — old call sites still import BENCHMARK_METRICS and
// historically got the MotionBench shape. Keep the alias so we don't break them
// during the rollout; new code should call getBenchmarkMetrics(benchmark_name).
export const BENCHMARK_METRICS = MOTIONBENCH_METRICS;

// Segmentation-specific metrics for summary display.
//
// Robotics runs headline exact-class F1 at IoU 0.5/0.75 and sampled frame
// accuracy. Runs without those keys keep the existing TAS headline cards.
// Remaining metrics are available in the comparison grid and CSV export.
export const SEGMENTATION_METRICS = [
  { title: 'F1@0.5', key: 'robotics/f1@0.5', color: '#A855F7', isPercentage: true },
  { title: 'F1@0.75', key: 'robotics/f1@0.75', color: '#06B6D4', isPercentage: true },
  { title: 'Frame Accuracy', key: 'robotics/frame_accuracy', color: '#8B5CF6', isPercentage: true },
  // Aggregated headline (top-5 summary cards on EvaluationRunPage).
  { title: 'Frame Accuracy', key: 'duration/micro/precision', color: '#8B5CF6', isPercentage: true },
  { title: 'Edit (order)', key: 'tas/edit', color: '#F59E0B', isPercentage: true },
  { title: 'F1@10', key: 'tas/f1@10', color: '#06B6D4', isPercentage: true },
  { title: 'F1@25', key: 'tas/f1@25', color: '#0EA5E9', isPercentage: true },
  { title: 'F1@50', key: 'tas/f1@50', color: '#A855F7', isPercentage: true },
  // Secondary metrics — surfaced by the compare grid / CSV, not in the top cards.
  { title: 'Instance F1 (Micro)', key: 'instance/micro/f1', color: '#F59E0B', isPercentage: true },
  { title: 'Duration F1 (Macro)', key: 'duration/macro/f1', color: '#06B6D4', isPercentage: true },
  { title: 'GT IoU', key: 'gt_iou', color: '#0EA5E9', isPercentage: true },
  { title: 'Pred IoU', key: 'pred_iou', color: '#A855F7', isPercentage: true },
  { title: 'TAS Frame Acc', key: 'tas/frame_acc', color: '#0EA5E9', isPercentage: true },
  // Parent-level TAS metrics (action-only, ignoring the leaf object).
  { title: 'Parent Edit', key: 'parent/tas/edit', color: '#10B981', isPercentage: true },
  { title: 'Parent F1@10', key: 'parent/tas/f1@10', color: '#22C55E', isPercentage: true },
  { title: 'Parent F1@25', key: 'parent/tas/f1@25', color: '#16A34A', isPercentage: true },
  { title: 'Parent F1@50', key: 'parent/tas/f1@50', color: '#15803D', isPercentage: true },
  { title: 'Parent Frame Acc', key: 'parent/tas/frame_acc', color: '#84CC16', isPercentage: true },
  // Additional metrics.
  { title: 'Duration F1 (Micro)', key: 'duration/micro/f1', color: '#10B981', isPercentage: true },
  { title: 'Instance F1 (Macro)', key: 'instance/macro/f1', color: '#10B981', isPercentage: true },
  { title: 'Duration Precision (Macro)', key: 'duration/macro/precision', color: '#EC4899', isPercentage: true },
  { title: 'Duration Recall (Macro)', key: 'duration/macro/recall', color: '#14B8A6', isPercentage: true },
  { title: 'Instance Precision (Macro)', key: 'instance/macro/precision', color: '#F97316', isPercentage: true },
  { title: 'Instance Recall (Macro)', key: 'instance/macro/recall', color: '#84CC16', isPercentage: true },
  // Class-agnostic event-boundary detection is shown by the dedicated, data-driven
  // <BoundaryMetricsByTolerance> section (tolerances per dataset: 5/10/20s for Bedrock,
  // 1/2/5s for Reassemble), so it's intentionally NOT duplicated as static cards here.
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

export const OPEN_ENDED_SEGMENTATION_METRICS: EvaluationMetricDefinition[] = [
  { title: 'Segment F1', key: 'segment_f1', color: '#8B5CF6', isPercentage: true },
  { title: 'Label Accuracy', key: 'label_accuracy', color: '#06B6D4', isPercentage: true },
  { title: 'Semantic Precision', key: 'semantic_precision', color: '#10B981', isPercentage: true },
  { title: 'Semantic Recall', key: 'semantic_recall', color: '#F59E0B', isPercentage: true },
  { title: 'E2E F1', key: 'semantic_e2e_f1', color: '#EC4899', isPercentage: true },
  { title: 'Segment Precision', key: 'segment_precision', color: '#14B8A6', isPercentage: true },
  { title: 'Segment Recall', key: 'segment_recall', color: '#F97316', isPercentage: true },
  { title: 'Temporal Matches', key: 'temporal_match_count', color: '#64748B', isPercentage: false },
  { title: 'Semantic TP', key: 'semantic_tp', color: '#22C55E', isPercentage: false },
  { title: 'Semantic FP', key: 'semantic_fp', color: '#EF4444', isPercentage: false },
  { title: 'Semantic FN', key: 'semantic_fn', color: '#F59E0B', isPercentage: false },
  { title: 'Processing Time', key: 'processing_time_seconds', color: '#6B7280', isPercentage: false },
];

// Segmentation agent names
export const SEGMENTATION_AGENTS = ['Robotic Indexing', 'Robotic Action Segmentation', 'Bedrock Action Segmentation', 'Robotic Action Segmentation (Classify)', 'ARMBench Action Segmentation', 'Reassemble Action Segmentation'];

/// Helper: detect if evaluation config uses segmentation mode.
// Checks job-level evaluation_mode first, falls back to per-row detection for old runs.
export function isOpenEndedSegmentationRun(evaluationMode?: EvaluationMode | string): boolean {
  return evaluationMode === 'open_ended_segmentation';
}

export function isSegmentationRun(configRows?: EvaluationRowConfig[], evaluationMode?: string): boolean {
  if (evaluationMode) return evaluationMode === 'segmentation';
  if (!configRows?.length) return false;
  // Fallback for old runs that stored evaluation_mode per-row
  return configRows.some(
    r => (r as any).evaluationMode === 'segmentation' || (r as any).evaluation_mode === 'segmentation'
      || SEGMENTATION_AGENTS.includes(r.agentName || (r as any).agent_name || '')
  );
}

// Helper: extract unique class names from per_class metric keys
function extractClassNames(metrics: Record<string, any> | undefined): string[] {
  if (!metrics) return [];
  const classes = new Set<string>();
  for (const key of Object.keys(metrics)) {
    const match = key.match(/^per_class\/([^/]+)\//);
    if (match) classes.add(match[1]);
  }
  return Array.from(classes).filter(c => c !== 'NONE').sort();
}

// Helper: extract unique parent class names from per_parent metric keys
function extractParentClassNames(metrics: Record<string, any> | undefined): string[] {
  if (!metrics) return [];
  const classes = new Set<string>();
  for (const key of Object.keys(metrics)) {
    const match = key.match(/^per_parent\/([^/]+)\//);
    if (match) classes.add(match[1]);
  }
  return Array.from(classes).filter(c => c !== 'NONE').sort();
}

// Helper: detect if any row result contains parent-level metrics
export function hasParentMetrics(rowResults: EvaluationRowResult[]): boolean {
  for (const row of rowResults) {
    const m = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    for (const key of Object.keys(m)) {
      if (key.startsWith('per_parent/')) return true;
    }
  }
  return false;
}

// Per-class breakdown table for segmentation with instance/duration toggle
export const SegmentationPerClassTable = ({ rowResults, level = 'child', rowSelector }: { rowResults: EvaluationRowResult[]; level?: 'child' | 'parent'; rowSelector?: ReactNode }) => {
  const [metricType, setMetricType] = useState<'instance' | 'duration'>('instance');
  const [sortStack, setSortStack] = useState<{ key: string; dir: 'asc' | 'desc' }[]>([]);

  // Merge metrics from all rows
  const allMetrics: Record<string, any> = {};
  for (const row of rowResults) {
    const m = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    for (const [k, v] of Object.entries(m)) {
      allMetrics[k] = v;
    }
  }

  const prefix = level === 'parent' ? 'per_parent' : 'per_class';
  const classes = level === 'parent' ? extractParentClassNames(allMetrics) : extractClassNames(allMetrics);
  if (classes.length === 0) return null;

  const getRawVal = (cls: string, suffix: string): number | null => {
    const key = `${prefix}/${cls}/${suffix}`;
    const val = allMetrics[`${key}/mean`] ?? allMetrics[key];
    return typeof val === 'number' ? val : null;
  };

  const getVal = (cls: string, suffix: string): string => {
    const val = getRawVal(cls, suffix);
    if (val === null) return '—';
    if (suffix.includes('precision') || suffix.includes('recall') || suffix.includes('f1') || suffix.includes('iou')) {
      return `${(val * 100).toFixed(1)}%`;
    }
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
  };

  const columns = [
    { label: 'Label', key: '' },
    { label: 'Count (GT)', key: 'gt_count' },
    { label: 'Count (Pred)', key: 'pred_count' },
    { label: 'F1', key: `${metricType}/f1` },
    { label: 'Precision', key: `${metricType}/precision` },
    { label: 'Recall', key: `${metricType}/recall` },
    { label: metricType === 'duration' ? 'TP (s)' : 'TP', key: `${metricType}/tp` },
    { label: metricType === 'duration' ? 'FP (s)' : 'FP', key: `${metricType}/fp` },
    { label: metricType === 'duration' ? 'FN (s)' : 'FN', key: `${metricType}/fn` },
    { label: 'GT IoU', key: 'gt_iou' },
    { label: 'Pred IoU', key: 'pred_iou' },
  ];

  // Click: cycle through desc → asc → remove. Adds to stack if new column.
  const handleSort = (key: string) => {
    setSortStack(prev => {
      const idx = prev.findIndex(s => s.key === key);
      if (idx === -1) {
        // New column — add as highest priority (end of stack) descending (asc for label)
        return [...prev, { key, dir: key === '' ? 'asc' : 'desc' }];
      }
      const entry = prev[idx];
      if ((key === '' && entry.dir === 'desc') || (key !== '' && entry.dir === 'asc')) {
        // Already toggled once — remove from stack
        return prev.filter((_, i) => i !== idx);
      }
      // Toggle direction
      const next = [...prev];
      next[idx] = { ...entry, dir: entry.dir === 'asc' ? 'desc' : 'asc' };
      return next;
    });
  };

  const sortedClasses = [...classes].sort((a, b) => {
    for (const { key, dir } of sortStack) {
      let cmp: number;
      if (key === '') {
        cmp = a.localeCompare(b);
      } else {
        const aVal = getRawVal(a, key) ?? -Infinity;
        const bVal = getRawVal(b, key) ?? -Infinity;
        cmp = aVal - bVal;
      }
      if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    }
    return 0;
  });

  const SortIcon = ({ colKey }: { colKey: string }) => {
    const entry = sortStack.find(s => s.key === colKey);
    const rank = sortStack.findIndex(s => s.key === colKey);
    if (!entry) return <ArrowUpDown className="w-3 h-3 text-gray-300" />;
    return (
      <span className="inline-flex items-center gap-0.5">
        {entry.dir === 'asc'
          ? <ArrowUp className="w-3 h-3 text-navy-600" />
          : <ArrowDown className="w-3 h-3 text-navy-600" />}
        {sortStack.length > 1 && <span className="text-[9px] font-bold text-navy-500">{rank + 1}</span>}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Detailed Label Metrics</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Per-label breakdown showing <span className="font-medium">{metricType}-weighted</span> metrics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rowSelector}
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
            {(['instance', 'duration'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setMetricType(t)}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${
                  metricType === t
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'instance' ? 'Instance' : 'Duration'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map(col => (
                <th
                  key={col.label}
                  className="text-left py-2.5 px-3 font-medium text-gray-600 whitespace-nowrap border-r border-gray-100 last:border-r-0 cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <SortIcon colKey={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedClasses.map(cls => (
              <tr key={cls} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-800 border-r border-gray-100">{cls}</td>
                {columns.slice(1).map(col => (
                  <td key={col.key} className="py-2 px-3 text-gray-600 whitespace-nowrap border-r border-gray-100 last:border-r-0">
                    {getVal(cls, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
              <td className="py-2.5 px-3 text-gray-900 border-r border-gray-100">Average</td>
              {columns.slice(1).map(col => {
                const vals = classes.map(cls => getRawVal(cls, col.key)).filter((v): v is number => v !== null);
                if (vals.length === 0) return <td key={col.key} className="py-2.5 px-3 text-gray-400 whitespace-nowrap border-r border-gray-100 last:border-r-0">—</td>;
                const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
                const isPercent = col.key.includes('precision') || col.key.includes('recall') || col.key.includes('f1') || col.key.includes('iou');
                const formatted = isPercent ? `${(avg * 100).toFixed(1)}%` : Number.isInteger(avg) ? String(avg) : avg.toFixed(2);
                return (
                  <td key={col.key} className="py-2.5 px-3 text-gray-900 whitespace-nowrap border-r border-gray-100 last:border-r-0">
                    {formatted}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/**
 * Card chrome shared by every chart/graph in the eval results, with a built-in
 * collapse toggle in the header. Pass the title/description; everything else is
 * the chart body. `headerRight` is for inline legends or extra controls.
 */
export const CollapsibleCard = ({
  title,
  description,
  headerRight,
  defaultCollapsed = false,
  className,
  bodyClassName,
  children,
}: {
  title: string;
  description?: ReactNode;
  headerRight?: ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // When collapsed, `self-start` opts out of the grid's stretch so the card
  // shrinks to just its header; expanded, `h-full` fills the row height.
  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 p-4 flex flex-col', collapsed ? 'self-start' : 'h-full', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {!collapsed && headerRight}
          <button
            type="button"
            onClick={() => setCollapsed(c => !c)}
            className="flex items-center justify-center size-7 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {!collapsed && <div className={cn('mt-3 flex-1 min-h-0', bodyClassName)}>{children}</div>}
    </div>
  );
};

// Horizontal grouped bar charts for segmentation (matches stat-2.png)
export const SegmentationBarCharts = ({ rowResults, level = 'child' }: { rowResults: EvaluationRowResult[]; level?: 'child' | 'parent' }) => {
  // Title of the chart currently open in the full-view modal (null = closed).
  const [expandedTitle, setExpandedTitle] = useState<string | null>(null);
  const allMetrics: Record<string, any> = {};
  for (const row of rowResults) {
    const m = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    for (const [k, v] of Object.entries(m)) {
      allMetrics[k] = v;
    }
  }

  const prefix = level === 'parent' ? 'per_parent' : 'per_class';
  const classes = level === 'parent' ? extractParentClassNames(allMetrics) : extractClassNames(allMetrics);
  if (classes.length === 0) return null;

  const getNumVal = (cls: string, suffix: string): number => {
    const key = `${prefix}/${cls}/${suffix}`;
    const val = allMetrics[`${key}/mean`] ?? allMetrics[key];
    return typeof val === 'number' ? val : 0;
  };

  const charts = [
    { title: 'F1 by Label', instSuffix: 'instance/f1', durSuffix: 'duration/f1', description: 'Harmonic mean of precision and recall', labels: ['Instance', 'Duration'] as const },
    { title: 'Recall by Label', instSuffix: 'instance/recall', durSuffix: 'duration/recall', description: 'What fraction of GT events are detected?', labels: ['Instance', 'Duration'] as const },
    { title: 'IoU by Label', instSuffix: 'pred_iou', durSuffix: 'gt_iou', description: 'Overlap quality between predictions and ground truth', labels: ['Pred IoU', 'GT IoU'] as const },
    { title: 'Precision by Label', instSuffix: 'instance/precision', durSuffix: 'duration/precision', description: 'What fraction of predictions are correct?', labels: ['Instance', 'Duration'] as const },
  ];

  // Render a chart body at a small (inline, 4-up) or large (modal) size.
  const renderChartBody = (chart: typeof charts[number], compact: boolean) => {
    const [label1, label2] = chart.labels;
    const data = classes.map(cls => ({
      name: cls,
      bar1: +(getNumVal(cls, chart.instSuffix) * 100).toFixed(1),
      bar2: +(getNumVal(cls, chart.durSuffix) * 100).toFixed(1),
    }));
    const rowH = compact ? 22 : 34;
    const minH = compact ? 150 : 260;
    const fontSize = compact ? 9 : 12;
    const barSize = compact ? 9 : 14;

    // Keep the label column tight to save space: every label uses the SAME small
    // font and is ellipsis-truncated to fit, so sizes stay uniform. The full name
    // shows on hover via a native <title> tooltip.
    const charRatio = 0.52;
    const labelPad = 10;
    const yLabelFont = compact ? 9 : 11;
    const widthCap = compact ? 110 : 160;
    const longestLabel = classes.reduce((m, c) => Math.max(m, c.length), 0) || 1;
    const yWidth = Math.min(widthCap, Math.ceil(longestLabel * yLabelFont * charRatio) + labelPad);
    const availLabelW = yWidth - labelPad;
    const maxLabelChars = Math.max(1, Math.floor(availLabelW / (yLabelFont * charRatio)));
    const renderYAxisTick = ({ x, y, payload }: { x: number; y: number; payload: { value: string | number } }) => {
      const raw = String(payload.value);
      const text = raw.length > maxLabelChars ? `${raw.slice(0, maxLabelChars - 1)}…` : raw;
      return (
        <text x={x} y={y} dy={yLabelFont / 3} textAnchor="end" fill="#6b7280" fontSize={yLabelFont}>
          <title>{raw}</title>
          {text}
        </text>
      );
    };
    return (
      <div className="flex h-full flex-col">
        <ResponsiveContainer width="100%" height={Math.max(minH, classes.length * rowH)} className="shrink-0">
          <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fontSize }} />
            <YAxis type="category" dataKey="name" width={yWidth} tick={renderYAxisTick} interval={0} />
            <Tooltip formatter={(value: number) => `${value}%`} />
            <Bar dataKey="bar1" name={label1} fill="#8491d2" barSize={barSize} />
            <Bar dataKey="bar2" name={label2} fill="#cbd5e1" barSize={barSize} />
          </BarChart>
        </ResponsiveContainer>
        {/* mt-auto pins the legend to the bottom so it lines up across cards of differing height */}
        <div className="flex items-center gap-4 mt-auto pt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#8491d2' }} /> {label1}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#cbd5e1' }} /> {label2}</span>
        </div>
      </div>
    );
  };

  const expandedChart = charts.find(c => c.title === expandedTitle) ?? null;

  return (
    <div className="grid gap-4 items-stretch grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
      {charts.map(chart => (
        <CollapsibleCard
          key={chart.title}
          title={chart.title}
          description={chart.description}
          headerRight={
            <button
              type="button"
              onClick={() => setExpandedTitle(chart.title)}
              className="flex items-center justify-center size-7 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
              title="Expand to full view"
              aria-label="Expand to full view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          }
        >
          {renderChartBody(chart, true)}
        </CollapsibleCard>
      ))}

      {expandedChart && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
          onClick={() => setExpandedTitle(null)}
        >
          <div
            className="w-[min(900px,95vw)] max-h-[90vh] overflow-auto rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h4 className="text-base font-semibold text-gray-900">{expandedChart.title}</h4>
                <p className="mt-0.5 text-xs text-gray-500">{expandedChart.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setExpandedTitle(null)}
                className="flex size-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                title="Close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderChartBody(expandedChart, false)}
          </div>
        </div>
      )}
    </div>
  );
};

// Confusion matrix heatmap for segmentation
type ConfusionMatrixParams = {
  title: string;
  subtitle: string;
  matrixStr: string;
  rowAxisLabel: string;
  colAxisLabel: string;
};

export const ConfusionMatrixHeatmap = ({ rowResults, level = 'child' }: { rowResults: EvaluationRowResult[]; level?: 'child' | 'parent' }) => {
  // Which matrix (if any) is open in the full-window modal.
  const [expanded, setExpanded] = useState<ConfusionMatrixParams | null>(null);

  // Find confusion matrix strings from metrics
  const gtKey = level === 'parent' ? 'parent_confusion_matrix_gt' : 'confusion_matrix_gt';
  const predKey = level === 'parent' ? 'parent_confusion_matrix_pred' : 'confusion_matrix_pred';
  let gtMatrixStr: string | undefined;
  let predMatrixStr: string | undefined;

  for (const row of rowResults) {
    const m = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    if (m[gtKey]) gtMatrixStr = m[gtKey];
    if (m[predKey]) predMatrixStr = m[predKey];
  }

  if (!gtMatrixStr && !predMatrixStr) return null;

  // Estimate a matrix's natural inline width (label count + longest label) so we
  // can let the browser pack two matrices per row only when both comfortably
  // fit, and fall back to one-per-row (full width) for large matrices.
  const INLINE_CELL = 30;
  const matrixStats = (matrixStr?: string) => {
    if (!matrixStr) return { count: 0, longest: 0 };
    try {
      const m = JSON.parse(matrixStr) as Record<string, Record<string, number>>;
      const set = new Set<string>();
      for (const r of Object.keys(m)) {
        set.add(r);
        for (const c of Object.keys(m[r])) set.add(c);
      }
      let longest = 0;
      for (const l of set) longest = Math.max(longest, l.length);
      return { count: set.size, longest };
    } catch {
      return { count: 0, longest: 0 };
    }
  };
  const gtStats = matrixStats(gtMatrixStr);
  const predStats = matrixStats(predMatrixStr);
  const maxCount = Math.max(gtStats.count, predStats.count);
  const maxLongest = Math.max(gtStats.longest, predStats.longest);
  // axis label (24) + row-label column + cells + card padding (48) + buffer.
  const estMatrixWidth = 48 + 24 + (maxLongest * 6 + 16) + maxCount * INLINE_CELL + 16;

  // Render just the matrix table at a given cell size. Returns null when the
  // data is missing/unparseable. Small sizes are used inline; the modal uses a
  // larger size for a comfortable full-window view.
  const buildMatrix = (
    matrixStr: string | undefined,
    rowAxisLabel: string,
    colAxisLabel: string,
    cellSize: number,
  ) => {
    if (!matrixStr) return null;
    let matrix: Record<string, Record<string, number>>;
    try {
      matrix = JSON.parse(matrixStr);
    } catch {
      return null;
    }

    // Unify row and column labels so the diagonal represents correct predictions
    const allLabelsSet = new Set<string>();
    for (const rowKey of Object.keys(matrix)) {
      allLabelsSet.add(rowKey);
      for (const colKey of Object.keys(matrix[rowKey])) allLabelsSet.add(colKey);
    }
    const labels = Array.from(allLabelsSet).sort();
    const rowLabels = labels;
    const colLabels = labels;

    // Compute global max for consistent heat scaling across the matrix
    let globalMax = 1;
    for (const cols of Object.values(matrix)) {
      for (const v of Object.values(cols)) {
        if (v > globalMax) globalMax = v;
      }
    }

    const small = cellSize <= 36;
    const labelFont = small ? 'text-[9px]' : 'text-[11px]';
    const valueFont = small ? 'text-[10px]' : 'text-xs';
    const labelFontPx = small ? 9 : 11;

    // Column labels render as diagonal text (rotated -50°) above the grid. Size
    // the header to the longest label's vertical extent so labels never overflow
    // out of it, and reserve matching padding for their rightward drift.
    let longestLabel = 0;
    for (const l of labels) longestLabel = Math.max(longestLabel, l.length);
    const labelPx = longestLabel * labelFontPx * 0.62;
    const labelExtent = Math.ceil(labelPx * 0.77) + 10;
    const labelRightPad = Math.ceil(labelPx * 0.64);

    return (
      <div className="overflow-x-auto">
        <div className="inline-block" style={{ paddingRight: labelRightPad }}>
          <div className="flex">
            {/* Row axis label — padded to stay centred on the grid (tbody) now
                that the diagonal labels sit above and the title sits below it. */}
            <div className="flex items-center justify-center" style={{ width: 24, paddingTop: labelExtent, paddingBottom: 28 }}>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>{rowAxisLabel}</span>
            </div>
            <table className="border-collapse" style={{ borderSpacing: 0 }}>
              {/* Column labels ABOVE the grid, rotated -50° ascending to the
                  right. Header height fits the longest label so it never
                  overflows up into the card title. */}
              <thead>
                <tr>
                  <th />
                  {colLabels.map(col => (
                    <th key={col} style={{ width: cellSize, height: labelExtent, verticalAlign: 'bottom', padding: 0 }}>
                      <div className="relative" style={{ height: labelExtent, width: cellSize }}>
                        <span
                          className={cn('absolute font-medium text-gray-600 whitespace-nowrap origin-bottom-left', labelFont)}
                          style={{ bottom: 4, left: cellSize / 2 + 4, transform: 'rotate(-50deg)' }}
                        >
                          {col}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowLabels.map(row => (
                  <tr key={row}>
                    {/* Row label — auto width, right-aligned */}
                    <td className="pr-3 whitespace-nowrap text-right" style={{ height: cellSize }}>
                      <span className={cn('font-medium text-gray-600', labelFont)}>{row}</span>
                    </td>
                    {colLabels.map(col => {
                      const val = matrix[row]?.[col] ?? 0;
                      const intensity = globalMax > 0 ? val / globalMax : 0;
                      const isDiagonal = row === col;
                      const bgColor = val === 0
                        ? '#F9FAFB'
                        : isDiagonal
                          ? `rgba(34, 197, 94, ${Math.max(0.15, intensity * 0.85)})`
                          : `rgba(239, 68, 68, ${Math.max(0.1, intensity * 0.7)})`;
                      const textColor = intensity > 0.55 ? '#FFFFFF' : '#374151';
                      return (
                        <td key={col} style={{ padding: 1 }}>
                          <div
                            className="flex items-center justify-center"
                            style={{
                              width: cellSize,
                              height: cellSize,
                              backgroundColor: bgColor,
                              color: textColor,
                              borderRadius: 4,
                            }}
                          >
                            <span className={cn('font-semibold tabular-nums', valueFont)}>
                              {val || ''}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              {/* Axis title BELOW the grid — a single clean line, so the diagonal
                  column labels above never collide with it. */}
              <tfoot>
                <tr>
                  <td />
                  <td colSpan={colLabels.length} className="text-center pt-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{colAxisLabel}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Inline card: small matrix + an expand button that opens the modal.
  const renderCard = (title: string, subtitle: string, matrixStr: string | undefined, rowAxisLabel = 'Actual', colAxisLabel = 'Predicted') => {
    const body = buildMatrix(matrixStr, rowAxisLabel, colAxisLabel, INLINE_CELL);
    if (!body || !matrixStr) return null;
    return (
      <CollapsibleCard
        key={title}
        title={title}
        description={subtitle}
        className="p-6"
        headerRight={
          <button
            type="button"
            onClick={() => setExpanded({ title, subtitle, matrixStr, rowAxisLabel, colAxisLabel })}
            className="flex items-center justify-center size-7 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
            title="Expand to full view"
            aria-label="Expand to full view"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        }
      >
        {body}
      </CollapsibleCard>
    );
  };

  return (
    <div
      className="grid gap-4 items-stretch"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(${estMatrixWidth}px, 100%), 1fr))` }}
    >
      {renderCard('Confusion Matrix (GT)', 'Rows = ground truth, columns = predicted', gtMatrixStr)}
      {renderCard('Confusion Matrix (Pred)', 'Rows = predicted, columns = ground truth', predMatrixStr, 'Predicted', 'Actual')}

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
          onClick={() => setExpanded(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-[95vw] max-h-[90vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h4 className="text-base font-semibold text-gray-900">{expanded.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{expanded.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(null)}
                className="flex items-center justify-center size-8 flex-shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
                title="Close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {(() => {
              // Size cells so the whole matrix fits inside the modal
              // (95vw × 90vh) minus its chrome — no scrolling. Rows === cols
              // (labels are unified), so the cell count is the same on both axes.
              const stats = matrixStats(expanded.matrixStr);
              const count = stats.count || 1;
              const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
              const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
              // Mirror buildMatrix's large-size label geometry (11px font) so the
              // budget matches what actually renders: long horizontal row labels
              // on the left, diagonal column labels below, and their rightward drift.
              const labelPx = stats.longest * 11 * 0.62;
              const footerH = labelPx * 0.77 + 10;
              const rowLabelW = labelPx + 16;
              const rightOverflow = labelPx * 0.64;
              const availW = vw * 0.95 - 48 - 28 - rowLabelW - rightOverflow;
              const availH = vh * 0.90 - 48 - 70 - 30 - footerH;
              const fit = Math.floor(Math.min(availW / count, availH / count));
              const cell = Math.max(16, Math.min(60, fit));
              return buildMatrix(expanded.matrixStr, expanded.rowAxisLabel, expanded.colAxisLabel, cell);
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

// Confidence-threshold precision/recall/F1 curve, one small chart per query
// that has the data. Two possible shapes on aggregated_metrics, both written
// as a JSON string `{thresholds, precision, recall, f1, tp, fp, n_scored,
// n_total_predictions}`:
//   - `confidence_pr_curve` — one curve per row (default event-detection eval
//     path, and DROID's event-detection mode), set by `build_confidence_pr_curve`
//     in evaluation_runner.py.
//   - `confidence_pr_curve/class/<action_id>` — DrivingDojo packs every class
//     query into a single row, so its per-class curves (from
//     `build_confidence_pr_curve` in drivingdojo_eval.py) are suffixed instead.
// Self-hides entirely when no row/class has the data, e.g. DROID
// classification mode, segmentation, or runs where events never went through
// confidence prediction.
type ConfidencePRCurveData = {
  thresholds: number[];
  precision: number[];
  recall: number[];
  f1: number[];
  tp: number[];
  fp: number[];
  n_scored: number;
  n_total_predictions: number;
  tp_scores?: number[];
  fp_scores?: number[];
};

const parseConfidencePRCurve = (raw: unknown): ConfidencePRCurveData | null => {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const data = JSON.parse(raw) as ConfidencePRCurveData;
    return data?.thresholds?.length ? data : null;
  } catch {
    return null;
  }
};

// The "current" (actual, unthresholded) precision/recall/F1 — shown in the
// card summary and used as the tooltip's green/red delta baseline. Distinct
// from the curve's own t=0 point: the curve only covers confidence-scored
// items, while "current" is the real headline metric across every prediction.
type CurrentPRF1 = { precision?: number; recall?: number; f1?: number };

type ConfidencePRCurveSource = { key: string; label: string; data: ConfidencePRCurveData; current: CurrentPRF1 };

const DRIVINGDOJO_ACTION_LABELS = new Map(DRIVINGDOJO_ACTIONS.map(a => [a.id, a.label]));

const collectConfidencePRCurves = (
  rowResults: EvaluationRowResult[],
  configRows?: EvaluationRowConfig[],
): ConfidencePRCurveSource[] => {
  const sources: ConfidencePRCurveSource[] = [];
  rowResults.forEach((row, idx) => {
    const m: any = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    const rowLevelData = parseConfidencePRCurve(m.confidence_pr_curve);
    if (rowLevelData) {
      sources.push({
        key: row.row_id,
        label: getEvalRowDisplayName(configRows?.[idx], row, idx),
        data: rowLevelData,
        current: { precision: m.precision, recall: m.recall, f1: m.f1 ?? m.f1_score },
      });
    }
    for (const key of Object.keys(m)) {
      const match = key.match(/^confidence_pr_curve\/class\/(.+)$/);
      if (!match) continue;
      const data = parseConfidencePRCurve(m[key]);
      if (!data) continue;
      const actionId = match[1];
      sources.push({
        key: `${row.row_id}:${actionId}`,
        label: DRIVINGDOJO_ACTION_LABELS.get(actionId) || actionId,
        data,
        current: {
          precision: m[`precision/class/${actionId}`],
          recall: m[`recall/class/${actionId}`],
          f1: m[`f1/class/${actionId}`],
        },
      });
    }
  });
  return sources;
};

const CONFIDENCE_CURVE_CHART_HEIGHT = 260;

// Signed delta formatter for the tooltip: green when the swept point beats
// today's actual (unthresholded) value, red when it's worse, grey when there's
// no baseline to compare against (e.g. per-class current stat missing).
const _deltaSpan = (value: number, baseline: number | undefined) => {
  if (baseline == null || !Number.isFinite(baseline)) return null;
  const delta = value - baseline;
  const color = delta > 0.0005 ? '#059669' : delta < -0.0005 ? '#DC2626' : '#6B7280';
  const sign = delta > 0 ? '+' : '';
  return <span style={{ color }}>({sign}{(delta * 100).toFixed(1)}pp)</span>;
};

// FN isn't shipped in ConfidencePRCurveData, but it's derivable: the backend
// computes fn_t = (fixed GT count) - tp_t at every threshold, so tp[i]+fn[i]
// is constant across all i for a given query. We infer that constant from
// the threshold with the highest recall (most numerically stable), then back
// out fn at the best-F1 threshold — used for micro-averaging TP/FP/FN across
// queries in the per-query threshold summary below.
const getBestF1ThresholdStats = (data: ConfidencePRCurveData) => {
  const bestF1Idx = data.f1.reduce((best, v, i) => (v > data.f1[best] ? i : best), 0);
  const maxRecallIdx = data.recall.reduce((best, v, i) => (v > data.recall[best] ? i : best), 0);
  const totalGt = data.recall[maxRecallIdx] > 0 ? data.tp[maxRecallIdx] / data.recall[maxRecallIdx] : 0;
  const tp = data.tp[bestF1Idx];
  const fp = data.fp[bestF1Idx];
  const fn = Math.max(0, totalGt - tp);
  return {
    bestF1Idx,
    threshold: data.thresholds[bestF1Idx],
    precision: data.precision[bestF1Idx],
    recall: data.recall[bestF1Idx],
    f1: data.f1[bestF1Idx],
    tp,
    fp,
    fn,
  };
};

/** One PR-curve-vs-threshold card, shared by ConfidencePRCurveChart (one per
 * query/row) and the Driving Dojo merged-query view (exactly one card). */
const ConfidencePRCurveCard = ({ label, data, current }: { label: string; data: ConfidencePRCurveData; current?: CurrentPRF1 }) => {
  const { bestF1Idx, threshold: bestThreshold, precision: bestPrecision, recall: bestRecall, f1: bestF1 } = getBestF1ThresholdStats(data);
  // Recall increases monotonically as the threshold decreases (a lower bar
  // only ever admits more true positives), so sorting by recall gives the
  // AreaChart a well-ordered x-axis for a proper PR curve.
  const chartData = data.thresholds
    .map((t, i) => ({
      threshold: t,
      precision: data.precision[i],
      recall: data.recall[i],
      f1: data.f1[i],
    }))
    .sort((a, b) => a.recall - b.recall);
  // Trapezoidal AUC-PR over the swept thresholds.
  const aucPr = chartData.slice(1).reduce((sum, pt, i) => {
    const prev = chartData[i];
    return sum + (pt.recall - prev.recall) * (pt.precision + prev.precision) / 2;
  }, 0);
  const coverage = data.n_scored < data.n_total_predictions
    ? `${data.n_scored} of ${data.n_total_predictions} predictions have a confidence score`
    : `${data.n_scored} predictions scored`;

  // Histogram of raw per-detection confidence scores, bucketed to the same
  // granularity as the threshold sweep (0.05-wide bins), stacked by whether
  // the detection was judged a true or false positive.
  const tpScores = data.tp_scores ?? [];
  const fpScores = data.fp_scores ?? [];
  const NUM_BINS = 20;
  const histData = Array.from({ length: NUM_BINS }, (_, i) => {
    const lo = i / NUM_BINS, hi = (i + 1) / NUM_BINS;
    const inBin = (s: number) => (i === NUM_BINS - 1 ? s >= lo && s <= hi : s >= lo && s < hi);
    return {
      bin: lo,
      label: `${lo.toFixed(2)}-${hi.toFixed(2)}`,
      tp: tpScores.filter(inBin).length,
      fp: fpScores.filter(inBin).length,
    };
  });
  const hasHistogram = tpScores.length + fpScores.length > 0;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
      <h5 className="text-xs font-semibold text-gray-900 truncate" title={label}>{label}</h5>
      <p className="text-[11px] text-gray-500 mt-0.5">
        AUC-PR {aucPr.toFixed(2)} &middot; best F1 {bestF1.toFixed(2)} @ threshold {bestThreshold.toFixed(2)} (P={bestPrecision.toFixed(2)}, R={bestRecall.toFixed(2)}) &middot; {coverage}
        {current && (current.precision != null || current.recall != null || current.f1 != null) && (
          <>
            {' '}&middot; current: P={current.precision != null ? current.precision.toFixed(2) : '—'}, R={current.recall != null ? current.recall.toFixed(2) : '—'}, F1={current.f1 != null ? current.f1.toFixed(2) : '—'}
          </>
        )}
      </p>
      <div className={cn('mt-2 grid grid-cols-1 gap-3', hasHistogram && 'lg:grid-cols-2')}>
        <div style={{ width: '100%', height: CONFIDENCE_CURVE_CHART_HEIGHT }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="recall"
                type="number"
                domain={[0, 1]}
                tick={{ fontSize: 11, fill: '#6B7280' }}
                label={{ value: 'Recall', position: 'insideBottom', offset: -3, fontSize: 11 }}
              />
              <YAxis
                dataKey="precision"
                domain={[0, 1]}
                tick={{ fontSize: 11, fill: '#6B7280' }}
                label={{ value: 'Precision', angle: -90, position: 'insideLeft', fontSize: 11 }}
              />
              <Tooltip
                content={({ active, payload }: any) => {
                  if (!active || !payload?.length) return null;
                  const pt = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded shadow-sm px-2 py-1.5 text-xs space-y-0.5">
                      <div className="font-semibold text-gray-900">Threshold: {pt.threshold.toFixed(2)}</div>
                      <div className="text-gray-600">Precision: {pt.precision.toFixed(3)} {current && _deltaSpan(pt.precision, current.precision)}</div>
                      <div className="text-gray-600">Recall: {pt.recall.toFixed(3)} {current && _deltaSpan(pt.recall, current.recall)}</div>
                      <div className="text-gray-600">F1: {pt.f1.toFixed(3)} {current && _deltaSpan(pt.f1, current.f1)}</div>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="precision"
                name="Precision"
                stroke="#1f2753"
                fill="#1f2753"
                fillOpacity={0.25}
                strokeWidth={2}
                dot={{ r: 3, stroke: '#1f2753', strokeWidth: 1.5, fill: 'white' }}
                activeDot={{ r: 5 }}
              />
              <ReferenceDot x={bestRecall} y={bestPrecision} r={5} fill="#F59E0B" stroke="white" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {hasHistogram && (
          <div style={{ width: '100%', height: CONFIDENCE_CURVE_CHART_HEIGHT }}>
            <ResponsiveContainer>
              <BarChart data={histData} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bin"
                  type="number"
                  domain={[0, 1]}
                  tickFormatter={(v) => v.toFixed(1)}
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  label={{ value: 'Confidence score', position: 'insideBottom', offset: -3, fontSize: 11 }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip
                  formatter={(value: any, name: any) => [value, name === 'tp' ? 'True Positive' : 'False Positive']}
                  labelFormatter={(_: any, payload: any) => `Score: ${payload?.[0]?.payload?.label ?? ''}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  payload={[
                    { value: 'True Positive', type: 'rect', color: '#059669' },
                    { value: 'False Positive', type: 'rect', color: '#DC2626' },
                  ]}
                />
                <Bar dataKey="tp" name="tp" stackId="a" fill="#059669" />
                <Bar dataKey="fp" name="fp" stackId="a" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

/** Per-query optimal-threshold table + micro-averaged P/R/F1 summary, shared
 * by ConfidencePRCurveChart (one row per eval row/query) and
 * DrivingDojoGraphView (one row per action class). */
const ConfidenceThresholdSummaryTable = ({ sources }: { sources: ConfidencePRCurveSource[] }) => {
  if (sources.length === 0) return null;
  const perQueryStats = sources.map(s => ({ ...s, stats: getBestF1ThresholdStats(s.data) }));
  const microTp = perQueryStats.reduce((sum, s) => sum + s.stats.tp, 0);
  const microFp = perQueryStats.reduce((sum, s) => sum + s.stats.fp, 0);
  const microFn = perQueryStats.reduce((sum, s) => sum + s.stats.fn, 0);
  const microPrecision = microTp + microFp > 0 ? microTp / (microTp + microFp) : 0;
  const microRecall = microTp + microFn > 0 ? microTp / (microTp + microFn) : 0;
  const microF1 = microPrecision + microRecall > 0
    ? 2 * microPrecision * microRecall / (microPrecision + microRecall)
    : 0;

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Per-Query Optimal Thresholds</h4>
      <p className="text-xs text-gray-500 mb-2">
        Green/red deltas compare each query's best-F1 threshold against its current (unthresholded) precision/recall/F1.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 pr-4 font-medium text-gray-600">Query</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-600">Threshold</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-600">Precision</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-600">Recall</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-600">F1</th>
            </tr>
          </thead>
          <tbody>
            {perQueryStats.map(({ key, label, stats, current }) => (
              <tr key={key} className="border-b border-gray-100">
                <td className="py-2 pr-4 text-gray-900 truncate max-w-xs" title={label}>{label}</td>
                <td className="py-2 pr-4 text-gray-700">{stats.threshold.toFixed(2)}</td>
                <td className="py-2 pr-4 text-gray-700">{stats.precision.toFixed(3)} {_deltaSpan(stats.precision, current.precision)}</td>
                <td className="py-2 pr-4 text-gray-700">{stats.recall.toFixed(3)} {_deltaSpan(stats.recall, current.recall)}</td>
                <td className="py-2 pr-4 text-gray-700">{stats.f1.toFixed(3)} {_deltaSpan(stats.f1, current.f1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p
        className="mt-3 text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2"
        title="TP/FP are exact per-query counts at each query's best-F1 threshold. FN is derived from each query's total ground-truth count, inferred from the curve (raw FN isn't shipped to the client) — ΣTP, ΣFP, ΣFN are summed globally across queries, then precision/recall/F1 are computed from those totals."
      >
        <span className="font-semibold text-gray-800">Micro-averaged</span> — if every query used its own optimal threshold above: Precision {microPrecision.toFixed(3)}, Recall {microRecall.toFixed(3)}, F1 {microF1.toFixed(3)}
      </p>
    </div>
  );
};

export const ConfidencePRCurveChart = ({
  rowResults,
  configRows,
}: {
  rowResults: EvaluationRowResult[];
  configRows?: EvaluationRowConfig[];
}) => {
  const sources = collectConfidencePRCurves(rowResults, configRows);
  if (sources.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-amber-600" />
        <h3 className="text-lg font-semibold text-gray-900">Confidence Threshold Analysis</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Precision vs. recall as the confidence-score cutoff sweeps 0.0 → 1.0 (0.05 steps), from the agentic validator's per-event confidence predictions. Shaded area is the area under the PR curve (AUC-PR) — use the marked point to pick a threshold.
      </p>
      <div className="grid grid-cols-1 gap-4">
        {sources.map(({ key, label, data, current }) => (
          <ConfidencePRCurveCard key={key} label={label} data={data} current={current} />
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-gray-200">
        <ConfidenceThresholdSummaryTable sources={sources} />
      </div>
    </div>
  );
};

// Simulates overall F1 if the agentic validator's own rejection precision or
// rejection recall (as a binary classifier over "is this raw initial detector prediction
// actually noise?") were different, holding the raw initial detector's detection behavior
// (raw_tp_count / vlm_noise_count / gt_covered_count / total_gt) fixed.
//
// Two independent sweeps, each holding the OTHER validator stat fixed at its
// currently-measured value:
//   - Recall sweep: as rejection recall (rr) varies, how much of the true
//     noise gets caught (and removed from FP) at the current rejection
//     precision (rp) — i.e. at the current "purity" of what gets rejected.
//   - Precision sweep: as rejection precision (rp) varies, how many true
//     positives get sacrificed to reject the same fixed amount of noise
//     (rr fixed) — a purer rejection call wastes fewer true positives for
//     the same noise removal.
//
// Both curves share the same F1 y-axis so the user can directly compare
// "improving recall alone" vs. "improving precision alone" against the
// baseline they'd get from not validating at all.
type ValidatorSimPoint = { x: number; f1: number };

const _simulateRejectionCurve = (
  sweepRecall: boolean,
  rawTp: number,
  vlmNoiseCount: number,
  gtCovered: number,
  totalGt: number,
  fixedRr: number,
  fixedRp: number,
): ValidatorSimPoint[] => {
  const steps = Array.from({ length: 21 }, (_, i) => i / 20);
  return steps.map((x) => {
    const rr = sweepRecall ? x : fixedRr;
    const rp = sweepRecall ? fixedRp : x;
    const correctlyRejectedNoise = rr * vlmNoiseCount;
    const keptFp = vlmNoiseCount - correctlyRejectedNoise;
    // Total rejected implied by wanting `correctlyRejectedNoise` correct
    // rejections at purity `rp`; incorrectly-rejected (sacrificed true
    // positives) is whatever's left, capped to the true positives that exist.
    const totalRejected = rp > 0 ? correctlyRejectedNoise / rp : (correctlyRejectedNoise > 0 ? Infinity : 0);
    const incorrectlyRejected = Math.max(0, Math.min(rawTp, totalRejected - correctlyRejectedNoise));
    const keptTp = rawTp - incorrectlyRejected;
    const fp = keptFp;
    const tp = keptTp;
    const precision = (tp + fp) > 0 ? tp / (tp + fp) : 0;
    const recallNumerator = rawTp > 0 ? gtCovered * (keptTp / rawTp) : 0;
    const recall = totalGt > 0 ? recallNumerator / totalGt : 0;
    const f1 = (precision + recall) > 0 ? 2 * precision * recall / (precision + recall) : 0;
    return { x, f1 };
  });
};

// Linear-interpolated x-value where a curve first reaches (or exceeds) the
// "No Validation" F1 baseline — the minimum validator stat needed for
// validation to help at all vs. not validating. Null if it never does within
// the simulated [0, 1] range.
const _findF1Breakeven = (curve: ValidatorSimPoint[], f1NoValidation: number): number | null => {
  if (curve.length === 0) return null;
  if (curve[0].f1 >= f1NoValidation) return curve[0].x;
  for (let i = 1; i < curve.length; i++) {
    const prev = curve[i - 1], cur = curve[i];
    if (cur.f1 >= f1NoValidation) {
      const span = cur.f1 - prev.f1;
      const t = span > 0 ? (f1NoValidation - prev.f1) / span : 0;
      return prev.x + t * (cur.x - prev.x);
    }
  }
  return null;
};

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

// One chart per query with two lines — F1 vs. simulated rejection recall,
// and F1 vs. simulated rejection precision — plus a dashed "No Validation"
// reference line and dots marking today's actual validator stats. Reads
// plain (unsuffixed) keys, so callers must pass a row whose
// `aggregated_metrics` is already flattened to a single query — DrivingDojo's
// merged synthetic row already is; the default/DROID event-detection path's
// rows already are (one row = one query). Self-hides per-source when any
// required key is missing (e.g. runs that predate this stat, or rows with
// zero raw predictions).
export const ValidatorPrecisionRecallF1SimChart = ({
  rowResults,
  configRows,
}: {
  rowResults: EvaluationRowResult[];
  configRows?: EvaluationRowConfig[];
}) => {
  const sources = rowResults
    .map((row, idx) => {
      const m: any = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
      const rawTp = m.raw_tp_count;
      const totalRawPredictions = m.total_raw_predictions;
      const gtCovered = m.gt_covered_count;
      const totalGt = m.total_gt ?? m.total_ground_truth;
      const f1NoValidation = m.f1_no_validation;
      const currentF1 = m.f1 ?? m.f1_score;
      const rrCurrent = m.ai_rejection_recall;
      const rpCurrent = m.ai_rejection_precision;
      if ([rawTp, totalRawPredictions, gtCovered, totalGt, f1NoValidation, currentF1, rrCurrent, rpCurrent].some(
        (v) => v == null || !Number.isFinite(Number(v)),
      )) return null;
      const vlmNoiseCount = totalRawPredictions - rawTp;
      const recallCurve = _simulateRejectionCurve(true, rawTp, vlmNoiseCount, gtCovered, totalGt, rrCurrent, rpCurrent);
      const precisionCurve = _simulateRejectionCurve(false, rawTp, vlmNoiseCount, gtCovered, totalGt, rrCurrent, rpCurrent);
      return {
        key: row.row_id,
        label: getEvalRowDisplayName(configRows?.[idx], row, idx),
        recallCurve, precisionCurve, f1NoValidation, currentF1, rrCurrent, rpCurrent,
        recallBreakeven: _findF1Breakeven(recallCurve, f1NoValidation),
        precisionBreakeven: _findF1Breakeven(precisionCurve, f1NoValidation),
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);

  if (sources.length === 0) return null;
  const CHART_HEIGHT = 260;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-emerald-600" />
        <h3 className="text-lg font-semibold text-gray-900">Validator Precision/Recall → F1 Simulation</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Simulates overall F1 if the validator's own rejection precision or recall (each held independently, the other fixed at today's value) were different — holding the raw initial detector's detection behavior fixed. Shows what precision or recall the validator would need to actually beat not validating at all.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sources.map(({ key, label, recallCurve, precisionCurve, f1NoValidation, currentF1, rrCurrent, rpCurrent, recallBreakeven, precisionBreakeven }) => {
          // Stacked-area trick to shade only the region where each sweep's F1
          // beats the "No Validation" baseline: an invisible base series
          // sitting at the baseline value, stacked with a "delta" series that's
          // 0 wherever the curve is at/below baseline and (f1 - baseline)
          // wherever it's above — so the colored band only appears above the
          // line, never below it.
          const chartData = recallCurve.map((p, i) => ({
            x: p.x,
            recall_sweep_f1: p.f1,
            precision_sweep_f1: precisionCurve[i].f1,
            recall_base: f1NoValidation,
            recall_delta: Math.max(0, p.f1 - f1NoValidation),
            precision_base: f1NoValidation,
            precision_delta: Math.max(0, precisionCurve[i].f1 - f1NoValidation),
          }));
          return (
            <div key={key} className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <h5 className="text-xs font-semibold text-gray-900 truncate" title={label}>{label}</h5>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Needs rejection recall ≥ {recallBreakeven == null ? 'n/a (never helps)' : pct(recallBreakeven)}, or rejection precision ≥ {precisionBreakeven == null ? 'n/a (never helps)' : pct(precisionBreakeven)}, to beat "No Validation" F1 ({pct(f1NoValidation)}).
                {' '}Current: recall {pct(rrCurrent)}, precision {pct(rpCurrent)} → F1 {pct(currentF1)}.
              </p>
              <div style={{ width: '100%', height: CHART_HEIGHT }} className="mt-2">
                <ResponsiveContainer>
                  <ComposedChart data={chartData} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="x"
                      type="number"
                      domain={[0, 1]}
                      tickFormatter={(v) => `${Math.round(v * 100)}%`}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      label={{ value: 'Simulated validator recall / precision', position: 'insideBottom', offset: -3, fontSize: 11 }}
                    />
                    <YAxis
                      domain={[0, 1]}
                      tickFormatter={(v) => `${Math.round(v * 100)}%`}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      label={{ value: 'F1', angle: -90, position: 'insideLeft', fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(value: any, name: any) => [pct(Number(value)), name]}
                      labelFormatter={(x: any) => `Value: ${pct(Number(x))}`}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} payload={[
                      { value: 'F1 (recall sweep)', type: 'line', color: '#0EA5E9' },
                      { value: 'F1 (precision sweep)', type: 'line', color: '#DC2626' },
                    ]} />
                    {/* Shaded bands: invisible base at the baseline value, stacked with a
                        delta that's only nonzero where the sweep beats "No Validation" —
                        so the fill only ever appears above the reference line. */}
                    <Area dataKey="recall_base" stackId="recallShade" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" tooltipType="none" />
                    <Area dataKey="recall_delta" stackId="recallShade" stroke="none" fill="#0EA5E9" fillOpacity={0.15} isAnimationActive={false} legendType="none" tooltipType="none" />
                    <Area dataKey="precision_base" stackId="precisionShade" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" tooltipType="none" />
                    <Area dataKey="precision_delta" stackId="precisionShade" stroke="none" fill="#DC2626" fillOpacity={0.15} isAnimationActive={false} legendType="none" tooltipType="none" />
                    <ReferenceLine
                      y={f1NoValidation}
                      stroke="#9CA3AF"
                      strokeDasharray="4 3"
                      label={{ value: 'No Validation', position: 'insideTopRight', fontSize: 10, fill: '#6B7280' }}
                    />
                    <Line type="monotone" dataKey="recall_sweep_f1" name="F1 (recall sweep)" stroke="#0EA5E9" dot={false} strokeWidth={2} legendType="none" />
                    <Line type="monotone" dataKey="precision_sweep_f1" name="F1 (precision sweep)" stroke="#DC2626" dot={false} strokeWidth={2} legendType="none" />
                    <ReferenceDot x={rrCurrent} y={currentF1} r={5} fill="#0EA5E9" stroke="white" />
                    <ReferenceDot x={rpCurrent} y={currentF1} r={5} fill="#DC2626" stroke="white" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Stacked-bar histograms of GT vs predicted target-object labels, with each
// bar broken into correct (blue) and incorrect (red) segments as graded by the
// LLM matcher. Hides itself when no DROID target-object distribution data is
// on the row.
//
// Data shape on aggregated_metrics (set by `_compute_metrics` in droid_eval.py):
//   target_object_gt_distribution:        JSON `{<label>: {correct: N, incorrect: M}}`
//   target_object_predicted_distribution: JSON `{<label>: {correct: N, incorrect: M}}`
export const TargetObjectDistribution = ({ rowResults }: { rowResults: EvaluationRowResult[] }) => {
  let gtJson: string | undefined;
  let predJson: string | undefined;
  for (const row of rowResults) {
    const m: any = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    if (m.target_object_gt_distribution) gtJson = m.target_object_gt_distribution;
    if (m.target_object_predicted_distribution) predJson = m.target_object_predicted_distribution;
  }
  if (!gtJson && !predJson) return null;

  type Bucket = { correct: number; incorrect: number };
  const parse = (raw: string | undefined): Record<string, Bucket> => {
    if (!raw) return {};
    try { return JSON.parse(raw) as Record<string, Bucket>; }
    catch { return {}; }
  };
  const toRows = (dist: Record<string, Bucket>) =>
    Object.entries(dist)
      .map(([label, b]) => ({
        label,
        correct: Number(b?.correct ?? 0),
        incorrect: Number(b?.incorrect ?? 0),
        total: Number(b?.correct ?? 0) + Number(b?.incorrect ?? 0),
      }))
      .filter((r) => r.total > 0)
      // Most-frequent labels first so the visually-interesting bars are
      // immediately visible without scrolling.
      .sort((a, b) => b.total - a.total);

  const gtRows = toRows(parse(gtJson));
  const predRows = toRows(parse(predJson));

  // Color the bars correct = blue, incorrect = red (per the user's spec).
  const COLOR_CORRECT = '#3B82F6';
  const COLOR_INCORRECT = '#EF4444';
  const BAR_WIDTH_PX = 56; // per-label horizontal slot
  const CHART_HEIGHT = 280;

  const renderChart = (
    title: string,
    subtitle: string,
    rows: { label: string; correct: number; incorrect: number; total: number }[],
  ) => {
    if (rows.length === 0) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          <p className="text-xs text-gray-400 mt-6 text-center">No data.</p>
        </div>
      );
    }
    // Min width keeps the chart full-width when there are few bars; explicit
    // width past the container threshold gives a horizontal scroll affordance.
    const innerWidth = Math.max(rows.length * BAR_WIDTH_PX + 80, 400);
    return (
      <CollapsibleCard
        title={title}
        description={subtitle}
        headerRight={
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR_CORRECT }} />
              Correct
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR_INCORRECT }} />
              Incorrect
            </span>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <div style={{ width: innerWidth, height: CHART_HEIGHT }}>
            <BarChart
              data={rows}
              width={innerWidth}
              height={CHART_HEIGHT}
              margin={{ left: 10, right: 20, top: 10, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                tick={{ fontSize: 11, fill: '#374151' }}
                angle={-35}
                textAnchor="end"
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} />
              <Tooltip
                formatter={(value: any, name: any) => [value, name === 'correct' ? 'Correct' : 'Incorrect']}
                labelFormatter={(l: any) => `Label: ${l}`}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="correct"   stackId="a" fill={COLOR_CORRECT}   name="Correct"   />
              <Bar dataKey="incorrect" stackId="a" fill={COLOR_INCORRECT} name="Incorrect" />
            </BarChart>
          </div>
        </div>
      </CollapsibleCard>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
      {renderChart(
        'Ground-truth target objects',
        'Per-label count, split by whether the VLM-graded prediction matched (blue) or not (red).',
        gtRows,
      )}
      {renderChart(
        'Predicted target objects',
        'Per-label count of VLM predictions, split by grader verdict.',
        predRows,
      )}
    </div>
  );
};

// Horizontal-scrollable single-color bar chart of false-positive count per
// ground-truth class for DROID event-detection runs. Reads
// `event_detection_fp_by_gt_class` (JSON `{<gt_class>: count}`) from
// aggregated_metrics. Self-hides when the key is absent (e.g. classification
// runs, or event-detection runs with zero FPs).
export const FpClassDistribution = ({ rowResults }: { rowResults: EvaluationRowResult[] }) => {
  let raw: string | undefined;
  let targetClass: string | undefined;
  for (const row of rowResults) {
    const m: any = row.aggregated_metrics || (row.trials?.[0]?.metrics as any) || {};
    if (m.event_detection_fp_by_gt_class) raw = m.event_detection_fp_by_gt_class;
    if (m.event_detection_target_class) targetClass = m.event_detection_target_class;
  }
  if (!raw) return null;
  let parsed: Record<string, number> = {};
  try { parsed = JSON.parse(raw) as Record<string, number>; }
  catch { return null; }
  const rows = Object.entries(parsed)
    .map(([label, count]) => ({ label, count: Number(count) || 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-900">False-positive classes</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          GT classes the model incorrectly identified as `{targetClass ?? 'target'}`.
        </p>
        <p className="text-xs text-gray-400 mt-6 text-center">No false positives — nothing to chart.</p>
      </div>
    );
  }
  const BAR_WIDTH_PX = 56;
  const CHART_HEIGHT = 280;
  const innerWidth = Math.max(rows.length * BAR_WIDTH_PX + 80, 400);
  return (
    <CollapsibleCard
      title="False-positive classes"
      description={
        <>
          Ground-truth classes the model incorrectly identified as
          {' '}<code className="text-[11px]">{targetClass ?? 'target'}</code>. Higher bars =
          common confusions for this query.
        </>
      }
    >
      <div className="overflow-x-auto">
        <div style={{ width: innerWidth, height: CHART_HEIGHT }}>
          <BarChart
            data={rows}
            width={innerWidth}
            height={CHART_HEIGHT}
            margin={{ left: 10, right: 20, top: 10, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" interval={0} tick={{ fontSize: 11, fill: '#374151' }} angle={-35} textAnchor="end" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} />
            <Tooltip
              formatter={(v: any) => [v, 'FP count']}
              labelFormatter={(l: any) => `GT class: ${l}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="count" fill="#EF4444" name="False positives" />
          </BarChart>
        </div>
      </div>
    </CollapsibleCard>
  );
};

// Status badge component
export const StatusBadge = ({ status, large = false }: { status: string; large?: boolean }) => {
  const baseClasses = large
    ? 'px-3 py-1.5 text-sm font-medium rounded-full'
    : 'px-2 py-0.5 text-xs font-medium rounded-full';

  const statusConfig: Record<string, { border: string; text: string; label: string; icon: React.ReactNode }> = {
    running: {
      border: 'border-blue-300',
      text: 'text-blue-700',
      label: 'Running',
      icon: <Loader2 className={large ? 'w-4 h-4 animate-spin' : 'w-3 h-3 animate-spin'} />
    },
    completed: {
      border: 'border-green-500',
      text: 'text-green-700',
      label: 'Completed',
      icon: <Check className={large ? 'w-4 h-4' : 'w-3 h-3'} />
    },
    failed: {
      border: 'border-red-300',
      text: 'text-red-700',
      label: 'Failed',
      icon: <AlertCircle className={large ? 'w-4 h-4' : 'w-3 h-3'} />
    },
    partial: {
      border: 'border-amber-300',
      text: 'text-amber-700',
      label: 'Partial',
      icon: <AlertCircle className={large ? 'w-4 h-4' : 'w-3 h-3'} />
    },
    cancelled: {
      border: 'border-red-300',
      text: 'text-red-700',
      label: 'Cancelled',
      icon: <AlertCircle className={large ? 'w-4 h-4' : 'w-3 h-3'} />
    },
  };

  const config = statusConfig[status] || { border: 'border-gray-300', text: 'text-gray-700', label: status, icon: null };

  return (
    <span className={`${baseClasses} border bg-transparent ${config.border} ${config.text} flex items-center gap-1.5 inline-flex`}>
      {config.icon}
      {config.label}
    </span>
  );
};

// Compact circular status indicator — a filled circle with an icon (e.g. a
// check for completed). Used where a label-less status dot reads cleaner than
// the full StatusBadge chip.
export const StatusCircle = ({ status }: { status: string }) => {
  const config: Record<string, { bg: string; icon: React.ReactNode }> = {
    running: { bg: 'bg-blue-500', icon: <Loader2 className="w-3 h-3 animate-spin text-white" /> },
    completed: { bg: 'bg-green-500', icon: <Check className="w-3 h-3 text-white" /> },
    failed: { bg: 'bg-red-500', icon: <X className="w-3 h-3 text-white" /> },
    partial: { bg: 'bg-amber-500', icon: <AlertCircle className="w-3 h-3 text-white" /> },
    cancelled: { bg: 'bg-gray-400', icon: <X className="w-3 h-3 text-white" /> },
  };
  const c = config[status] || { bg: 'bg-gray-300', icon: null };
  return (
    <span
      className={cn('flex flex-shrink-0 items-center justify-center size-5 rounded-full', c.bg)}
      title={status.charAt(0).toUpperCase() + status.slice(1)}
    >
      {c.icon}
    </span>
  );
};

// JSON format tooltip component
export const JsonFormatTooltip = ({ children }: { children?: ReactNode }) => {
  return (
    <UITooltipProvider delayDuration={150}>
      <UITooltip>
        <UITooltipTrigger asChild>
          {children ?? (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              aria-label="View JSON format"
            >
              <Info className="w-4 h-4" />
            </button>
          )}
        </UITooltipTrigger>
        <UITooltipContent
          side="right"
          align="start"
          className="w-96 max-w-[90vw] max-h-[80vh] overflow-y-auto overscroll-contain bg-gray-900 text-white rounded-xl shadow-2xl border border-gray-700 p-0"
        >
          <div className="text-sm font-semibold p-4 pb-3 flex items-center gap-2 border-b border-gray-700">
            <FileJson className="w-4 h-4 text-amber-400" />
            Expected JSON Format
          </div>
          <div className="p-4 pt-3">
            <pre className="text-xs bg-gray-800 rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">
{`{
  "video_id_abc123": [
    {
      "t_start": "0:15",
      "t_end": "0:30",
      "description": "Lane change to left",
      "category": "new"
    },
    {
      "t_start": "1:45",
      "t_end": "2:00",
      "description": "Red light violation"
    }
  ],
  "video_id_def456": [
    {
      "t_start": "0:05",
      "t_end": "0:12",
      "description": "Pedestrian crossing",
      "category": "same"
    }
  ]
}`}
            </pre>
            <div className="text-xs text-gray-400 mt-3 space-y-1">
              <p><span className="text-amber-400 font-medium">Keys:</span> Video IDs from your library</p>
              <p><span className="text-amber-400 font-medium">Values:</span> Array of ground truth events</p>
              <p><span className="text-amber-400 font-medium">Timestamps:</span> MM:SS or HH:MM:SS format</p>
              <p><span className="text-amber-400 font-medium">Category:</span> (Optional) When present, matching uses category + time overlap instead of description similarity</p>
            </div>
          </div>
        </UITooltipContent>
      </UITooltip>
    </UITooltipProvider>
  );
};

// View toggle component for table/graph/videos view
export const ViewToggle = ({ view, onViewChange }: { view: 'table' | 'graph' | 'videos'; onViewChange: (v: 'table' | 'graph' | 'videos') => void }) => (
  <div className="flex bg-gray-100 rounded-lg p-1">
    <button
      onClick={() => onViewChange('table')}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
        view === 'table'
          ? 'bg-white shadow-sm text-gray-900'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <Table2 className="w-4 h-4" />
      Table
    </button>
    <button
      onClick={() => onViewChange('graph')}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
        view === 'graph'
          ? 'bg-white shadow-sm text-gray-900'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <BarChart3 className="w-4 h-4" />
      Graph
    </button>
    <button
      onClick={() => onViewChange('videos')}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
        view === 'videos'
          ? 'bg-white shadow-sm text-gray-900'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <Film className="w-4 h-4" />
      Videos
    </button>
  </div>
);

// Metric card component for summary stats
// Pale, on-brand fills cycled across the metric cards so each reads as a
// distinct tile while staying within the app's palette (navy primary + soft
// accents). `tone` indexes into this list; it wraps for longer metric sets.
const METRIC_CARD_TONES = [
  'bg-indigo-50 border-indigo-100',
  'bg-blue-50 border-blue-100',
  'bg-sky-50 border-sky-100',
  'bg-gray-50 border-gray-200',
  'bg-gray-100 border-gray-200',
];

export const MetricCard = ({ label, value, subtitle, tone = 0 }: { label: string; value: number; subtitle?: string; tone?: number }) => {
  return (
    <div className={cn('rounded-xl border p-5', METRIC_CARD_TONES[tone % METRIC_CARD_TONES.length])}>
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="text-3xl font-bold mt-2 tracking-tight text-navy-700">{(value * 100).toFixed(1)}%</div>
      {subtitle && <div className="text-xs text-gray-400 mt-1 truncate" title={subtitle}>{subtitle}</div>}
    </div>
  );
};

// Summary stats cards component
export const SummaryStatsCards = ({ rowResults, metrics, level, configRows }: { rowResults: EvaluationRowResult[]; metrics?: EvaluationMetricDefinition[]; level?: 'child' | 'parent'; configRows?: EvaluationRowConfig[] }) => {
  const average = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

  const validResults = rowResults.filter(r => r.aggregated_metrics && Object.keys(r.aggregated_metrics).length > 0);

  // Benchmark / segmentation mode: show metric values directly from aggregated_metrics.
  // Only render cards whose metric IS actually present in at least one row — this hides
  // TAS cards (edit, f1@k) on older runs that predate the TAS integration, and hides
  // parent-level cards on non-hierarchical runs. Draws from the top of the metrics
  // list and skips missing entries until up to 5 cards are collected.
  if (metrics) {
    const cards: Array<{
      key: string; title: string; value: number; maxIdx: number;
    }> = [];
    const hasRobotics = validResults.some(r => r.aggregated_metrics?.['robotics/f1@0.5/mean'] != null
      || r.aggregated_metrics?.['robotics/f1@0.5'] != null);
    const headlineMetrics = hasRobotics ? metrics.filter(m => m.key.startsWith('robotics/')) : metrics;
    for (const m of headlineMetrics) {
      if (cards.length >= 5) break;
      const metricKey = level === 'parent' ? `parent/${m.key}` : m.key;
      let hasAny = false;
      let maxValue = 0;
      let maxIdx = 0;
      validResults.forEach((r, idx) => {
        const raw = r.aggregated_metrics?.[`${metricKey}/mean`] ?? r.aggregated_metrics?.[metricKey];
        if (raw == null) return;
        const v = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(v)) return;
        if (!hasAny || v > maxValue) {
          maxValue = v;
          maxIdx = idx;
        }
        hasAny = true;
      });
      if (!hasAny) continue;
      cards.push({ key: m.key, title: m.title, value: maxValue, maxIdx });
    }
    if (cards.length === 0) return null;
    // Tailwind JIT needs the literal class name, so map count → explicit class.
    const cols = cards.length >= 5 ? 'grid-cols-5'
      : cards.length === 4 ? 'grid-cols-4'
      : cards.length === 3 ? 'grid-cols-3'
      : cards.length === 2 ? 'grid-cols-2'
      : 'grid-cols-1';
    return (
      <div className={`grid ${cols} gap-4`}>
        {cards.map((c, cardIdx) => {
          const subtitle = validResults.length > 1
            ? `Best: Row ${c.maxIdx + 1}, ${getEvalRowDisplayName(configRows?.find(cr => cr.id === validResults[c.maxIdx].row_id), validResults[c.maxIdx], c.maxIdx)}`
            : undefined;
          return (
            <MetricCard
              key={c.key}
              label={c.title}
              value={c.value}
              subtitle={subtitle}
              tone={cardIdx}
            />
          );
        })}
      </div>
    );
  }

  // `evaluate_predictions()` computes the full three-tier metric set
  // (Final/VLM/Rejection/Oracle Statistics) unconditionally for every
  // event-detection row, not just DrivingDojo — render it in the same
  // grouped/collapsible card format via GroupedMetricCards. Hidden for any
  // run whose stored metrics predate this computation.
  const hasValidationStats = validResults.some(r =>
    hasMetric(r.aggregated_metrics, 'ai_rejected_count')
    || hasMetric(r.aggregated_metrics, 'precision_oracle_validation')
  );
  if (!hasValidationStats) return null;

  // GroupedMetricCards reads bare keys directly, and subline templates need
  // every numerator/denominator key too, not just each card's headline value
  // (see collectMetricDefKeys) — otherwise the fine print silently vanishes.
  // Each card's own `.key` is a rate/percentage → average across rows;
  // everything else referenced is a raw count used only in a subline → sum
  // across rows (e.g. total TP, not "average TP per row").
  const primaryKeys = new Set(EVENT_DETECTION_OVERALL_METRIC_DEFS.map(m => m.key));
  const remappedMetrics: Record<string, number> = {};
  for (const key of collectMetricDefKeys(EVENT_DETECTION_OVERALL_METRIC_DEFS)) {
    if (isStepMetric(key)) continue;
    const values = validResults.map(r => metricAt(r.aggregated_metrics, key) ?? 0);
    remappedMetrics[key] = primaryKeys.has(key)
      ? average(values)
      : values.reduce((a, b) => a + b, 0);
  }
  // Precision/Recall/F1 (micro) are NOT a plain per-row average — DrivingDojo
  // sums TP/FP/FN across every row first, then derives the rate from those
  // totals, so a row with more events pulls the number more than one with
  // few. `fn` isn't referenced in any subline (only `tp`/`fp`, already summed
  // above), so sum it directly here.
  const sumKey = (key: string) => validResults.reduce((acc, r) => acc + (metricAt(r.aggregated_metrics, key) ?? 0), 0);
  const totalTp = remappedMetrics['tp'] ?? sumKey('tp');
  const totalFp = remappedMetrics['fp'] ?? sumKey('fp');
  const totalFn = sumKey('fn');
  remappedMetrics['precision'] = (totalTp + totalFp) > 0 ? totalTp / (totalTp + totalFp) : 0;
  remappedMetrics['recall'] = (totalTp + totalFn) > 0 ? totalTp / (totalTp + totalFn) : 0;
  remappedMetrics['f1_score'] = (remappedMetrics['precision'] + remappedMetrics['recall']) > 0
    ? 2 * remappedMetrics['precision'] * remappedMetrics['recall'] / (remappedMetrics['precision'] + remappedMetrics['recall'])
    : 0;
  // F1 (macro) — mean of each row's own F1, the average already computed above.
  remappedMetrics['f1_score_macro'] = average(validResults.map(r => metricAt(r.aggregated_metrics, 'f1_score') ?? 0));

  Object.assign(remappedMetrics, aggregateStepMetrics(validResults.map(r => r.aggregated_metrics || {})));
  return <GroupedMetricCards metrics={remappedMetrics} cardDefs={EVENT_DETECTION_OVERALL_METRIC_DEFS} />;
};

// Substitutes `{metric_key}` placeholders in a card's `subline` template
// against a metrics dict. Returns undefined if any referenced key is missing.
const substituteMetricTemplate = (template: string, metrics: Record<string, any>): string | undefined => {
  let missing = false;
  const out = template.replace(/\{([^}]+)\}/g, (_, k) => {
    const v = metrics[k];
    if (v == null) { missing = true; return ''; }
    return String(v);
  });
  return missing ? undefined : out;
};

// Single metric chart component
export const SingleMetricChart = ({
  title,
  metricKey,
  rowResults,
  configRows,
  onRowClick,
  accentColor,
  isPercentage = true,
  subline,
}: {
  title: string;
  metricKey: string;
  rowResults: EvaluationRowResult[];
  configRows?: EvaluationRowConfig[];
  onRowClick?: (rowId: string) => void;
  accentColor: string;
  isPercentage?: boolean;
  subline?: string;
}) => {
  const chartData = rowResults.map((row, idx) => {
    const rawValue = row.aggregated_metrics?.[`${metricKey}/mean`] ?? row.aggregated_metrics?.[metricKey] ?? 0;
    return {
      name: getEvalRowDisplayName(configRows?.find(c => c.id === row.row_id), row, idx),
      rowId: row.row_id,
      value: isPercentage ? rawValue * 100 : rawValue,
      fill: EVAL_ROW_COLORS[idx % EVAL_ROW_COLORS.length],
    };
  });

  // Calculate max value for Y-axis domain (for count metrics)
  const maxValue = isPercentage ? 100 : Math.max(...chartData.map(d => d.value), 10);

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { name: string; value: number; fill: string } }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[140px]">
          <p className="text-sm font-semibold text-gray-900 mb-1">{data.name}</p>
          <p className="text-lg font-bold" style={{ color: data.fill }}>
            {isPercentage ? `${data.value.toFixed(1)}%` : Math.round(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <CollapsibleCard title={title} description={subline} className="rounded-2xl border-gray-100 p-5">
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={chartData}
          barCategoryGap="25%"
          onClick={(data) => {
            if (data?.activePayload?.[0]?.payload?.rowId && onRowClick) {
              onRowClick(data.activePayload[0].payload.rowId);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={70}
          />
          <YAxis
            domain={[0, maxValue]}
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => isPercentage ? `${v}%` : `${v}`}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(31, 39, 83, 0.08)' }} />
          <Bar
            dataKey="value"
            radius={[6, 6, 0, 0]}
            className="cursor-pointer"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </CollapsibleCard>
  );
};

// Reads a metric value trying several candidate key spellings in order
// (e.g. default-mode `total_ground_truth` vs Driving Dojo's `total_gt`),
// each with the existing `/mean` trial-aggregation fallback. Returns the
// first defined value, or 0 if none are present.
const readMetric = (row: EvaluationRowResult, keys: string[]): number => {
  for (const k of keys) {
    const v = row.aggregated_metrics?.[`${k}/mean`] ?? row.aggregated_metrics?.[k];
    if (v !== undefined && v !== null) return v as number;
  }
  return 0;
};

/**
 * Stacked bar per eval row, summing to 100% of that row's total ground
 * truth: Recall (detected) + VLM Missed + Incorrectly Rejected. These three
 * sum to exactly 100% by construction (tp + vlm_missed_count +
 * ai_incorrectly_rejected_count == total_gt) — visualizes the full recall
 * picture in one chart instead of three separate ones.
 */
export const RecallBreakdownChart = ({
  rowResults,
  configRows,
  onRowClick,
}: {
  rowResults: EvaluationRowResult[];
  configRows?: EvaluationRowConfig[];
  onRowClick?: (rowId: string) => void;
}) => {
  const COLOR_RECALL = '#10B981';
  const COLOR_VLM_MISSED = '#F59E0B';
  const COLOR_INCORRECTLY_REJECTED = '#EF4444';

  const chartData = rowResults.map((row, idx) => {
    const totalGt = readMetric(row, ['total_gt', 'total_ground_truth']);
    const recall = readMetric(row, ['recall']);
    const vlmMissedCount = readMetric(row, ['vlm_missed_count']);
    const incorrectlyRejectedCount = readMetric(row, ['ai_incorrectly_rejected_count']);
    return {
      name: getEvalRowDisplayName(configRows?.find(c => c.id === row.row_id), row, idx),
      rowId: row.row_id,
      recallPct: recall * 100,
      vlmMissedPct: totalGt > 0 ? (vlmMissedCount / totalGt) * 100 : 0,
      incorrectlyRejectedPct: totalGt > 0 ? (incorrectlyRejectedCount / totalGt) * 100 : 0,
      vlmMissedCount,
      incorrectlyRejectedCount,
      totalGt,
    };
  });

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: typeof chartData[number] }> }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[190px]">
          <p className="text-sm font-semibold text-gray-900 mb-1.5">{d.name}</p>
          <p className="text-xs" style={{ color: COLOR_RECALL }}>Recall: {d.recallPct.toFixed(1)}%</p>
          <p className="text-xs" style={{ color: COLOR_VLM_MISSED }}>Initial Detection Missed: {d.vlmMissedPct.toFixed(1)}% ({d.vlmMissedCount})</p>
          <p className="text-xs" style={{ color: COLOR_INCORRECTLY_REJECTED }}>Incorrectly Rejected: {d.incorrectlyRejectedPct.toFixed(1)}% ({d.incorrectlyRejectedCount})</p>
          <p className="text-xs text-gray-400 mt-1">Total GT: {d.totalGt}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <CollapsibleCard
      title="Recall Breakdown"
      headerRight={
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR_RECALL }} />Recall
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR_VLM_MISSED }} />Initial Detection Missed
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR_INCORRECTLY_REJECTED }} />Incorrectly Rejected
          </span>
        </div>
      }
      className="rounded-2xl border-gray-100 p-5"
    >
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={chartData}
          barCategoryGap="25%"
          onClick={(data) => {
            if (data?.activePayload?.[0]?.payload?.rowId && onRowClick) {
              onRowClick(data.activePayload[0].payload.rowId);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={70}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(31, 39, 83, 0.08)' }} />
          <Bar dataKey="recallPct" stackId="a" fill={COLOR_RECALL} className="cursor-pointer" />
          <Bar dataKey="vlmMissedPct" stackId="a" fill={COLOR_VLM_MISSED} className="cursor-pointer" />
          <Bar dataKey="incorrectlyRejectedPct" stackId="a" fill={COLOR_INCORRECTLY_REJECTED} radius={[6, 6, 0, 0]} className="cursor-pointer" />
        </BarChart>
      </ResponsiveContainer>
    </CollapsibleCard>
  );
};

/**
 * Generic grouped (side-by-side, not stacked) bar chart comparing 3 tiers of
 * the same underlying metric — e.g. Precision (No Validation / With
 * Validation / Oracle). Bars are colored by tier (consistent across rows)
 * rather than by row, since comparing tiers is the point.
 */
export const GroupedTierChart = ({
  title,
  description,
  tiers,
  rowResults,
  configRows,
  onRowClick,
}: {
  title: string;
  description?: string;
  tiers: { key: string; label: string; color: string }[];
  rowResults: EvaluationRowResult[];
  configRows?: EvaluationRowConfig[];
  onRowClick?: (rowId: string) => void;
}) => {
  const chartData = rowResults.map((row, idx) => {
    const entry: Record<string, string | number> = {
      name: getEvalRowDisplayName(configRows?.find(c => c.id === row.row_id), row, idx),
      rowId: row.row_id,
    };
    for (const tier of tiers) {
      entry[tier.key] = readMetric(row, [tier.key]) * 100;
    }
    return entry;
  });

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string; color: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[160px]">
          <p className="text-sm font-semibold text-gray-900 mb-1.5">{label}</p>
          {payload.map((p) => (
            <p key={p.name} className="text-xs" style={{ color: p.color }}>
              {p.name}: {p.value.toFixed(1)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <CollapsibleCard
      title={title}
      description={description}
      className="rounded-2xl border-gray-100 p-5"
    >
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={chartData}
          barCategoryGap="25%"
          onClick={(data) => {
            if (data?.activePayload?.[0]?.payload?.rowId && onRowClick) {
              onRowClick(data.activePayload[0].payload.rowId);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={70}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(31, 39, 83, 0.08)' }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {tiers.map((tier) => (
            <Bar key={tier.key} dataKey={tier.key} name={tier.label} fill={tier.color} radius={[6, 6, 0, 0]} className="cursor-pointer" />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </CollapsibleCard>
  );
};

const _harmonicMeanTS = (precision: number, recall: number): number =>
  (precision + recall) > 0 ? (2 * precision * recall) / (precision + recall) : 0;

/**
 * Mirrors backend `_compute_validation_metrics` (drivingdojo_eval.py) so the
 * Graph View can recompute every derived stat for an arbitrary SUBSET of
 * queries (merged client-side), not just the pre-aggregated global/per-class
 * values the backend already returns.
 */
const computeDojoValidationMetrics = (
  tp: number, fp: number, fn: number,
  aiRejected: number, aiIncorrectlyRejected: number, aiRejectedNoise: number,
  keptMatched: number = 0,
): Record<string, number> => {
  const totalPred = tp + fp;
  const totalGt = tp + fn;
  const totalRawPredictions = totalPred + aiRejected;
  const vlmNoiseCount = fp + aiRejectedNoise;

  const aiIncorrectRejectionRate = aiRejected > 0 ? aiIncorrectlyRejected / aiRejected : 0;
  const aiRecallImpactRate = totalGt > 0 ? aiIncorrectlyRejected / totalGt : 0;
  const vlmMissedCount = Math.max(0, fn - aiIncorrectlyRejected);
  const vlmNoiseRate = totalRawPredictions > 0 ? vlmNoiseCount / totalRawPredictions : 0;

  const rawTp = tp + (aiRejected - aiRejectedNoise);
  const gtCoveredCount = totalGt - vlmMissedCount;
  const precisionNoValidation = totalRawPredictions > 0 ? rawTp / totalRawPredictions : 0;
  const recallNoValidation = totalGt > 0 ? gtCoveredCount / totalGt : 0;
  const f1NoValidation = _harmonicMeanTS(precisionNoValidation, recallNoValidation);

  const precisionOracleValidation = rawTp > 0 ? 1 : 0;
  const recallOracleValidation = recallNoValidation;
  const f1OracleValidation = _harmonicMeanTS(precisionOracleValidation, recallOracleValidation);

  const aiCorrectlyRejectedCount = aiRejectedNoise;
  const aiRejectionPrecision = aiRejected > 0 ? aiCorrectlyRejectedCount / aiRejected : 0;
  const aiRejectionRecall = vlmNoiseCount > 0 ? aiCorrectlyRejectedCount / vlmNoiseCount : 0;
  const aiValidationF1 = _harmonicMeanTS(aiRejectionPrecision, aiRejectionRecall);

  const fnShareVlmMissed = fn > 0 ? vlmMissedCount / fn : 0;
  const fnShareIncorrectlyRejected = fn > 0 ? aiIncorrectlyRejected / fn : 0;

  const precision = totalPred > 0 ? tp / totalPred : 0;
  const recall = totalGt > 0 ? tp / totalGt : 0;
  const f1 = _harmonicMeanTS(precision, recall);

  // Mirrors backend `validator_accuracy`: of every raw prediction the
  // validator looked at (kept + rejected), % it decided on correctly. The
  // denominator must be PREDICTION-level (keptMatched + fp + aiRejected), not
  // totalRawPredictions (tp + fp + aiRejected) — tp is GT-level (# distinct GT
  // intervals covered) while keptMatched is prediction-level (# kept
  // predictions that overlapped some GT), so when multiple predictions cover
  // the same GT interval, totalRawPredictions understates the true total and
  // pushes validatorAccuracy above 1.0.
  const totalRawDecisions = keptMatched + fp + aiRejected;
  const validatorCorrectDecisions = keptMatched + aiCorrectlyRejectedCount;
  const validatorAccuracy = totalRawDecisions > 0 ? validatorCorrectDecisions / totalRawDecisions : 0;

  return {
    total_tp: tp, total_fp: fp, total_fn: fn, total_gt: totalGt, total_pred: totalPred,
    precision, recall, f1,
    raw_tp_count: rawTp,
    gt_covered_count: gtCoveredCount,
    ai_rejected_count: aiRejected,
    ai_incorrectly_rejected_count: aiIncorrectlyRejected,
    ai_incorrect_rejection_rate: aiIncorrectRejectionRate,
    ai_recall_impact_rate: aiRecallImpactRate,
    vlm_missed_count: vlmMissedCount,
    vlm_noise_count: vlmNoiseCount,
    vlm_noise_rate: vlmNoiseRate,
    precision_no_validation: precisionNoValidation,
    recall_no_validation: recallNoValidation,
    f1_no_validation: f1NoValidation,
    precision_oracle_validation: precisionOracleValidation,
    recall_oracle_validation: recallOracleValidation,
    f1_oracle_validation: f1OracleValidation,
    ai_correctly_rejected_count: aiCorrectlyRejectedCount,
    ai_rejection_precision: aiRejectionPrecision,
    ai_rejection_recall: aiRejectionRecall,
    ai_validation_f1: aiValidationF1,
    fn_share_vlm_missed: fnShareVlmMissed,
    fn_share_incorrectly_rejected: fnShareIncorrectlyRejected,
    validator_accuracy: validatorAccuracy,
    validator_correct_decisions: validatorCorrectDecisions,
    total_raw_predictions: totalRawPredictions,
    total_raw_decisions: totalRawDecisions,
  };
};

/**
 * Driving Dojo's Graph View, keyed by QUERY (action class) rather than eval
 * row — the run always emits exactly one synthetic row, so the regular
 * row-based MetricsChartsGrid has nothing meaningful to separate by.
 * Checking multiple queries does NOT plot them as separate bars; it merges
 * their raw per-class counts into one combined bar, recomputing every
 * derived stat from the summed counts (mirrors the backend's per-class ->
 * aggregate math via `computeDojoValidationMetrics`).
 */
export const DrivingDojoGraphView = ({
  row,
  activeClasses,
  evalId,
  runId,
  rowIdx = 0,
}: {
  row: EvaluationRowResult;
  activeClasses: { id: string; label: string }[];
  evalId: string;
  runId: string;
  rowIdx?: number;
}) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(activeClasses.map(a => a.id)));
  const [perQueryConfidenceView, setPerQueryConfidenceView] = useState<'graph' | 'table'>('graph');
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const metrics = row.aggregated_metrics || {};
  const selectedClasses = activeClasses.filter(a => selected.has(a.id));

  const merged = (() => {
    let tp = 0, fp = 0, fn = 0, aiRejected = 0, aiIncorrectlyRejected = 0, aiRejectedNoise = 0, keptMatched = 0;
    let tiouWeightedSum = 0, tiouWeight = 0;
    let macroF1Sum = 0, macroF1Count = 0;
    for (const a of selectedClasses) {
      const classTp = Number(metrics[`correct/class/${a.id}`] ?? 0);
      const classFp = Number(metrics[`false_positives/class/${a.id}`] ?? 0);
      const classFn = Number(metrics[`false_negatives/class/${a.id}`] ?? 0);
      const classAiRejected = Number(metrics[`ai_rejected_count/class/${a.id}`] ?? 0);
      const classAiIncorrectlyRejected = Number(metrics[`ai_incorrectly_rejected_count/class/${a.id}`] ?? 0);
      const classAiRejectedNoise = Number(metrics[`ai_correctly_rejected_count/class/${a.id}`] ?? 0);
      // `kept_matched` isn't exposed as its own key — back it out from the
      // combined `validator_correct_decisions` (kept-and-matched +
      // correctly-rejected) minus the correctly-rejected half, so it can be
      // re-summed across the selected classes before re-deriving accuracy.
      const classValidatorCorrect = Number(metrics[`validator_correct_decisions/class/${a.id}`] ?? 0);
      const classKeptMatched = classValidatorCorrect - classAiRejectedNoise;
      tp += classTp; fp += classFp; fn += classFn;
      aiRejected += classAiRejected;
      aiIncorrectlyRejected += classAiIncorrectlyRejected;
      aiRejectedNoise += classAiRejectedNoise;
      keptMatched += classKeptMatched;
      const support = classTp + classFn;
      if (support > 0) {
        const classTiou = Number(metrics[`mean_tiou/class/${a.id}`] ?? 0);
        tiouWeightedSum += classTiou * classTp;
        tiouWeight += classTp;
        const classF1 = Number(metrics[`f1/class/${a.id}`] ?? 0);
        macroF1Sum += classF1;
        macroF1Count += 1;
      }
    }
    const derived = computeDojoValidationMetrics(tp, fp, fn, aiRejected, aiIncorrectlyRejected, aiRejectedNoise, keptMatched);
    return {
      ...derived,
      mean_tiou: tiouWeight > 0 ? tiouWeightedSum / tiouWeight : 0,
      'f1/macro': macroF1Count > 0 ? macroF1Sum / macroF1Count : 0,
      processing_time_seconds: Number(metrics['processing_time_seconds'] ?? 0),
    };
  })();

  const syntheticRow: EvaluationRowResult = {
    row_id: 'merged',
    row_description: selectedClasses.length === activeClasses.length
      ? `All queries (${activeClasses.length})`
      : `${selectedClasses.length} of ${activeClasses.length} queries`,
    status: row.status,
    batch_ids: [],
    trials: [],
    aggregated_metrics: merged,
  };
  const rowResults = [syntheticRow];

  // Merged confidence-threshold curve across the selected queries. Can't be
  // derived from the precomputed per-class `confidence_pr_curve` values
  // point-wise (merging requires re-sweeping the union of raw per-video
  // samples at each threshold), and those raw samples are no longer shipped
  // to the client at all (they scaled with video count and risked exceeding
  // Firestore's 1 MiB document limit) — fetched on demand instead from the
  // backend, which rebuilds them from the per-video eval analysis docs.
  const { getToken } = useAuth();
  const [mergedConfidenceCurve, setMergedConfidenceCurve] = useState<ConfidencePRCurveData | null>(null);
  const selectedActionIdsKey = selectedClasses.map(a => a.id).sort().join(',');

  useEffect(() => {
    if (!selectedActionIdsKey || !evalId || !runId) {
      setMergedConfidenceCurve(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const url = `${API_BASE_URL}/evaluations/${evalId}/runs/${runId}/drivingdojo-merged-confidence-curve`
          + `?action_ids=${encodeURIComponent(selectedActionIdsKey)}&row_idx=${rowIdx}`;
        const response = await apiClient.get(url, { headers: authHeaders(token) });
        if (cancelled || !response.ok) {
          if (!cancelled) setMergedConfidenceCurve(null);
          return;
        }
        const body = await response.json();
        setMergedConfidenceCurve(parseConfidencePRCurve(body?.curve));
      } catch {
        if (!cancelled) setMergedConfidenceCurve(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedActionIdsKey, evalId, runId, rowIdx, getToken]);

  // Per-class curves are already in row.aggregated_metrics (no fetch needed) —
  // unlike mergedConfidenceCurve above, this doesn't require re-sweeping, so
  // it can show every active query's own optimal threshold regardless of
  // which ones are currently checked for merging.
  const perClassSources = collectConfidencePRCurves([row]).filter(s =>
    activeClasses.some(a => s.key === `${row.row_id}:${a.id}`)
  );

  return (
    <div className="space-y-4">
      {/* Query selector — same visual pattern as the Evaluations legend on
          the regular eval page, but keyed by action class ("query"); unlike
          that legend, checking multiple here merges them into one combined
          result rather than plotting separate bars per query. */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Queries</span>
          <span className="text-xs text-gray-400">Selected queries are merged into one combined result</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {activeClasses.map((a, idx) => (
            <label
              key={a.id}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                selected.has(a.id) ? 'bg-gray-50' : 'bg-gray-100 opacity-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(a.id)}
                onChange={() => toggle(a.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-navy-600 focus:ring-navy-500 cursor-pointer"
              />
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: _ACTION_COLORS[idx % _ACTION_COLORS.length] }}
              />
              <span className="text-xs font-medium text-gray-700">{a.label}</span>
            </label>
          ))}
        </div>
      </div>

      {selectedClasses.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
          <p className="text-sm">Select at least one query to view charts</p>
        </div>
      ) : (
        <>
        <div className="grid gap-4 items-stretch grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
          {DRIVINGDOJO_GRAPH_METRICS.map((metric) => {
            const wideClass = metric.chartType ? 'sm:col-span-2' : '';
            if (metric.chartType === 'recall_breakdown') {
              return (
                <div key={metric.key} className={wideClass}>
                  <RecallBreakdownChart rowResults={rowResults} />
                </div>
              );
            }
            if (metric.chartType === 'grouped_tiers' && metric.tiers) {
              return (
                <div key={metric.key} className={wideClass}>
                  <GroupedTierChart title={metric.title} tiers={metric.tiers} rowResults={rowResults} />
                </div>
              );
            }
            return (
              <SingleMetricChart
                key={metric.key}
                title={metric.title}
                metricKey={metric.key}
                rowResults={rowResults}
                accentColor={metric.color}
                isPercentage={metric.isPercentage}
                subline={metric.subline ? substituteMetricTemplate(metric.subline, merged) : undefined}
              />
            );
          })}
        </div>
        {mergedConfidenceCurve && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-semibold text-gray-900">Confidence Threshold Analysis</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Precision vs. recall as the confidence-score cutoff sweeps 0.0 → 1.0 (0.05 steps), merged across the {selectedClasses.length} selected quer{selectedClasses.length === 1 ? 'y' : 'ies'} — shows what threshold would work across the combined selection rather than one curve per query. Shaded area is the area under the PR curve (AUC-PR); the marked point is the best-F1 threshold.
            </p>
            <ConfidencePRCurveCard
              label={syntheticRow.row_description!}
              data={mergedConfidenceCurve}
              current={{ precision: merged.precision, recall: merged.recall, f1: merged.f1 }}
            />
          </div>
        )}
        {perClassSources.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Per-Query Confidence Thresholds</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Each active query's own confidence-threshold curve — independent of the merge selection above.
                </p>
              </div>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setPerQueryConfidenceView('graph')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                    perQueryConfidenceView === 'graph' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  Graph
                </button>
                <button
                  onClick={() => setPerQueryConfidenceView('table')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                    perQueryConfidenceView === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Table2 className="w-3.5 h-3.5" />
                  Table
                </button>
              </div>
            </div>
            {perQueryConfidenceView === 'graph' ? (
              <div className="grid grid-cols-1 gap-4">
                {perClassSources.map(({ key, label, data, current }) => (
                  <ConfidencePRCurveCard key={key} label={label} data={data} current={current} />
                ))}
              </div>
            ) : (
              <ConfidenceThresholdSummaryTable sources={perClassSources} />
            )}
          </div>
        )}
        <ValidatorPrecisionRecallF1SimChart rowResults={rowResults} />
        </>
      )}
    </div>
  );
};

// Format processing time helper
export const formatProcessingTime = (seconds: number | undefined): string => {
  if (seconds === undefined || seconds === null) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Format metric helper
export const formatMetric = (value: number | undefined) => {
  if (value === undefined || value === null) return '-';
  return (value * 100).toFixed(1) + '%';
};

// DrivingDojo's card sublines reference a few key names
// (`total_gt`/`total_fn`/`total_raw_decisions`) that `evaluate_predictions()`
// only started emitting as of this change — runs computed before that
// backend deploy have the equivalent value under an older name instead.
// Resolve through the alias so already-stored runs render correctly too,
// without needing a re-run.
const METRIC_KEY_ALIASES: Record<string, string> = {
  total_gt: 'total_ground_truth',
  total_fn: 'fn',
  total_raw_decisions: 'total_raw_predictions',
};

// `aggregate_trial_metrics()` (backend) always writes `<key>/mean` — never a
// bare key, even for a single-trial row — so any lookup against a row's
// `aggregated_metrics` must check the `/mean` suffix first, bare key as a
// fallback (older pre-aggregation call sites may still write bare keys).
export const metricAt = (metrics: Record<string, any> | undefined, key: string): number | undefined => {
  const direct = metrics?.[`${key}/mean`] ?? metrics?.[key];
  if (direct !== undefined) return direct;
  const alias = METRIC_KEY_ALIASES[key];
  return alias ? (metrics?.[`${alias}/mean`] ?? metrics?.[alias]) : undefined;
};

export const hasMetric = (metrics: Record<string, any> | undefined, key: string): boolean =>
  metricAt(metrics, key) !== undefined;

// Every key a set of card defs actually needs — each def's own `key`, plus
// any `{placeholder}` referenced in its `subline` template (the numerator/
// denominator fine print under each card), plus `correctKey`/`totalKey`/
// `supportKey` if set. GroupedMetricCards' subline substitution silently
// drops the whole subline if ANY referenced key is missing from the metrics
// object it's given, so a remap that only covers each def's primary `key`
// leaves every subline blank — this covers the full set instead.
export const collectMetricDefKeys = (defs: EvaluationMetricDefinition[]): string[] => {
  const keys = new Set<string>();
  for (const m of defs) {
    keys.add(m.key);
    if (m.correctKey) keys.add(m.correctKey);
    if (m.totalKey) keys.add(m.totalKey);
    if (m.supportKey) keys.add(m.supportKey);
    if (m.subline) {
      for (const match of m.subline.matchAll(/\{([^}]+)\}/g)) {
        keys.add(match[1]);
      }
    }
  }
  return Array.from(keys);
};

// Remap a single row's `aggregated_metrics` into the flat, bare-keyed object
// GroupedMetricCards expects, resolving every key `defs` could reference
// (primary value + subline placeholders) through metricAt().
export const remapRowMetricsForCards = (
  metrics: Record<string, any> | undefined,
  defs: EvaluationMetricDefinition[],
): Record<string, any> =>
  ({ ...Object.fromEntries(collectMetricDefKeys(defs).map(key => [key, metricAt(metrics, key)])),
     ...aggregateStepMetrics([metrics || {}]) });

// Format count helper
export const formatCount = (value: number | undefined) => {
  if (value === undefined || value === null) return '-';
  return value.toString();
};

// Format timestamp helper
export const formatTimestamp = (timestamp: string | null | undefined): string => {
  if (!timestamp) return 'Unknown';
  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return timestamp;
  }
};

// Relative timestamp formatting for job cards
export const formatRelativeTimestamp = (timestamp: any): string => {
  if (!timestamp) return 'Unknown';

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays < 1) return 'Today';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Unknown';
  }
};

// Helper to derive a display name for an eval row from config/result fields
export const getEvalRowDisplayName = (
  configRow?: EvaluationRowConfig,
  rowResult?: EvaluationRowResult,
  index?: number,
): string => {
  // 0. row_name from result — set by the DROID multi-row orchestrator
  // (each DROID row carries its own user-supplied label that gets surfaced
  // in the row-selector dropdown and the headline comparison table).
  const rowName = (rowResult as unknown as { row_name?: string } | undefined)?.row_name;
  if (rowName && rowName.trim()) return rowName.trim();

  // 1. row_description from result
  if (rowResult?.row_description) return rowResult.row_description;

  // 2. description from config
  if (configRow?.description) return configRow.description;

  // 3. Derive from config fields
  if (configRow) {
    const cfg = configRow as Record<string, unknown>;
    const analysisType = cfg.analysisType || cfg.analysis_type;
    const query = cfg.query as string | undefined;
    const agentName = (cfg.agentName || cfg.agent_name) as string | undefined;

    if (analysisType === 'ask' && query) {
      return query.length > 15 ? query.slice(0, 15) + '...' : query;
    }
    if (analysisType === 'agent' && agentName) {
      return agentName;
    }
  }

  // 4. Fallback
  return `Eval ${(index ?? 0) + 1}`;
};

// Component to display original + optimized prompts side-by-side
export const PromptDisplay = ({
  originalPrompt,
  optimizedPrompt,
}: {
  originalPrompt?: string;
  optimizedPrompt?: string;
}) => {
  if (!optimizedPrompt) return null;

  return (
    <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
      <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-1.5 text-gray-500 mb-1.5 font-medium">
          Original Query
        </div>
        <div className="text-gray-700 whitespace-pre-wrap">{originalPrompt || '(none)'}</div>
      </div>
      <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-center gap-1.5 text-amber-600 mb-1.5 font-medium">
          <Sparkles className="w-3 h-3" />
          Optimized Prompt
        </div>
        <div className="text-gray-700 whitespace-pre-wrap">{optimizedPrompt}</div>
      </div>
    </div>
  );
};

// Badge component for auto-prompt optimization
export const PromptOptimizedBadge = ({ optimized }: { optimized?: boolean }) => {
  if (!optimized) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
      <Sparkles className="w-3 h-3" />
      Optimized
    </span>
  );
};

// Metrics charts grid component
export const MetricsChartsGrid = ({ rowResults, configRows, metrics }: { rowResults: EvaluationRowResult[]; configRows?: EvaluationRowConfig[]; metrics?: EvaluationMetricDefinition[] }) => {
  const effectiveMetrics = metrics ?? ALL_METRICS;

  // State for eval visibility
  const [visibleEvals, setVisibleEvals] = useState<Set<string>>(() => new Set(rowResults.map(r => r.row_id)));

  // State for metric filter
  const [visibleMetrics, setVisibleMetrics] = useState<Set<string>>(() => new Set(effectiveMetrics.map(m => m.key)));

  // State for expanded eval details (shown inline)
  const [expandedEvalId, setExpandedEvalId] = useState<string | null>(null);

  // Filter row results based on visibility
  const filteredRowResults = rowResults.filter(r => visibleEvals.has(r.row_id));

  // Toggle eval visibility
  const toggleEval = (rowId: string) => {
    const newSet = new Set(visibleEvals);
    if (newSet.has(rowId)) {
      newSet.delete(rowId);
    } else {
      newSet.add(rowId);
    }
    setVisibleEvals(newSet);
  };

  // Toggle metric visibility
  const toggleMetric = (key: string) => {
    const newSet = new Set(visibleMetrics);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setVisibleMetrics(newSet);
  };

  // Get filtered metrics
  const visibleMetricsList = effectiveMetrics.filter(m => visibleMetrics.has(m.key));

  return (
    <div className="space-y-4">
      {/* Eval Legend with visibility controls */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Evaluations</span>
          <span className="text-xs text-gray-400">Toggle visibility or view details</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {rowResults.map((row, idx) => (
            <div
              key={row.row_id}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                visibleEvals.has(row.row_id) ? 'bg-gray-50' : 'bg-gray-100 opacity-50'
              }`}
            >
              <input
                type="checkbox"
                checked={visibleEvals.has(row.row_id)}
                onChange={() => toggleEval(row.row_id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-navy-600 focus:ring-navy-500 cursor-pointer"
              />
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: EVAL_ROW_COLORS[idx % EVAL_ROW_COLORS.length] }}
              />
              <span className="text-xs font-medium text-gray-700 truncate max-w-[100px]">
                {getEvalRowDisplayName(configRows?.find(c => c.id === row.row_id), row, idx)}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedEvalId(expandedEvalId === row.row_id ? null : row.row_id);
                }}
                className={`px-2 py-0.5 text-xs font-medium ml-1 rounded transition-colors border ${
                  expandedEvalId === row.row_id
                    ? 'text-white bg-navy-600 border-navy-600'
                    : 'text-navy-600 hover:text-white hover:bg-navy-600 border-navy-200 hover:border-navy-600'
                }`}
              >
                {expandedEvalId === row.row_id ? 'Hide' : 'Details'}
              </button>
            </div>
          ))}
        </div>

        {/* Inline expanded eval config details */}
        {expandedEvalId && (() => {
          const row = rowResults.find(r => r.row_id === expandedEvalId);
          const configRow = configRows?.find(r => r.id === expandedEvalId);
          if (!row) return null;

          // Handle both camelCase (frontend) and snake_case (API response) field names
          const cfg = configRow as Record<string, unknown> | undefined;
          const analysisType = cfg?.analysisType || cfg?.analysis_type;
          const numberOfTrials = cfg?.numberOfTrials || cfg?.number_of_trials;
          const isThumbnail = cfg?.isThumbnail ?? cfg?.is_thumbnail;
          const useEnhancedMotion = cfg?.useEnhancedMotionAnalysis ?? cfg?.use_enhanced_motion_analysis;
          const agentName = cfg?.agentName || cfg?.agent_name;
          const vlmModel = cfg?.vlmModelSelection || cfg?.vlm_model_selection;
          const llmAgenticModel = cfg?.llmAgenticValidationModel || cfg?.llm_agentic_validation_model;
          const category = cfg?.category;
          const confidence = cfg?.confidence;
          const query = cfg?.query;
          const customFps = cfg?.custom_fps;

          return (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-gray-800">
                  {getEvalRowDisplayName(configRow, row, rowResults.findIndex(r => r.row_id === expandedEvalId))} — Configuration
                </h5>
                <button
                  onClick={() => setExpandedEvalId(null)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {analysisType && (
                  <div>
                    <span className="text-gray-500 text-xs">Analysis Type</span>
                    <p className="font-medium text-gray-900 capitalize">{String(analysisType)}</p>
                  </div>
                )}
                {numberOfTrials && (
                  <div>
                    <span className="text-gray-500 text-xs">Trials</span>
                    <p className="font-medium text-gray-900">{String(numberOfTrials)}</p>
                  </div>
                )}
                {category && (
                  <div>
                    <span className="text-gray-500 text-xs">Category</span>
                    <p className="font-medium text-gray-900 capitalize">{String(category)}</p>
                  </div>
                )}
                {confidence && (
                  <div>
                    <span className="text-gray-500 text-xs">Thinking Mode</span>
                    <p className="font-medium text-gray-900 capitalize flex items-center gap-1">
                      {String(confidence) === 'high' ? <Lightbulb className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                      {String(confidence) === 'high' ? 'Thinking' : 'Fast'}
                    </p>
                  </div>
                )}
                {isThumbnail !== undefined && (
                  <div>
                    <span className="text-gray-500 text-xs">Thumbnail</span>
                    <p className="font-medium text-gray-900">{isThumbnail ? 'Yes' : 'No'}</p>
                  </div>
                )}
                {useEnhancedMotion !== undefined && (
                  <div>
                    <span className="text-gray-500 text-xs">Enhanced Motion</span>
                    <p className="font-medium text-gray-900">{useEnhancedMotion ? 'Yes' : 'No'}</p>
                  </div>
                )}
                {agentName && (
                  <div className="col-span-2">
                    <span className="text-gray-500 text-xs">Agent</span>
                    <p className="font-medium text-gray-900">{String(agentName)}</p>
                  </div>
                )}
                {vlmModel && (
                  <div>
                    <span className="text-gray-500 text-xs">VLM Model</span>
                    <p className="font-medium text-gray-900">
                      {VLM_MODEL_OPTIONS.find(o => o.value === vlmModel)?.label || String(vlmModel)}
                    </p>
                  </div>
                )}
                {llmAgenticModel && llmAgenticModel !== 'off' && (
                  <div>
                    <span className="text-gray-500 text-xs">Agentic Model</span>
                    <p className="font-medium text-gray-900">
                      {LLM_AGENTIC_MODEL_OPTIONS.find(o => o.value === llmAgenticModel)?.label || String(llmAgenticModel)}
                    </p>
                  </div>
                )}
                {customFps != null && (
                  <div>
                    <span className="text-gray-500 text-xs">Custom FPS</span>
                    <p className="font-medium text-gray-900">{String(customFps)}</p>
                  </div>
                )}
                {(cfg?.useAutoPromptOptimization || cfg?.use_auto_prompt_optimization) !== undefined && (
                  <div>
                    <span className="text-gray-500 text-xs">Auto-Optimize</span>
                    <p className="font-medium text-gray-900">
                      {(cfg?.useAutoPromptOptimization || cfg?.use_auto_prompt_optimization) ? 'Yes' : 'No'}
                    </p>
                  </div>
                )}
              </div>
              {query && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-gray-500 text-xs">Query</span>
                  <p className="font-medium text-gray-900 mt-0.5">"{String(query)}"</p>
                </div>
              )}
              {cfg?.optimized_prompt && (
                <div className="mt-2">
                  <span className="text-gray-500 text-xs">Optimized Prompt</span>
                  <p className="font-medium text-amber-700 mt-0.5 bg-amber-50 p-2 rounded border border-amber-200">
                    "{String(cfg.optimized_prompt)}"
                  </p>
                </div>
              )}
              {/* Quick metrics summary */}
              {row.aggregated_metrics && Object.keys(row.aggregated_metrics).length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-gray-500 text-xs mb-2 block">Metrics Summary</span>
                  <div className="flex flex-wrap gap-3">
                    {['precision', 'recall', 'f1_score', 'video_event_accuracy', 'video_precision', 'video_recall'].map((metric) => {
                      const value = row.aggregated_metrics?.[`${metric}/mean`] ?? row.aggregated_metrics?.[metric];
                      if (value === undefined) return null;
                      const labelMap: Record<string, string> = {
                        'video_event_accuracy': 'Video Acc',
                        'video_precision': 'Video Prec',
                        'video_recall': 'Video Rec',
                      };
                      const label = labelMap[metric] ?? metric.replace('_', ' ');
                      return (
                        <div key={metric} className="px-2 py-1 bg-white rounded border border-gray-200">
                          <span className="text-xs text-gray-500 capitalize">{label}:</span>
                          <span className="ml-1 text-sm font-semibold text-gray-900">{(value * 100).toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Metric Filter */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Metrics Filter</span>
          <button
            onClick={() => setVisibleMetrics(new Set(effectiveMetrics.map(m => m.key)))}
            className="text-xs text-navy-600 hover:text-navy-800 font-medium"
          >
            Show All
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {effectiveMetrics.map((metric) => (
            <label
              key={metric.key}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                visibleMetrics.has(metric.key) ? 'bg-gray-100' : 'bg-gray-50 opacity-50'
              }`}
            >
              <input
                type="checkbox"
                checked={visibleMetrics.has(metric.key)}
                onChange={() => toggleMetric(metric.key)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-navy-600 focus:ring-navy-500 cursor-pointer"
              />
              <span className="text-xs font-medium text-gray-700">{metric.title}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Dynamic grid of metric charts */}
      {filteredRowResults.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
          <p className="text-sm">Select at least one evaluation to view charts</p>
        </div>
      ) : visibleMetricsList.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
          <p className="text-sm">Select at least one metric to view charts</p>
        </div>
      ) : (
        <div className={`grid gap-4 items-stretch ${visibleMetricsList.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'}`}>
          {visibleMetricsList.map((metric) => {
            // Composite charts (3 grouped/stacked series) get double-width so
            // the extra bars/legend aren't cramped into a quarter column.
            const wideClass = metric.chartType ? 'sm:col-span-2' : '';
            if (metric.chartType === 'recall_breakdown') {
              return (
                <div key={metric.key} className={wideClass}>
                  <RecallBreakdownChart
                    rowResults={filteredRowResults}
                    configRows={configRows}
                    onRowClick={(rowId) => setExpandedEvalId(expandedEvalId === rowId ? null : rowId)}
                  />
                </div>
              );
            }
            if (metric.chartType === 'grouped_tiers' && metric.tiers) {
              return (
                <div key={metric.key} className={wideClass}>
                  <GroupedTierChart
                    title={metric.title}
                    tiers={metric.tiers}
                    rowResults={filteredRowResults}
                    configRows={configRows}
                    onRowClick={(rowId) => setExpandedEvalId(expandedEvalId === rowId ? null : rowId)}
                  />
                </div>
              );
            }
            return (
              <SingleMetricChart
                key={metric.key}
                title={metric.title}
                metricKey={metric.key}
                rowResults={filteredRowResults}
                configRows={configRows}
                onRowClick={(rowId) => setExpandedEvalId(expandedEvalId === rowId ? null : rowId)}
                accentColor={metric.color}
                isPercentage={metric.isPercentage}
                subline={
                  metric.subline && filteredRowResults.length === 1
                    ? substituteMetricTemplate(
                        metric.subline,
                        filteredRowResults[0].aggregated_metrics || (filteredRowResults[0].trials?.[0]?.metrics as any) || {},
                      )
                    : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Renders a set of metric cards bucketed by `group`, with a section header
 * per group (metrics without a `group` render ungrouped, no header). Shared
 * between the benchmark "Overall" section and per-class result cards
 * (Driving Dojo) — same rendering logic, different `metrics`/`cardDefs`.
 */
export const GroupedMetricCards = ({
  metrics,
  cardDefs,
  legacySublineSuffix = 'correct',
}: {
  metrics: Record<string, any>;
  cardDefs: EvaluationMetricDefinition[];
  legacySublineSuffix?: string;
}) => {
  // Each group is independently collapsible. "Final Statistics" defaults
  // open (it's the headline numbers); every other group defaults collapsed.
  // Ungrouped ('__nogroup__') cards always render open (no header/toggle).
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['Overall Event Detection']));
  const toggleGroup = (g: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  };
  // Bucket by `group` while preserving insertion order. Metrics without a
  // group fall under `__nogroup__` and render without a header.
  const groups = new Map<string, EvaluationMetricDefinition[]>();
  for (const m of cardDefs) {
    if (isStepMetric(m.key) && metrics[m.key] === undefined) continue;
    const g = m.group ?? '__nogroup__';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(m);
  }
  // Substitute `{metric_key}` placeholders in a card's `subline` template
  // against the metrics dict. Returns null if any referenced key is
  // missing — caller falls back to legacy correctKey/totalKey rendering.
  const renderSubline = (template: string): string | null => {
    let missing = false;
    const out = template.replace(/\{([^}]+)\}/g, (_, k) => {
      const v = metrics[k];
      if (v == null) { missing = true; return ''; }
      return String(v);
    });
    return missing ? null : out;
  };
  // Cards keyed on a slice / class with zero support render '—' instead of
  // '0%'. Detected via `supportKey` on the config: 0 / null / undefined →
  // no data → dash.
  const isInactive = (m: EvaluationMetricDefinition): boolean => {
    const sKey = m.supportKey;
    if (!sKey) return false;
    const v = metrics[sKey];
    return v == null || v === 0;
  };
  const renderCard = (m: EvaluationMetricDefinition) => {
    const acc = metrics[m.key];
    const inactive = isInactive(m);
    // `isPercentage` decides whether the raw value is a 0-1 fraction
    // (multiply by 100, render as N.N%) or an integer count (render as-is).
    const isPct = m.isPercentage !== false;
    const valueText = inactive
      ? '—'
      : acc == null
        ? '—'
        : isPct
          ? `${(acc * 100).toFixed(1)}%`
          : (Number.isInteger(acc) ? String(acc) : Number(acc).toFixed(2));
    let sublineText: string | null = null;
    if (!inactive) {
      if (m.subline) {
        sublineText = renderSubline(m.subline);
      } else if (m.correctKey && m.totalKey) {
        const correct = metrics[m.correctKey];
        const total = metrics[m.totalKey];
        if (correct !== undefined && total !== undefined) {
          sublineText = `${correct} / ${total} ${legacySublineSuffix}`;
        }
      }
    }
    return (
      <div key={m.key} className="border border-gray-200 rounded-lg p-3">
        <div className="flex items-start gap-1">
          <div className="text-xs font-medium text-gray-500 mb-1 flex-1">{m.title}</div>
          {m.tooltip && (
            <UITooltipProvider delayDuration={150}>
              <UITooltip>
                <UITooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`What ${m.title} means`}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 -mr-0.5"
                  >
                    <Info className="h-3 w-3" />
                  </button>
                </UITooltipTrigger>
                <UITooltipContent className="max-w-[260px] text-xs leading-5">
                  {m.tooltip}
                </UITooltipContent>
              </UITooltip>
            </UITooltipProvider>
          )}
        </div>
        <div className="text-xl font-bold text-gray-900">{valueText}</div>
        {sublineText && (
          <div className="text-xs text-gray-400 mt-0.5">{sublineText}</div>
        )}
      </div>
    );
  };
  const entries = Array.from(groups.entries());
  return (
    <div className="space-y-3">
      {entries.map(([groupName, items]) => {
        const isNoGroup = groupName === '__nogroup__';
        const isOpen = isNoGroup || expandedGroups.has(groupName);
        return (
          <div key={groupName}>
            {!isNoGroup && (
              <button
                type="button"
                onClick={() => toggleGroup(groupName)}
                className="w-full flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-1 mb-3 hover:text-gray-700 transition-colors"
              >
                {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {groupName}
              </button>
            )}
            {isOpen && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {items.map(renderCard)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Card defs reused per-class: same as the global validation metric list, but
// `f1/macro` has no per-class equivalent (macro-averaging is only meaningful
// across classes), so it's dropped here.
const DRIVINGDOJO_PER_CLASS_STAT_DEFS: EvaluationMetricDefinition[] =
  DRIVINGDOJO_VALIDATION_METRIC_DEFS.filter(m => m.key !== 'f1/macro');

/**
 * Pulls one class's slice of a Driving Dojo run's `aggregated_metrics` into a
 * flat, unsuffixed dict — e.g. `f1/class/lane_change` → `f1` — so it can be
 * fed straight into `GroupedMetricCards` alongside the same bare-key card
 * defs used for the global "Overall" section.
 */
const extractClassMetrics = (metrics: Record<string, any>, actionId: string): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const m of DRIVINGDOJO_PER_CLASS_STAT_DEFS) {
    out[m.key] = metrics[`${m.key}/class/${actionId}`];
  }
  // Auxiliary numerator/denominator fields referenced by subline templates
  // above. total_tp/total_fp/total_fn/total_gt/total_pred reuse the
  // pre-existing (differently-named) per-class counters from the basic
  // F1/precision/recall block; raw_tp_count/total_raw_predictions/
  // gt_covered_count aren't cards themselves, so pull them directly here.
  out['total_tp'] = metrics[`correct/class/${actionId}`];
  out['total_fp'] = metrics[`false_positives/class/${actionId}`];
  out['total_fn'] = metrics[`false_negatives/class/${actionId}`];
  out['total_gt'] = metrics[`support/class/${actionId}`];
  out['total_pred'] = metrics[`predictions/class/${actionId}`];
  out['support'] = metrics[`support/class/${actionId}`];
  out['raw_tp_count'] = metrics[`raw_tp_count/class/${actionId}`];
  out['total_raw_predictions'] = metrics[`total_raw_predictions/class/${actionId}`];
  out['gt_covered_count'] = metrics[`gt_covered_count/class/${actionId}`];
  // vlm_missed_count/vlm_noise_count are no longer their own cards (folded
  // into subtext on FN Share: VLM Missed / VLM Noise Rate), so they're not
  // picked up by the card-key loop above — pull them directly too.
  out['vlm_missed_count'] = metrics[`vlm_missed_count/class/${actionId}`];
  out['vlm_noise_count'] = metrics[`vlm_noise_count/class/${actionId}`];
  // Same for the AI-Rejected/Incorrectly/Correctly Rejected counts — folded
  // into subtext on Rejection Precision/Recall/FN Share cards, no longer
  // their own cards.
  out['ai_rejected_count'] = metrics[`ai_rejected_count/class/${actionId}`];
  out['ai_incorrectly_rejected_count'] = metrics[`ai_incorrectly_rejected_count/class/${actionId}`];
  out['ai_correctly_rejected_count'] = metrics[`ai_correctly_rejected_count/class/${actionId}`];
  // Not cards themselves — referenced by Validator Accuracy's subline template.
  out['validator_correct_decisions'] = metrics[`validator_correct_decisions/class/${actionId}`];
  out['total_raw_decisions'] = metrics[`total_raw_decisions/class/${actionId}`];
  const suffix = `/class/${actionId}`;
  for (const [key, value] of Object.entries(metrics)) {
    if (isStepMetric(key) && key.endsWith(suffix)) out[key.slice(0, -suffix.length)] = value;
  }
  return out;
};

/**
 * One collapsible card per Driving Dojo action class ("query"), mirroring
 * RowResultCard's collapsed/expanded pattern for the regular eval page.
 * Collapsed: headline F1/Precision/Recall/Support + a "View Batch" link.
 * Expanded: the same 4-section grouped metric grid as the page's "Overall"
 * section, scoped to this class.
 */
export const DrivingDojoClassResultCard = ({
  actionId,
  actionLabel,
  metrics,
  batchId,
}: {
  actionId: string;
  actionLabel: string;
  metrics: Record<string, any>;
  batchId?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const classMetrics = extractClassMetrics(metrics, actionId);
  const support = classMetrics['support'];
  const isInactive = support == null || support === 0;
  const fmtPct = (v: any) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-900">{actionLabel}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">
              F1: <span className="font-medium text-gray-900">{fmtPct(classMetrics['f1'])}</span>
            </span>
            <span className="text-gray-500">
              Precision: <span className="font-medium text-gray-900">{fmtPct(classMetrics['precision'])}</span>
            </span>
            <span className="text-gray-500">
              Recall: <span className="font-medium text-gray-900">{fmtPct(classMetrics['recall'])}</span>
            </span>
            <span className="text-gray-500">
              Support: <span className="font-medium text-gray-900">{isInactive ? '—' : support}</span>
            </span>
          </div>
          {batchId && (
            <a
              href={`/use-cases/rapid-review/batch-view/${batchId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-navy-600 bg-navy-50 hover:bg-navy-100 rounded-md transition-colors"
            >
              <Database className="w-3 h-3" />
              View Batch
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-4">
          <GroupedMetricCards metrics={classMetrics} cardDefs={DRIVINGDOJO_PER_CLASS_STAT_DEFS} />
        </div>
      )}
    </div>
  );
};

// Row result card component for run detail view
export const RowResultCard = ({
  row,
  index,
  configRows,
  metrics,
  mode,
  level,
}: {
  row: EvaluationRowResult;
  index: number;
  configRows?: EvaluationRowConfig[];
  metrics?: { title: string; key: string }[];
  mode?: EvaluationMode | 'benchmark';
  level?: 'child' | 'parent';
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  // Condensed by default — the three-tier stat cards and trial-by-trial
  // table are a lot of content to dump on every row open; keep the expanded
  // view to the compact Videos/GT Events/Trials line until the user asks.
  const [showMetrics, setShowMetrics] = useState(false);
  const [showTrialResults, setShowTrialResults] = useState(false);

  // Get the config row for this result
  const configRow = configRows?.find(r => r.id === row.row_id);

  // Get human-readable row name
  const getRowDisplayName = () => {
    return getEvalRowDisplayName(configRow, row, index);
  };

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
          <StatusCircle status={row.status} />
          <span className="font-medium text-gray-900">
            {getRowDisplayName()}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {row.aggregated_metrics && (
            <div className="flex items-center gap-4 text-sm">
              {mode === 'benchmark' ? (
                // Benchmark mode: show overall accuracy (%) and processing time.
                // Robo-Dopamine runs don't have ``accuracy`` — they emit
                // ``voc_plus_overall`` / ``voc_minus_overall`` instead, so the
                // headline switches to VOC+ / VOC− for those rows.
                (row.aggregated_metrics as any)?.voc_plus_overall !== undefined
                  || (row.aggregated_metrics as any)?.voc_minus_overall !== undefined ? (
                  <>
                    {(row.aggregated_metrics as any).voc_plus_overall != null && (
                      <span className="text-gray-500">
                        VOC+: <span className="font-medium text-gray-900">
                          {((row.aggregated_metrics as any).voc_plus_overall as number).toFixed(3)}
                        </span>
                      </span>
                    )}
                    {(row.aggregated_metrics as any).voc_minus_overall != null && (
                      <span className="text-gray-500">
                        VOC−: <span className="font-medium text-gray-900">
                          {((row.aggregated_metrics as any).voc_minus_overall as number).toFixed(3)}
                        </span>
                      </span>
                    )}
                    {row.aggregated_metrics['processing_time_seconds'] != null && (
                      <span className="text-gray-500">
                        Time: <span className="font-medium text-gray-900">
                          {Math.round(row.aggregated_metrics['processing_time_seconds'] as number)}s
                        </span>
                      </span>
                    )}
                  </>
                ) : (
                <>
                  <span className="text-gray-500">
                    Accuracy: <span className="font-medium text-gray-900">
                      {row.aggregated_metrics['accuracy'] != null
                        ? `${(row.aggregated_metrics['accuracy'] * 100).toFixed(1)}%`
                        : '-'}
                    </span>
                    {row.aggregated_metrics['total_correct'] != null && row.aggregated_metrics['total_answered'] != null && (
                      <span className="text-gray-400 ml-1">
                        ({row.aggregated_metrics['total_correct']}/{row.aggregated_metrics['total_answered']})
                      </span>
                    )}
                  </span>
                  {row.aggregated_metrics['processing_time_seconds'] != null && (
                    <span className="text-gray-500">
                      Time: <span className="font-medium text-gray-900">
                        {Math.round(row.aggregated_metrics['processing_time_seconds'])}s
                      </span>
                    </span>
                  )}
                </>
                )
              ) : mode === 'open_ended_segmentation' ? (
                <>
                  {[
                    { label: 'Segment F1', key: 'segment_f1' },
                    { label: 'Label Acc', key: 'label_accuracy' },
                    { label: 'Sem Prec', key: 'semantic_precision' },
                    { label: 'Sem Rec', key: 'semantic_recall' },
                    { label: 'E2E F1', key: 'semantic_e2e_f1' },
                  ].map(m => (
                    <span key={m.key} className="text-gray-500">
                      {m.label}: <span className="font-medium text-gray-900">
                        {formatMetric(row.aggregated_metrics[`${m.key}/mean`] ?? row.aggregated_metrics[m.key])}
                      </span>
                    </span>
                  ))}
                </>
              ) : mode === 'segmentation' ? (
                // Segmentation mode: 5 key metrics respecting child/parent level
                (() => {
                  const lp = level === 'parent' ? 'parent/' : '';
                  const segHeaderMetrics = row.aggregated_metrics['robotics/f1@0.5/mean'] != null || row.aggregated_metrics['robotics/f1@0.5'] != null ? [
                    { label: 'F1@0.5', key: 'robotics/f1@0.5' },
                    { label: 'F1@0.75', key: 'robotics/f1@0.75' },
                    { label: 'Frame Accuracy', key: 'robotics/frame_accuracy' },
                  ] : [
                    { label: 'Frame Acc', key: `${lp}duration/micro/precision` },
                    { label: 'Event F1', key: `${lp}instance/micro/f1` },
                    { label: 'Bal F1', key: `${lp}duration/macro/f1` },
                    { label: 'GT IoU', key: `${lp}gt_iou` },
                    { label: 'Pred IoU', key: `${lp}pred_iou` },
                    { label: 'Bnd F1@10', key: `${lp}boundary/10s/f1` },
                  ];
                  return (
                    <>
                      {segHeaderMetrics.map(m => (
                        <span key={m.key} className="text-gray-500">
                          {m.label}: <span className="font-medium text-gray-900">
                            {formatMetric(row.aggregated_metrics[`${m.key}/mean`] ?? row.aggregated_metrics[m.key])}
                          </span>
                        </span>
                      ))}
                    </>
                  );
                })()

              ) : (
                // Event detection mode
                <>
                  <span className="text-gray-500">
                    F1: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['f1_score/mean'] ?? row.aggregated_metrics['f1_score'])}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Precision: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['precision/mean'] ?? row.aggregated_metrics['precision'])}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Recall: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['recall/mean'] ?? row.aggregated_metrics['recall'])}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Video Acc: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['video_event_accuracy/mean'] ?? row.aggregated_metrics['video_event_accuracy'])}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Video Prec: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['video_precision/mean'] ?? row.aggregated_metrics['video_precision'])}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Video Rec: <span className="font-medium text-gray-900">
                      {formatMetric(row.aggregated_metrics['video_recall/mean'] ?? row.aggregated_metrics['video_recall'])}
                    </span>
                  </span>
                </>
              )}
            </div>
          )}
          {/* Batch links — quick access without expanding the row */}
          {row.batch_ids && row.batch_ids.length > 0 && (() => {
            const labelById = new Map(DRIVINGDOJO_ACTIONS.map(a => [a.id, a.label]));
            return (
              <div className="flex items-center gap-1.5">
                {row.batch_ids.map((batchId, batchIdx) => {
                  const rawLabel = row.batch_labels?.[batchIdx];
                  const display = rawLabel ? (labelById.get(rawLabel) ?? rawLabel) : null;
                  const linkText = display
                    ? display
                    : row.batch_ids.length > 1
                      ? `Batch ${batchIdx + 1}`
                      : 'Batch';
                  return (
                    <a
                      key={batchId}
                      href={`/use-cases/rapid-review/batch-view/${batchId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium text-white bg-navy-600 border border-navy-600 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:bg-navy-700 hover:border-navy-700 rounded-[8px] transition-colors whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                      title={display ? `Open batch: ${display}` : 'Open batch'}
                    >
                      {linkText}
                      <ExternalLink className="w-3 h-3 text-white/70" />
                    </a>
                  );
                })}
              </div>
            );
          })()}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 ml-9">
        <div className="flex items-start gap-4">
      {/* Batch configuration — collapsible side panel, docked on the right */}
      {configRow && (() => {
        // Handle both camelCase (frontend) and snake_case (API response) field names
        const cfg = configRow as Record<string, unknown>;
        const analysisType = cfg.analysisType || cfg.analysis_type;
        const numberOfTrials = cfg.numberOfTrials || cfg.number_of_trials;
        const isThumbnail = cfg.isThumbnail ?? cfg.is_thumbnail;
        const useEnhancedMotion = cfg.useEnhancedMotionAnalysis ?? cfg.use_enhanced_motion_analysis;
        const agentName = cfg.agentName || cfg.agent_name;
        const vlmModel = cfg.vlmModelSelection || cfg.vlm_model_selection;
        const llmAgenticModel = cfg.llmAgenticValidationModel || cfg.llm_agentic_validation_model;

        return (
          <aside className={cn('order-2 shrink-0 self-start transition-[width] duration-200', showConfig ? 'w-72' : 'w-10')}>
            {!showConfig ? (
              // Collapsed: thin rail on the right with a vertical label.
              <div className="flex flex-col items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 py-3">
                <button
                  type="button"
                  onClick={() => setShowConfig(true)}
                  className="flex items-center justify-center size-7 rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  title="Expand batch configuration"
                  aria-label="Expand batch configuration"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-400 whitespace-nowrap rotate-180 [writing-mode:vertical-rl]">
                  Batch Configuration
                </span>
              </div>
            ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Batch Configuration</span>
                <button
                  type="button"
                  onClick={() => setShowConfig(false)}
                  className="flex flex-shrink-0 items-center justify-center size-6 rounded-md border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
                  title="Collapse panel"
                  aria-label="Collapse panel"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            <div className="space-y-2 text-sm px-3 pb-4 pt-3 bg-gray-50">
              <div>
                <span className="text-gray-500">Analysis Type:</span>
                <span className="ml-2 font-medium capitalize">{String(analysisType || '-')}</span>
              </div>
              <div>
                <span className="text-gray-500">Trials:</span>
                <span className="ml-2 font-medium">{numberOfTrials ?? '-'}</span>
              </div>
              {cfg.category && (
                <div>
                  <span className="text-gray-500">Category:</span>
                  <span className="ml-2 font-medium">{String(cfg.category)}</span>
                </div>
              )}
              {cfg.confidence && (
                <div>
                  <span className="text-gray-500">Thinking Mode:</span>
                  <span className="ml-2 font-medium capitalize inline-flex items-center gap-1">
                    {String(cfg.confidence) === 'high' ? <Lightbulb className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                    {String(cfg.confidence) === 'high' ? 'Thinking' : 'Fast'}
                  </span>
                </div>
              )}
              {isThumbnail !== undefined && (
                <div>
                  <span className="text-gray-500">Thumbnail:</span>
                  <span className="ml-2 font-medium">{isThumbnail ? 'Yes' : 'No'}</span>
                </div>
              )}
              {useEnhancedMotion !== undefined && (
                <div>
                  <span className="text-gray-500">Enhanced Motion:</span>
                  <span className="ml-2 font-medium">{useEnhancedMotion ? 'Yes' : 'No'}</span>
                </div>
              )}
              {agentName && (
                <div>
                  <span className="text-gray-500">Agent:</span>
                  <span className="ml-2 font-medium">{String(agentName)}</span>
                </div>
              )}
              {vlmModel && (
                <div>
                  <span className="text-gray-500">VLM Model:</span>
                  <span className="ml-2 font-medium">
                    {VLM_MODEL_OPTIONS.find(o => o.value === vlmModel)?.label || String(vlmModel)}
                  </span>
                </div>
              )}
              {llmAgenticModel && llmAgenticModel !== 'off' && (
                <div>
                  <span className="text-gray-500">Agentic Model:</span>
                  <span className="ml-2 font-medium">
                    {LLM_AGENTIC_MODEL_OPTIONS.find(o => o.value === llmAgenticModel)?.label || String(llmAgenticModel)}
                  </span>
                </div>
              )}
              {cfg.custom_fps != null && (
                <div>
                  <span className="text-gray-500">Custom FPS:</span>
                  <span className="ml-2 font-medium">{String(cfg.custom_fps)}</span>
                </div>
              )}
              {(cfg.useAutoPromptOptimization || cfg.use_auto_prompt_optimization) !== undefined && (
                <div>
                  <span className="text-gray-500">Auto-Optimize:</span>
                  <span className="ml-2 font-medium">
                    {(cfg.useAutoPromptOptimization || cfg.use_auto_prompt_optimization) ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {cfg.query && (
                <div>
                  <span className="text-gray-500">Query:</span>
                  <span className="ml-2 font-medium">"{String(cfg.query)}"</span>
                </div>
              )}
              {cfg.optimized_prompt && (
                <div className="mt-1">
                  <span className="text-gray-500">Optimized Prompt:</span>
                  <p className="font-medium text-amber-700 mt-0.5 bg-amber-50 p-2 rounded border border-amber-200">
                    "{String(cfg.optimized_prompt)}"
                  </p>
                </div>
              )}
            </div>
            </div>
            )}
          </aside>
        );
      })()}

          {/* Main content — results, left column */}
          <div className="order-1 min-w-0 flex-1 space-y-4">
          {/* Benchmark mode: show per-category breakdown instead of generic row info.
              Robo-Dopamine runs replace the motionbench category grid with a
              per-source VOC table (sources are derived from the metric keys). */}
          {(row.aggregated_metrics as any)?.voc_plus_overall !== undefined
              || (row.aggregated_metrics as any)?.voc_minus_overall !== undefined ? (
            (() => {
              const am: any = row.aggregated_metrics || {};
              const sources = Array.from(new Set(
                Object.keys(am)
                  .filter((k) => k.startsWith('voc_plus/') || k.startsWith('voc_minus/') || k.startsWith('videos_scored/'))
                  .map((k) => k.split('/', 2)[1])
                  .filter(Boolean),
              )).sort();
              return (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Per-Source VOC</h5>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                          <th className="text-left py-2 pr-3">Source</th>
                          <th className="text-right py-2 px-3">Videos</th>
                          <th className="text-right py-2 px-3">VOC+</th>
                          <th className="text-right py-2 pl-3">VOC−</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sources.map((src) => (
                          <tr key={src} className="border-b border-gray-100">
                            <td className="py-1.5 pr-3 font-medium text-gray-900">{src}</td>
                            <td className="py-1.5 px-3 text-right tabular-nums text-gray-700">
                              {am[`videos_scored/${src}`] ?? '—'}
                              {am[`video_count/${src}`] != null ? ` / ${am[`video_count/${src}`]}` : ''}
                            </td>
                            <td className="py-1.5 px-3 text-right tabular-nums text-emerald-700">
                              {typeof am[`voc_plus/${src}`] === 'number' ? am[`voc_plus/${src}`].toFixed(3) : '—'}
                            </td>
                            <td className="py-1.5 pl-3 text-right tabular-nums text-blue-700">
                              {typeof am[`voc_minus/${src}`] === 'number' ? am[`voc_minus/${src}`].toFixed(3) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()
          ) : metrics && mode !== 'open_ended_segmentation' ? (
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Per-Category Results</h5>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {metrics.filter(m => (m as Record<string, unknown>).correctKey && (m as Record<string, unknown>).totalKey && m.key !== 'accuracy').map((m) => {
                  const met = m as Record<string, unknown>;
                  const correct = row.aggregated_metrics?.[met.correctKey as string] ?? 0;
                  const total = row.aggregated_metrics?.[met.totalKey as string] ?? 0;
                  const acc = total > 0 ? ((correct as number) / (total as number) * 100).toFixed(1) : '-';
                  return (
                    <div key={m.key} className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-600">{m.title}</span>
                      <span className="font-medium text-gray-900">
                        {correct}/{total} <span className="text-gray-400">({acc}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Videos:</span>
              <span className="ml-2 font-medium">{row.video_count || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Ground Truth Events:</span>
              <span className="ml-2 font-medium">{row.ground_truth_event_count || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Trials:</span>
              <span className="ml-2 font-medium">{row.trials?.length || 0}</span>
            </div>
          </div>
          )}

          {/* Same three-tier grouped/collapsible card format DrivingDojo uses
              — `evaluate_predictions()` computes these unconditionally for
              every event-detection row, not just DrivingDojo. Hidden for
              rows whose stored metrics predate this computation.
              remapRowMetricsForCards resolves the `/mean` suffix
              `aggregate_trial_metrics()` always writes AND every subline
              numerator/denominator key, not just each card's headline value —
              otherwise the numerator/denominator fine print silently vanishes. */}
          {mode === 'event_detection' && (
            hasMetric(row.aggregated_metrics, 'ai_rejected_count')
              || hasMetric(row.aggregated_metrics, 'precision_oracle_validation')
          ) && (
            <GroupedMetricCards
              metrics={remapRowMetricsForCards(row.aggregated_metrics, EVENT_DETECTION_VALIDATION_METRIC_DEFS)}
              cardDefs={EVENT_DETECTION_VALIDATION_METRIC_DEFS}
            />
          )}

          {/* Trial details table (not shown for benchmark mode) */}
          {mode !== 'benchmark' && row.trials && row.trials.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowTrialResults(!showTrialResults)}
                className="w-full flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2"
              >
                {showTrialResults ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Trial Results ({row.trials.length})
              </button>
              {showTrialResults && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-medium text-gray-600">Trial</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-600">Status</th>
                      {mode === 'open_ended_segmentation' ? (
                        <>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Segment F1</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Label Acc</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Sem Prec</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Sem Rec</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">E2E F1</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Temp Matches</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Sem TP</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Sem FP</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Sem FN</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Time</th>
                        </>
                      ) : mode === 'segmentation' ? (
                        <>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Frame Acc</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Event F1</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Bal F1</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">GT IoU</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Pred IoU</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Bnd F1@10</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Time</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Precision</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Recall</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">F1</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Video Acc</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Video Prec</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Video Rec</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">TP</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">FP</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">FN</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600">Time</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {row.trials.map((trial) => {
                      const m = trial.metrics as Record<string, any> | undefined;
                      return (
                        <tr key={trial.trial_num} className="border-b border-gray-100">
                          <td className="py-2 pr-4">
                            <a
                              href={`/use-cases/rapid-review/batch-view/${trial.batch_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-navy-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              #{trial.trial_num}
                            </a>
                          </td>
                          <td className="py-2 pr-4">
                            <StatusBadge status={trial.status} />
                          </td>
                          {mode === 'open_ended_segmentation' ? (
                            <>
                              <td className="py-2 pr-4">{formatMetric(m?.segment_f1)}</td>
                              <td className="py-2 pr-4">{formatMetric(m?.label_accuracy)}</td>
                              <td className="py-2 pr-4">{formatMetric(m?.semantic_precision)}</td>
                              <td className="py-2 pr-4">{formatMetric(m?.semantic_recall)}</td>
                              <td className="py-2 pr-4">{formatMetric(m?.semantic_e2e_f1)}</td>
                              <td className="py-2 pr-4">{formatCount(m?.temporal_match_count)}</td>
                              <td className="py-2 pr-4">{formatCount(m?.semantic_tp)}</td>
                              <td className="py-2 pr-4">{formatCount(m?.semantic_fp)}</td>
                              <td className="py-2 pr-4">{formatCount(m?.semantic_fn)}</td>
                              <td className="py-2 pr-4">{formatProcessingTime(m?.processing_time_seconds)}</td>
                            </>
                          ) : mode === 'segmentation' ? (
                            (() => {
                              const lp = level === 'parent' ? 'parent/' : '';
                              return (
                                <>
                                  <td className="py-2 pr-4">{formatMetric(m?.[`${lp}duration/micro/precision`])}</td>
                                  <td className="py-2 pr-4">{formatMetric(m?.[`${lp}instance/micro/f1`])}</td>
                                  <td className="py-2 pr-4">{formatMetric(m?.[`${lp}duration/macro/f1`])}</td>
                                  <td className="py-2 pr-4">{formatMetric(m?.['gt_iou'])}</td>
                                  <td className="py-2 pr-4">{formatMetric(m?.['pred_iou'])}</td>
                                  <td className="py-2 pr-4">{formatMetric(m?.[`${lp}boundary/10s/f1`])}</td>
                                  <td className="py-2 pr-4">{formatProcessingTime(m?.processing_time_seconds)}</td>
                                </>
                              );
                            })()
                          ) : (
                            <>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.precision)}</td>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.recall)}</td>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.f1_score)}</td>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.video_event_accuracy)}</td>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.video_precision)}</td>
                              <td className="py-2 pr-4">{formatMetric(trial.metrics?.video_recall)}</td>
                              <td className="py-2 pr-4">{formatCount(trial.metrics?.tp)}</td>
                              <td className="py-2 pr-4">{formatCount(trial.metrics?.fp)}</td>
                              <td className="py-2 pr-4">{formatCount(trial.metrics?.fn)}</td>
                              <td className="py-2 pr-4">{formatProcessingTime(trial.metrics?.processing_time_seconds)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {/* Aggregated metrics. The card grid is hardcoded to look up
              specific keys, so we pick the right key set for the row's mode
              (segmentation / benchmark / legacy event-detection). */}
          {row.aggregated_metrics && Object.keys(row.aggregated_metrics).length > 0 && (() => {
            const lp = level === 'parent' ? 'parent/' : '';
            const am = row.aggregated_metrics ?? {};
            // DROID event-detection runs emit `event_detection_*` keys + TP/
            // FP/FN/TN counts. Surface those here instead of the legacy
            // `precision`/`recall`/`f1_score` set (which would all be dashes).
            const isBenchmarkEventDetection = mode === 'benchmark'
              && (am as any).event_detection_precision !== undefined;
            // ViFailback failure detection emits failure_* keys + TP/FP/FN/TN.
            const isBenchmarkFailureDetection = mode === 'benchmark'
              && (am as Record<string, unknown>).failure_precision !== undefined;
            // Robo-Dopamine GRM runs emit VOC+/VOC− overall keys instead of
            // accuracy / precision / recall. Detect that shape first so the
            // generic "accuracy" branch doesn't swallow them.
            const isBenchmarkRoboDopamine = mode === 'benchmark'
              && ((am as any).voc_plus_overall !== undefined
                  || (am as any).voc_minus_overall !== undefined);
            // Other benchmark runs (DROID classification, MotionBench) emit
            // `accuracy` + `total_correct/total_answered`.
            const isBenchmarkAccuracy = mode === 'benchmark'
              && !isBenchmarkEventDetection
              && !isBenchmarkFailureDetection
              && !isBenchmarkRoboDopamine
              && (am as any).accuracy !== undefined;
            const aggMetricDefs = mode === 'open_ended_segmentation' ? [
              { key: 'segment_f1', label: 'Segment F1' },
              { key: 'label_accuracy', label: 'Label Accuracy' },
              { key: 'semantic_precision', label: 'Semantic Precision' },
              { key: 'semantic_recall', label: 'Semantic Recall' },
              { key: 'semantic_e2e_f1', label: 'E2E F1' },
              { key: 'segment_precision', label: 'Segment Precision' },
              { key: 'segment_recall', label: 'Segment Recall' },
              { key: 'temporal_match_count', label: 'Temporal Matches', isCount: true },
              { key: 'semantic_tp', label: 'Semantic TP', isCount: true },
              { key: 'semantic_fp', label: 'Semantic FP', isCount: true },
              { key: 'semantic_fn', label: 'Semantic FN', isCount: true },
              { key: 'processing_time_seconds', label: 'Processing Time', isTime: true },
            ] : mode === 'segmentation' ? [
              { key: 'robotics/f1@0.5', label: 'F1@0.5' },
              { key: 'robotics/f1@0.75', label: 'F1@0.75' },
              { key: 'robotics/frame_accuracy', label: 'Frame Accuracy (10 Hz)' },
              { key: `${lp}duration/micro/precision`, label: 'Frame Accuracy' },
              { key: `${lp}instance/micro/f1`, label: 'Event Detection (Inst F1 Micro)' },
              { key: `${lp}duration/macro/f1`, label: 'Balanced F1 (Dur Macro)' },
              { key: `${lp}duration/micro/f1`, label: 'Duration F1 (Micro)' },
              { key: `${lp}instance/macro/f1`, label: 'Instance F1 (Macro)' },
              { key: `${lp}duration/macro/precision`, label: 'Duration Precision' },
              { key: `${lp}duration/macro/recall`, label: 'Duration Recall' },
              { key: `${lp}gt_iou`, label: 'GT IoU' },
              { key: `${lp}pred_iou`, label: 'Pred IoU' },
              { key: 'processing_time_seconds', label: 'Processing Time', isTime: true },
            ] : isBenchmarkEventDetection ? [
              { key: 'event_detection_precision', label: 'precision' },
              { key: 'event_detection_recall',    label: 'recall' },
              { key: 'event_detection_f1',        label: 'f1 score' },
              { key: 'accuracy',                  label: 'accuracy' },
              { key: 'event_detection_tp',        label: 'TP', isCount: true },
              { key: 'event_detection_fp',        label: 'FP', isCount: true },
              { key: 'event_detection_fn',        label: 'FN', isCount: true },
              { key: 'event_detection_tn',        label: 'TN', isCount: true },
              { key: 'processing_time_seconds',   label: 'processing time', isTime: true },
            ] : isBenchmarkFailureDetection ? [
              { key: 'accuracy',          label: 'video accuracy' },
              { key: 'failure_precision', label: 'failure precision' },
              { key: 'failure_recall',    label: 'failure recall' },
              { key: 'failure_f1',        label: 'failure f1' },
              { key: 'tp',                label: 'TP', isCount: true },
              { key: 'fp',                label: 'FP', isCount: true },
              { key: 'fn',                label: 'FN', isCount: true },
              { key: 'tn',                label: 'TN', isCount: true },
              { key: 'processing_time_seconds', label: 'processing time', isTime: true },
            ] : isBenchmarkRoboDopamine ? [
              { key: 'voc_plus_overall',  label: 'VOC+ overall' },
              { key: 'voc_minus_overall', label: 'VOC- overall' },
              { key: 'videos_scored_total', label: 'videos scored', isCount: true },
              { key: 'video_count_total',   label: 'videos total',  isCount: true },
              { key: 'processing_time_seconds', label: 'processing time', isTime: true },
            ] : isBenchmarkAccuracy ? [
              { key: 'accuracy',          label: 'accuracy' },
              { key: 'total_correct',     label: 'correct',  isCount: true },
              { key: 'total_answered',    label: 'answered', isCount: true },
              { key: 'processing_time_seconds', label: 'processing time', isTime: true },
            ] : [
              { key: 'precision', label: 'precision' },
              { key: 'recall', label: 'recall' },
              { key: 'f1_score', label: 'f1 score' },
              { key: 'video_event_accuracy', label: 'video accuracy' },
              { key: 'video_precision', label: 'video precision' },
              { key: 'video_recall', label: 'video recall' },
              { key: 'processing_time_seconds', label: 'processing time', isTime: true },
            ];
            const boundaryTolerances = mode === 'segmentation'
              ? Array.from(new Set(
                Object.keys(am)
                  .map(key => key.match(new RegExp(`^${lp}boundary/(\\d+)s/(?:precision|recall|f1)(?:/(?:mean|std))?$`))?.[1])
                  .filter((value): value is string => Boolean(value))
                  .map(value => Number(value))
              )).sort((a, b) => a - b)
              : [];
            const metricMean = (key: string) => row.aggregated_metrics?.[`${key}/mean`] ?? row.aggregated_metrics?.[key];
            const metricStd = (key: string) => row.aggregated_metrics?.[`${key}/std`];
            return (
              <div>
                <button
                  type="button"
                  onClick={() => setShowMetrics(!showMetrics)}
                  className="w-full flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2"
                >
                  {showMetrics ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Aggregated Metrics (Mean +/- Std)
                </button>
                {showMetrics && (
                <div className="grid grid-cols-4 gap-4">
                  {aggMetricDefs.map(({ key: metricName, label, isTime, isCount }: any) => {
                    const mean = metricMean(metricName);
                    const std = metricStd(metricName);
                    const renderVal = (v: any) =>
                      isTime ? formatProcessingTime(v) : isCount ? formatCount(v) : formatMetric(v);
                    return (
                      <div key={metricName} className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 uppercase">{label}</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {renderVal(mean)}
                          {std !== undefined && std !== null && (
                            <span className="text-sm font-normal text-gray-500">
                              {' '}+/- {renderVal(std)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {boundaryTolerances.map(tolerance => {
                    const base = `${lp}boundary/${tolerance}s`;
                    const precision = metricMean(`${base}/precision`);
                    const recall = metricMean(`${base}/recall`);
                    const f1 = metricMean(`${base}/f1`);
                    const precisionStd = metricStd(`${base}/precision`);
                    const recallStd = metricStd(`${base}/recall`);
                    const f1Std = metricStd(`${base}/f1`);
                    return (
                      <div key={base} className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 uppercase">Boundary @{tolerance}s</div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {[
                            ['P', precision, precisionStd],
                            ['R', recall, recallStd],
                            ['F1', f1, f1Std],
                          ].map(([label, mean, std]) => (
                            <div key={label as string}>
                              <div className="text-[10px] font-medium text-gray-400 uppercase">{label}</div>
                              <div className="text-base font-semibold text-gray-900">
                                {formatMetric(mean)}
                              </div>
                              {std !== undefined && std !== null && (
                                <div className="text-[10px] text-gray-500">+/- {formatMetric(std)}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })()}

          {/* Error display */}
          {row.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                Error
              </div>
              <p className="text-sm text-red-600 mt-1">{row.error}</p>
            </div>
          )}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};
